import { p as put, A as ApolloSettingKey, i as index_es5, a as accessibilityChannelName, b as alertDurationChannelName, c as createStore, g as getOrganizationStandardUrl, d as getAccessToken, L as Logger, s as sessionStore, e as shouldRefetch, f as getAccountName, F as Features, G as GlobalEventEmitter, S as STORAGE_KEYS, h as AlertInstanceResolver, j as getFeatureFlag, k as AccessibilityInstanceResolver, l as AlertDurationDefault, m as AlertDurationEnum, n as SeverityLevel, o as getTenantName, T as Tracker, W as WellKnownApplication, q as AppInsightDriverBuilder, D as DeploymentType, r as sanitizeUrl, t as getSpaceName, u as getEnvironment, v as getEnvConfigField, w as LocaleInstanceResolver, P as PendoApiKey, x as SHARED_PENDO_API_KEY, B as BuiltInGroups, y as removeObjectUndefinedProperties, z as process, C as global, E as getCircularReplacer, H as store, I as handleThemeChange, J as getAutoThemeValue, K as validateThemeCode, M as getCachedTheme, N as defaultTheme, O as setTheme, Q as MediaQueryListener, R as darkMediaQuery, U as highContrastMediaQueries, V as AccountAndTenantsInstanceResolver, X as Entitlement, Y as isStandalone, Z as setUserTheme, _ as AppInsightsInstanceResolver, $ as FeatureFlags, a0 as getFeatureFlagsStore, a1 as ChatInstanceResolver, a2 as chatStore, a3 as ThemeInstanceResolver, a4 as AutopilotChatMode, a5 as GlobalPropsInstanceResolver } from './loc-8e400c4a.js';
import { g as getPushNotificationsBaseUrl, T as ThemeEvent, L as Locale, l as localeStore, A as AutopilotChatService, S as StorageService, C as CHAT_MODE_KEY, a as Session } from './theme.events-0129643d.js';
import { S as SettingsInstanceResolver, a as accountAndTenantsStore, E as EventHandlersInstanceResolver, A as AccountAndTenants, b as Settings, I as ICON_MAP } from './serviceIconHelper-70cacacd.js';
import { e as enqueueSnackbar, c as closeSnackbar } from './notistack.esm-e02b98fd.js';
import { S as StatusTypes } from './statusTypes-44ddf7d8.js';
import { N as NavigationInstanceResolver } from './navigationInstanceResolver-9f4ebf1c.js';
import { g as getIsRunningInStorybook } from './chromatic-b6cfde84.js';

/**
 * Copyright 2016 Google Inc. All Rights Reserved.
 *
 * Licensed under the W3C SOFTWARE AND DOCUMENT NOTICE AND LICENSE.
 *
 *  https://www.w3.org/Consortium/Legal/2015/copyright-software-and-document
 *
 */
(function() {

// Exit early if we're not running in a browser.
if (typeof window !== 'object') {
  return;
}

// Exit early if all IntersectionObserver and IntersectionObserverEntry
// features are natively supported.
if ('IntersectionObserver' in window &&
    'IntersectionObserverEntry' in window &&
    'intersectionRatio' in window.IntersectionObserverEntry.prototype) {

  // Minimal polyfill for Edge 15's lack of `isIntersecting`
  // See: https://github.com/w3c/IntersectionObserver/issues/211
  if (!('isIntersecting' in window.IntersectionObserverEntry.prototype)) {
    Object.defineProperty(window.IntersectionObserverEntry.prototype,
      'isIntersecting', {
      get: function () {
        return this.intersectionRatio > 0;
      }
    });
  }
  return;
}

/**
 * Returns the embedding frame element, if any.
 * @param {!Document} doc
 * @return {!Element}
 */
function getFrameElement(doc) {
  try {
    return doc.defaultView && doc.defaultView.frameElement || null;
  } catch (e) {
    // Ignore the error.
    return null;
  }
}

/**
 * A local reference to the root document.
 */
var document = (function(startDoc) {
  var doc = startDoc;
  var frame = getFrameElement(doc);
  while (frame) {
    doc = frame.ownerDocument;
    frame = getFrameElement(doc);
  }
  return doc;
})(window.document);

/**
 * An IntersectionObserver registry. This registry exists to hold a strong
 * reference to IntersectionObserver instances currently observing a target
 * element. Without this registry, instances without another reference may be
 * garbage collected.
 */
var registry = [];

/**
 * The signal updater for cross-origin intersection. When not null, it means
 * that the polyfill is configured to work in a cross-origin mode.
 * @type {function(DOMRect|ClientRect, DOMRect|ClientRect)}
 */
var crossOriginUpdater = null;

/**
 * The current cross-origin intersection. Only used in the cross-origin mode.
 * @type {DOMRect|ClientRect}
 */
var crossOriginRect = null;


/**
 * Creates the global IntersectionObserverEntry constructor.
 * https://w3c.github.io/IntersectionObserver/#intersection-observer-entry
 * @param {Object} entry A dictionary of instance properties.
 * @constructor
 */
function IntersectionObserverEntry(entry) {
  this.time = entry.time;
  this.target = entry.target;
  this.rootBounds = ensureDOMRect(entry.rootBounds);
  this.boundingClientRect = ensureDOMRect(entry.boundingClientRect);
  this.intersectionRect = ensureDOMRect(entry.intersectionRect || getEmptyRect());
  this.isIntersecting = !!entry.intersectionRect;

  // Calculates the intersection ratio.
  var targetRect = this.boundingClientRect;
  var targetArea = targetRect.width * targetRect.height;
  var intersectionRect = this.intersectionRect;
  var intersectionArea = intersectionRect.width * intersectionRect.height;

  // Sets intersection ratio.
  if (targetArea) {
    // Round the intersection ratio to avoid floating point math issues:
    // https://github.com/w3c/IntersectionObserver/issues/324
    this.intersectionRatio = Number((intersectionArea / targetArea).toFixed(4));
  } else {
    // If area is zero and is intersecting, sets to 1, otherwise to 0
    this.intersectionRatio = this.isIntersecting ? 1 : 0;
  }
}


/**
 * Creates the global IntersectionObserver constructor.
 * https://w3c.github.io/IntersectionObserver/#intersection-observer-interface
 * @param {Function} callback The function to be invoked after intersection
 *     changes have queued. The function is not invoked if the queue has
 *     been emptied by calling the `takeRecords` method.
 * @param {Object=} opt_options Optional configuration options.
 * @constructor
 */
function IntersectionObserver(callback, opt_options) {

  var options = opt_options || {};

  if (typeof callback != 'function') {
    throw new Error('callback must be a function');
  }

  if (
    options.root &&
    options.root.nodeType != 1 &&
    options.root.nodeType != 9
  ) {
    throw new Error('root must be a Document or Element');
  }

  // Binds and throttles `this._checkForIntersections`.
  this._checkForIntersections = throttle(
      this._checkForIntersections.bind(this), this.THROTTLE_TIMEOUT);

  // Private properties.
  this._callback = callback;
  this._observationTargets = [];
  this._queuedEntries = [];
  this._rootMarginValues = this._parseRootMargin(options.rootMargin);

  // Public properties.
  this.thresholds = this._initThresholds(options.threshold);
  this.root = options.root || null;
  this.rootMargin = this._rootMarginValues.map(function(margin) {
    return margin.value + margin.unit;
  }).join(' ');

  /** @private @const {!Array<!Document>} */
  this._monitoringDocuments = [];
  /** @private @const {!Array<function()>} */
  this._monitoringUnsubscribes = [];
}


/**
 * The minimum interval within which the document will be checked for
 * intersection changes.
 */
IntersectionObserver.prototype.THROTTLE_TIMEOUT = 100;


/**
 * The frequency in which the polyfill polls for intersection changes.
 * this can be updated on a per instance basis and must be set prior to
 * calling `observe` on the first target.
 */
IntersectionObserver.prototype.POLL_INTERVAL = null;

/**
 * Use a mutation observer on the root element
 * to detect intersection changes.
 */
IntersectionObserver.prototype.USE_MUTATION_OBSERVER = true;


/**
 * Sets up the polyfill in the cross-origin mode. The result is the
 * updater function that accepts two arguments: `boundingClientRect` and
 * `intersectionRect` - just as these fields would be available to the
 * parent via `IntersectionObserverEntry`. This function should be called
 * each time the iframe receives intersection information from the parent
 * window, e.g. via messaging.
 * @return {function(DOMRect|ClientRect, DOMRect|ClientRect)}
 */
IntersectionObserver._setupCrossOriginUpdater = function() {
  if (!crossOriginUpdater) {
    /**
     * @param {DOMRect|ClientRect} boundingClientRect
     * @param {DOMRect|ClientRect} intersectionRect
     */
    crossOriginUpdater = function(boundingClientRect, intersectionRect) {
      if (!boundingClientRect || !intersectionRect) {
        crossOriginRect = getEmptyRect();
      } else {
        crossOriginRect = convertFromParentRect(boundingClientRect, intersectionRect);
      }
      registry.forEach(function(observer) {
        observer._checkForIntersections();
      });
    };
  }
  return crossOriginUpdater;
};


/**
 * Resets the cross-origin mode.
 */
IntersectionObserver._resetCrossOriginUpdater = function() {
  crossOriginUpdater = null;
  crossOriginRect = null;
};


/**
 * Starts observing a target element for intersection changes based on
 * the thresholds values.
 * @param {Element} target The DOM element to observe.
 */
IntersectionObserver.prototype.observe = function(target) {
  var isTargetAlreadyObserved = this._observationTargets.some(function(item) {
    return item.element == target;
  });

  if (isTargetAlreadyObserved) {
    return;
  }

  if (!(target && target.nodeType == 1)) {
    throw new Error('target must be an Element');
  }

  this._registerInstance();
  this._observationTargets.push({element: target, entry: null});
  this._monitorIntersections(target.ownerDocument);
  this._checkForIntersections();
};


/**
 * Stops observing a target element for intersection changes.
 * @param {Element} target The DOM element to observe.
 */
IntersectionObserver.prototype.unobserve = function(target) {
  this._observationTargets =
      this._observationTargets.filter(function(item) {
        return item.element != target;
      });
  this._unmonitorIntersections(target.ownerDocument);
  if (this._observationTargets.length == 0) {
    this._unregisterInstance();
  }
};


/**
 * Stops observing all target elements for intersection changes.
 */
IntersectionObserver.prototype.disconnect = function() {
  this._observationTargets = [];
  this._unmonitorAllIntersections();
  this._unregisterInstance();
};


/**
 * Returns any queue entries that have not yet been reported to the
 * callback and clears the queue. This can be used in conjunction with the
 * callback to obtain the absolute most up-to-date intersection information.
 * @return {Array} The currently queued entries.
 */
IntersectionObserver.prototype.takeRecords = function() {
  var records = this._queuedEntries.slice();
  this._queuedEntries = [];
  return records;
};


/**
 * Accepts the threshold value from the user configuration object and
 * returns a sorted array of unique threshold values. If a value is not
 * between 0 and 1 and error is thrown.
 * @private
 * @param {Array|number=} opt_threshold An optional threshold value or
 *     a list of threshold values, defaulting to [0].
 * @return {Array} A sorted list of unique and valid threshold values.
 */
IntersectionObserver.prototype._initThresholds = function(opt_threshold) {
  var threshold = opt_threshold || [0];
  if (!Array.isArray(threshold)) threshold = [threshold];

  return threshold.sort().filter(function(t, i, a) {
    if (typeof t != 'number' || isNaN(t) || t < 0 || t > 1) {
      throw new Error('threshold must be a number between 0 and 1 inclusively');
    }
    return t !== a[i - 1];
  });
};


/**
 * Accepts the rootMargin value from the user configuration object
 * and returns an array of the four margin values as an object containing
 * the value and unit properties. If any of the values are not properly
 * formatted or use a unit other than px or %, and error is thrown.
 * @private
 * @param {string=} opt_rootMargin An optional rootMargin value,
 *     defaulting to '0px'.
 * @return {Array<Object>} An array of margin objects with the keys
 *     value and unit.
 */
IntersectionObserver.prototype._parseRootMargin = function(opt_rootMargin) {
  var marginString = opt_rootMargin || '0px';
  var margins = marginString.split(/\s+/).map(function(margin) {
    var parts = /^(-?\d*\.?\d+)(px|%)$/.exec(margin);
    if (!parts) {
      throw new Error('rootMargin must be specified in pixels or percent');
    }
    return {value: parseFloat(parts[1]), unit: parts[2]};
  });

  // Handles shorthand.
  margins[1] = margins[1] || margins[0];
  margins[2] = margins[2] || margins[0];
  margins[3] = margins[3] || margins[1];

  return margins;
};


/**
 * Starts polling for intersection changes if the polling is not already
 * happening, and if the page's visibility state is visible.
 * @param {!Document} doc
 * @private
 */
IntersectionObserver.prototype._monitorIntersections = function(doc) {
  var win = doc.defaultView;
  if (!win) {
    // Already destroyed.
    return;
  }
  if (this._monitoringDocuments.indexOf(doc) != -1) {
    // Already monitoring.
    return;
  }

  // Private state for monitoring.
  var callback = this._checkForIntersections;
  var monitoringInterval = null;
  var domObserver = null;

  // If a poll interval is set, use polling instead of listening to
  // resize and scroll events or DOM mutations.
  if (this.POLL_INTERVAL) {
    monitoringInterval = win.setInterval(callback, this.POLL_INTERVAL);
  } else {
    addEvent(win, 'resize', callback, true);
    addEvent(doc, 'scroll', callback, true);
    if (this.USE_MUTATION_OBSERVER && 'MutationObserver' in win) {
      domObserver = new win.MutationObserver(callback);
      domObserver.observe(doc, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true
      });
    }
  }

  this._monitoringDocuments.push(doc);
  this._monitoringUnsubscribes.push(function() {
    // Get the window object again. When a friendly iframe is destroyed, it
    // will be null.
    var win = doc.defaultView;

    if (win) {
      if (monitoringInterval) {
        win.clearInterval(monitoringInterval);
      }
      removeEvent(win, 'resize', callback, true);
    }

    removeEvent(doc, 'scroll', callback, true);
    if (domObserver) {
      domObserver.disconnect();
    }
  });

  // Also monitor the parent.
  var rootDoc =
    (this.root && (this.root.ownerDocument || this.root)) || document;
  if (doc != rootDoc) {
    var frame = getFrameElement(doc);
    if (frame) {
      this._monitorIntersections(frame.ownerDocument);
    }
  }
};


/**
 * Stops polling for intersection changes.
 * @param {!Document} doc
 * @private
 */
IntersectionObserver.prototype._unmonitorIntersections = function(doc) {
  var index = this._monitoringDocuments.indexOf(doc);
  if (index == -1) {
    return;
  }

  var rootDoc =
    (this.root && (this.root.ownerDocument || this.root)) || document;

  // Check if any dependent targets are still remaining.
  var hasDependentTargets =
      this._observationTargets.some(function(item) {
        var itemDoc = item.element.ownerDocument;
        // Target is in this context.
        if (itemDoc == doc) {
          return true;
        }
        // Target is nested in this context.
        while (itemDoc && itemDoc != rootDoc) {
          var frame = getFrameElement(itemDoc);
          itemDoc = frame && frame.ownerDocument;
          if (itemDoc == doc) {
            return true;
          }
        }
        return false;
      });
  if (hasDependentTargets) {
    return;
  }

  // Unsubscribe.
  var unsubscribe = this._monitoringUnsubscribes[index];
  this._monitoringDocuments.splice(index, 1);
  this._monitoringUnsubscribes.splice(index, 1);
  unsubscribe();

  // Also unmonitor the parent.
  if (doc != rootDoc) {
    var frame = getFrameElement(doc);
    if (frame) {
      this._unmonitorIntersections(frame.ownerDocument);
    }
  }
};


/**
 * Stops polling for intersection changes.
 * @param {!Document} doc
 * @private
 */
IntersectionObserver.prototype._unmonitorAllIntersections = function() {
  var unsubscribes = this._monitoringUnsubscribes.slice(0);
  this._monitoringDocuments.length = 0;
  this._monitoringUnsubscribes.length = 0;
  for (var i = 0; i < unsubscribes.length; i++) {
    unsubscribes[i]();
  }
};


/**
 * Scans each observation target for intersection changes and adds them
 * to the internal entries queue. If new entries are found, it
 * schedules the callback to be invoked.
 * @private
 */
IntersectionObserver.prototype._checkForIntersections = function() {
  if (!this.root && crossOriginUpdater && !crossOriginRect) {
    // Cross origin monitoring, but no initial data available yet.
    return;
  }

  var rootIsInDom = this._rootIsInDom();
  var rootRect = rootIsInDom ? this._getRootRect() : getEmptyRect();

  this._observationTargets.forEach(function(item) {
    var target = item.element;
    var targetRect = getBoundingClientRect(target);
    var rootContainsTarget = this._rootContainsTarget(target);
    var oldEntry = item.entry;
    var intersectionRect = rootIsInDom && rootContainsTarget &&
        this._computeTargetAndRootIntersection(target, targetRect, rootRect);

    var rootBounds = null;
    if (!this._rootContainsTarget(target)) {
      rootBounds = getEmptyRect();
    } else if (!crossOriginUpdater || this.root) {
      rootBounds = rootRect;
    }

    var newEntry = item.entry = new IntersectionObserverEntry({
      time: now(),
      target: target,
      boundingClientRect: targetRect,
      rootBounds: rootBounds,
      intersectionRect: intersectionRect
    });

    if (!oldEntry) {
      this._queuedEntries.push(newEntry);
    } else if (rootIsInDom && rootContainsTarget) {
      // If the new entry intersection ratio has crossed any of the
      // thresholds, add a new entry.
      if (this._hasCrossedThreshold(oldEntry, newEntry)) {
        this._queuedEntries.push(newEntry);
      }
    } else {
      // If the root is not in the DOM or target is not contained within
      // root but the previous entry for this target had an intersection,
      // add a new record indicating removal.
      if (oldEntry && oldEntry.isIntersecting) {
        this._queuedEntries.push(newEntry);
      }
    }
  }, this);

  if (this._queuedEntries.length) {
    this._callback(this.takeRecords(), this);
  }
};


/**
 * Accepts a target and root rect computes the intersection between then
 * following the algorithm in the spec.
 * TODO(philipwalton): at this time clip-path is not considered.
 * https://w3c.github.io/IntersectionObserver/#calculate-intersection-rect-algo
 * @param {Element} target The target DOM element
 * @param {Object} targetRect The bounding rect of the target.
 * @param {Object} rootRect The bounding rect of the root after being
 *     expanded by the rootMargin value.
 * @return {?Object} The final intersection rect object or undefined if no
 *     intersection is found.
 * @private
 */
IntersectionObserver.prototype._computeTargetAndRootIntersection =
    function(target, targetRect, rootRect) {
  // If the element isn't displayed, an intersection can't happen.
  if (window.getComputedStyle(target).display == 'none') return;

  var intersectionRect = targetRect;
  var parent = getParentNode(target);
  var atRoot = false;

  while (!atRoot && parent) {
    var parentRect = null;
    var parentComputedStyle = parent.nodeType == 1 ?
        window.getComputedStyle(parent) : {};

    // If the parent isn't displayed, an intersection can't happen.
    if (parentComputedStyle.display == 'none') return null;

    if (parent == this.root || parent.nodeType == /* DOCUMENT */ 9) {
      atRoot = true;
      if (parent == this.root || parent == document) {
        if (crossOriginUpdater && !this.root) {
          if (!crossOriginRect ||
              crossOriginRect.width == 0 && crossOriginRect.height == 0) {
            // A 0-size cross-origin intersection means no-intersection.
            parent = null;
            parentRect = null;
            intersectionRect = null;
          } else {
            parentRect = crossOriginRect;
          }
        } else {
          parentRect = rootRect;
        }
      } else {
        // Check if there's a frame that can be navigated to.
        var frame = getParentNode(parent);
        var frameRect = frame && getBoundingClientRect(frame);
        var frameIntersect =
            frame &&
            this._computeTargetAndRootIntersection(frame, frameRect, rootRect);
        if (frameRect && frameIntersect) {
          parent = frame;
          parentRect = convertFromParentRect(frameRect, frameIntersect);
        } else {
          parent = null;
          intersectionRect = null;
        }
      }
    } else {
      // If the element has a non-visible overflow, and it's not the <body>
      // or <html> element, update the intersection rect.
      // Note: <body> and <html> cannot be clipped to a rect that's not also
      // the document rect, so no need to compute a new intersection.
      var doc = parent.ownerDocument;
      if (parent != doc.body &&
          parent != doc.documentElement &&
          parentComputedStyle.overflow != 'visible') {
        parentRect = getBoundingClientRect(parent);
      }
    }

    // If either of the above conditionals set a new parentRect,
    // calculate new intersection data.
    if (parentRect) {
      intersectionRect = computeRectIntersection(parentRect, intersectionRect);
    }
    if (!intersectionRect) break;
    parent = parent && getParentNode(parent);
  }
  return intersectionRect;
};


/**
 * Returns the root rect after being expanded by the rootMargin value.
 * @return {ClientRect} The expanded root rect.
 * @private
 */
IntersectionObserver.prototype._getRootRect = function() {
  var rootRect;
  if (this.root && !isDoc(this.root)) {
    rootRect = getBoundingClientRect(this.root);
  } else {
    // Use <html>/<body> instead of window since scroll bars affect size.
    var doc = isDoc(this.root) ? this.root : document;
    var html = doc.documentElement;
    var body = doc.body;
    rootRect = {
      top: 0,
      left: 0,
      right: html.clientWidth || body.clientWidth,
      width: html.clientWidth || body.clientWidth,
      bottom: html.clientHeight || body.clientHeight,
      height: html.clientHeight || body.clientHeight
    };
  }
  return this._expandRectByRootMargin(rootRect);
};


/**
 * Accepts a rect and expands it by the rootMargin value.
 * @param {DOMRect|ClientRect} rect The rect object to expand.
 * @return {ClientRect} The expanded rect.
 * @private
 */
IntersectionObserver.prototype._expandRectByRootMargin = function(rect) {
  var margins = this._rootMarginValues.map(function(margin, i) {
    return margin.unit == 'px' ? margin.value :
        margin.value * (i % 2 ? rect.width : rect.height) / 100;
  });
  var newRect = {
    top: rect.top - margins[0],
    right: rect.right + margins[1],
    bottom: rect.bottom + margins[2],
    left: rect.left - margins[3]
  };
  newRect.width = newRect.right - newRect.left;
  newRect.height = newRect.bottom - newRect.top;

  return newRect;
};


/**
 * Accepts an old and new entry and returns true if at least one of the
 * threshold values has been crossed.
 * @param {?IntersectionObserverEntry} oldEntry The previous entry for a
 *    particular target element or null if no previous entry exists.
 * @param {IntersectionObserverEntry} newEntry The current entry for a
 *    particular target element.
 * @return {boolean} Returns true if a any threshold has been crossed.
 * @private
 */
IntersectionObserver.prototype._hasCrossedThreshold =
    function(oldEntry, newEntry) {

  // To make comparing easier, an entry that has a ratio of 0
  // but does not actually intersect is given a value of -1
  var oldRatio = oldEntry && oldEntry.isIntersecting ?
      oldEntry.intersectionRatio || 0 : -1;
  var newRatio = newEntry.isIntersecting ?
      newEntry.intersectionRatio || 0 : -1;

  // Ignore unchanged ratios
  if (oldRatio === newRatio) return;

  for (var i = 0; i < this.thresholds.length; i++) {
    var threshold = this.thresholds[i];

    // Return true if an entry matches a threshold or if the new ratio
    // and the old ratio are on the opposite sides of a threshold.
    if (threshold == oldRatio || threshold == newRatio ||
        threshold < oldRatio !== threshold < newRatio) {
      return true;
    }
  }
};


/**
 * Returns whether or not the root element is an element and is in the DOM.
 * @return {boolean} True if the root element is an element and is in the DOM.
 * @private
 */
IntersectionObserver.prototype._rootIsInDom = function() {
  return !this.root || containsDeep(document, this.root);
};


/**
 * Returns whether or not the target element is a child of root.
 * @param {Element} target The target element to check.
 * @return {boolean} True if the target element is a child of root.
 * @private
 */
IntersectionObserver.prototype._rootContainsTarget = function(target) {
  var rootDoc =
    (this.root && (this.root.ownerDocument || this.root)) || document;
  return (
    containsDeep(rootDoc, target) &&
    (!this.root || rootDoc == target.ownerDocument)
  );
};


/**
 * Adds the instance to the global IntersectionObserver registry if it isn't
 * already present.
 * @private
 */
IntersectionObserver.prototype._registerInstance = function() {
  if (registry.indexOf(this) < 0) {
    registry.push(this);
  }
};


/**
 * Removes the instance from the global IntersectionObserver registry.
 * @private
 */
IntersectionObserver.prototype._unregisterInstance = function() {
  var index = registry.indexOf(this);
  if (index != -1) registry.splice(index, 1);
};


/**
 * Returns the result of the performance.now() method or null in browsers
 * that don't support the API.
 * @return {number} The elapsed time since the page was requested.
 */
function now() {
  return window.performance && performance.now && performance.now();
}


/**
 * Throttles a function and delays its execution, so it's only called at most
 * once within a given time period.
 * @param {Function} fn The function to throttle.
 * @param {number} timeout The amount of time that must pass before the
 *     function can be called again.
 * @return {Function} The throttled function.
 */
function throttle(fn, timeout) {
  var timer = null;
  return function () {
    if (!timer) {
      timer = setTimeout(function() {
        fn();
        timer = null;
      }, timeout);
    }
  };
}


/**
 * Adds an event handler to a DOM node ensuring cross-browser compatibility.
 * @param {Node} node The DOM node to add the event handler to.
 * @param {string} event The event name.
 * @param {Function} fn The event handler to add.
 * @param {boolean} opt_useCapture Optionally adds the even to the capture
 *     phase. Note: this only works in modern browsers.
 */
function addEvent(node, event, fn, opt_useCapture) {
  if (typeof node.addEventListener == 'function') {
    node.addEventListener(event, fn, opt_useCapture || false);
  }
  else if (typeof node.attachEvent == 'function') {
    node.attachEvent('on' + event, fn);
  }
}


/**
 * Removes a previously added event handler from a DOM node.
 * @param {Node} node The DOM node to remove the event handler from.
 * @param {string} event The event name.
 * @param {Function} fn The event handler to remove.
 * @param {boolean} opt_useCapture If the event handler was added with this
 *     flag set to true, it should be set to true here in order to remove it.
 */
function removeEvent(node, event, fn, opt_useCapture) {
  if (typeof node.removeEventListener == 'function') {
    node.removeEventListener(event, fn, opt_useCapture || false);
  }
  else if (typeof node.detachEvent == 'function') {
    node.detachEvent('on' + event, fn);
  }
}


/**
 * Returns the intersection between two rect objects.
 * @param {Object} rect1 The first rect.
 * @param {Object} rect2 The second rect.
 * @return {?Object|?ClientRect} The intersection rect or undefined if no
 *     intersection is found.
 */
function computeRectIntersection(rect1, rect2) {
  var top = Math.max(rect1.top, rect2.top);
  var bottom = Math.min(rect1.bottom, rect2.bottom);
  var left = Math.max(rect1.left, rect2.left);
  var right = Math.min(rect1.right, rect2.right);
  var width = right - left;
  var height = bottom - top;

  return (width >= 0 && height >= 0) && {
    top: top,
    bottom: bottom,
    left: left,
    right: right,
    width: width,
    height: height
  } || null;
}


/**
 * Shims the native getBoundingClientRect for compatibility with older IE.
 * @param {Element} el The element whose bounding rect to get.
 * @return {DOMRect|ClientRect} The (possibly shimmed) rect of the element.
 */
function getBoundingClientRect(el) {
  var rect;

  try {
    rect = el.getBoundingClientRect();
  } catch (err) {
    // Ignore Windows 7 IE11 "Unspecified error"
    // https://github.com/w3c/IntersectionObserver/pull/205
  }

  if (!rect) return getEmptyRect();

  // Older IE
  if (!(rect.width && rect.height)) {
    rect = {
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.right - rect.left,
      height: rect.bottom - rect.top
    };
  }
  return rect;
}


/**
 * Returns an empty rect object. An empty rect is returned when an element
 * is not in the DOM.
 * @return {ClientRect} The empty rect.
 */
function getEmptyRect() {
  return {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    width: 0,
    height: 0
  };
}


/**
 * Ensure that the result has all of the necessary fields of the DOMRect.
 * Specifically this ensures that `x` and `y` fields are set.
 *
 * @param {?DOMRect|?ClientRect} rect
 * @return {?DOMRect}
 */
function ensureDOMRect(rect) {
  // A `DOMRect` object has `x` and `y` fields.
  if (!rect || 'x' in rect) {
    return rect;
  }
  // A IE's `ClientRect` type does not have `x` and `y`. The same is the case
  // for internally calculated Rect objects. For the purposes of
  // `IntersectionObserver`, it's sufficient to simply mirror `left` and `top`
  // for these fields.
  return {
    top: rect.top,
    y: rect.top,
    bottom: rect.bottom,
    left: rect.left,
    x: rect.left,
    right: rect.right,
    width: rect.width,
    height: rect.height
  };
}


/**
 * Inverts the intersection and bounding rect from the parent (frame) BCR to
 * the local BCR space.
 * @param {DOMRect|ClientRect} parentBoundingRect The parent's bound client rect.
 * @param {DOMRect|ClientRect} parentIntersectionRect The parent's own intersection rect.
 * @return {ClientRect} The local root bounding rect for the parent's children.
 */
function convertFromParentRect(parentBoundingRect, parentIntersectionRect) {
  var top = parentIntersectionRect.top - parentBoundingRect.top;
  var left = parentIntersectionRect.left - parentBoundingRect.left;
  return {
    top: top,
    left: left,
    height: parentIntersectionRect.height,
    width: parentIntersectionRect.width,
    bottom: top + parentIntersectionRect.height,
    right: left + parentIntersectionRect.width
  };
}


/**
 * Checks to see if a parent element contains a child element (including inside
 * shadow DOM).
 * @param {Node} parent The parent element.
 * @param {Node} child The child element.
 * @return {boolean} True if the parent node contains the child node.
 */
function containsDeep(parent, child) {
  var node = child;
  while (node) {
    if (node == parent) return true;

    node = getParentNode(node);
  }
  return false;
}


/**
 * Gets the parent node of an element or its host element if the parent node
 * is a shadow root.
 * @param {Node} node The node whose parent to get.
 * @return {Node|null} The parent node or null if no parent exists.
 */
function getParentNode(node) {
  var parent = node.parentNode;

  if (node.nodeType == /* DOCUMENT */ 9 && node != document) {
    // If this node is a document node, look for the embedding frame.
    return getFrameElement(node);
  }

  // If the parent has element that is assigned through shadow root slot
  if (parent && parent.assignedSlot) {
    parent = parent.assignedSlot.parentNode;
  }

  if (parent && parent.nodeType == 11 && parent.host) {
    // If the parent is a shadow root, return the host element.
    return parent.host;
  }

  return parent;
}

/**
 * Returns true if `node` is a Document.
 * @param {!Node} node
 * @returns {boolean}
 */
function isDoc(node) {
  return node && node.nodeType === 9;
}


// Exposes the constructors globally.
window.IntersectionObserver = IntersectionObserver;
window.IntersectionObserverEntry = IntersectionObserverEntry;

}());

let accessibilityChannelOut = null;
function createAccessibilityChannel() {
    return new index_es5.BroadcastChannel(accessibilityChannelName);
}
function messageAccessibilityChannel(accessibility) {
    if (!accessibilityChannelOut) {
        accessibilityChannelOut = createAccessibilityChannel();
    }
    accessibilityChannelOut.postMessage(accessibility);
}
async function setUserAccessibility(accessibility, organizationStandardUrl, token, partitionGlobalId, userId, isMsi, { shouldBroadcast = true } = {}) {
    if (shouldBroadcast) {
        messageAccessibilityChannel(accessibility);
    }
    const settingsUrl = `${organizationStandardUrl}/${isMsi ? 'identity/api' : 'portal_/api/identity'}/Setting`;
    await put(settingsUrl, {
        token,
        body: {
            settings: [
                {
                    key: ApolloSettingKey.UserAccessibility,
                    value: accessibility.toString(),
                },
            ],
            partitionGlobalId,
            userId,
        },
    });
}
/**
 * @returns The disposer
 */
function handleAccessibilityChange(callback) {
    const channel = createAccessibilityChannel();
    channel.onmessage = accessibility => {
        callback(accessibility);
    };
    return () => {
        channel.close();
    };
}

let alertDurationChannelOut = null;
function createAlertDurationChannel() {
    return new index_es5.BroadcastChannel(alertDurationChannelName);
}
function messageAlertDurationChannel(alertDuration) {
    if (!alertDurationChannelOut) {
        alertDurationChannelOut = createAlertDurationChannel();
    }
    alertDurationChannelOut.postMessage(alertDuration);
}
async function setUserAlertDuration(alertDuration, organizationStandardUrl, token, partitionGlobalId, userId, isMsi, { shouldBroadcast = true } = {}) {
    if (shouldBroadcast) {
        messageAlertDurationChannel(alertDuration);
    }
    const settingsUrl = `${organizationStandardUrl}/${isMsi ? 'identity/api' : 'portal_/api/identity'}/Setting`;
    await put(settingsUrl, {
        token,
        body: {
            settings: [
                {
                    key: ApolloSettingKey.UserAlert,
                    value: JSON.stringify(alertDuration),
                },
            ],
            partitionGlobalId,
            userId,
        },
    });
}
/**
 * @return The disposer
 */
function handleAlertDurationChange(callback) {
    const channel = createAlertDurationChannel();
    channel.onmessage = alertDuration => {
        callback(alertDuration);
    };
    return () => {
        channel.close();
    };
}

const globalStyleGeneratedCss = "body.light{--color-background:#ffffff;--color-background-secondary:#f4f5f7;--color-background-inverse:#182027;--color-background-gray:#cfd8dd;--color-background-disabled:#ecedee;--color-background-highlight:#fee3dc;--color-background-raised:#ffffff;--color-background-edit:#ffffff;--color-background-gray-emp:#a4b1b8;--color-background-hover:rgba(82, 96, 105, 0.0784313725);--color-background-pressed:rgba(82, 96, 105, 0.1215686275);--color-background-selected:#e9f1fa;--color-selection-indicator:#0067df;--color-notification-badge:#000000;--color-notification-indicator:#da3b11;--color-hover:#e9f1fa;--color-curtain:#000000;--color-border:#a4b1b8;--color-border-disabled:#cfd8dd;--color-border-grid:#f4f5f7;--color-border-de-emp:#cfd8dd;--color-selection:#da3b11;--color-logo:#fa4616;--color-foreground:#273139;--color-foreground-light:#6b7882;--color-foreground-emp:#182027;--color-foreground-de-emp:#526069;--color-foreground-disable:#8a97a0;--color-foreground-inverse:#f8f9fa;--color-foreground-highlight:#da3b11;--color-foreground-highlight-selected:rgba(218, 59, 17, 0.1019607843);--color-foreground-highlight-selected-hover:rgba(218, 59, 17, 0.1490196078);--color-foreground-link:#0067df;--color-foreground-link-pressed:#00489d;--color-foreground-inv-de-emp:#cfd8dd;--color-primary:#0067df;--color-primary-lighter:#badaff;--color-primary-darker:#00489d;--color-primary-hover:#0056ba;--color-primary-focused:#0056ba;--color-primary-pressed:#0067df;--color-secondary-focused:#e9f1fa;--color-secondary-pressed:#b2d2f3;--color-chip-default-background:#cfd8dd;--color-chip-error-background:#fff0f1;--color-chip-warning-background:#fff3db;--color-chip-info-background:#e9f1fa;--color-chip-success-background:#eeffe5;--color-error-icon:#cc3d45;--color-error-icon-inverse:#ff8484;--color-error-text:#a6040a;--color-error-background:#fff0f1;--color-error-foreground-inverse:#ff8484;--color-info-foreground:#1665b3;--color-info-icon:#1976d2;--color-info-icon-inverse:#42a1ff;--color-info-text:#1665b3;--color-info-foreground-inverse:#42a1ff;--color-info-background:#e9f1fa;--color-success-icon:#038108;--color-success-icon-inverse:#74c94b;--color-success-text:#038108;--color-success-background:#eeffe5;--color-success-foreground-inverse:#74c94b;--color-warning-icon:#ffb40e;--color-warning-icon-inverse:#ffbb27;--color-warning-text:#9e6100;--color-warning-background:#fff3db;--color-warning-foreground-inverse:#ffbb27;--color-icon-default:#526069;--color-icon-inverted-default:#cfd8dd;--color-toggle-off-hover:rgba(82, 96, 105, 0.0784313725);--color-toggle-off-focus:rgba(82, 96, 105, 0.0784313725);--color-toggle-off-pressed:rgba(82, 96, 105, 0.1215686275);--color-toggle-on-hover:rgba(0, 103, 223, 0.0784313725);--color-toggle-on-focus:rgba(0, 103, 223, 0.0784313725);--color-toggle-on-pressed:rgba(0, 103, 223, 0.1215686275);--color-callout-background:#00489d;--color-warn:#b6242b;--color-warn-darker:#a6040a;--color-warn-hover:rgba(182, 36, 43, 0.0392156863);--color-warn-focus:rgba(182, 36, 43, 0.1215686275);--color-warn-pressed:rgba(182, 36, 43, 0.0784313725);--color-carousel-background:#f5f9fe;--color-icon-button-hover:rgba(82, 96, 105, 0.0784313725);--color-icon-button-focus:rgba(82, 96, 105, 0.0784313725);--color-icon-button-pressed:rgba(82, 96, 105, 0.1215686275);--color-code-function:#b748b6;--color-code-operator:#0067df;--color-code-numeric:#1e7f5a;--color-code-text:#d91153;--color-toggle-thumb-off:#526069;--color-toggle-track-off:#cfd8dd;--color-toggle-thumb-on:#0067df;--color-toggle-track-on:#badaff;--color-toggle-thumb-off-disabled:#cfd8dd;--color-toggle-track-off-disabled:#ecedee;--color-toggle-thumb-on-disabled:rgba(0, 103, 223, 0.2784313725);--color-toggle-track-on-disabled:rgba(186, 218, 255, 0.3803921569);--color-skeleton:#ecedee;--color-skeleton-glow:#fbfbfc;--color-data-grid-hover:#f1f2f3;--color-data-grid-pressed:#eaeced;--color-focus-indicator:#0067df;--color-focus-indicator-inverse:#66adff;--color-chart-purple:#933692;--color-chart-pink:#ed145b;--color-chart-blue-secondary:#42a1ff;--color-chart-light-blue:#38c6f4;--color-chart-green:#6eb84a;--color-chart-yellow:#ffb40e;--border:1px solid #a4b1b8;color-scheme:light}body.light-hc{--color-background:#ffffff;--color-background-secondary:#f4f5f7;--color-background-inverse:#182027;--color-background-gray:#cfd8dd;--color-background-disabled:#ecedee;--color-background-highlight:#fee3dc;--color-background-raised:#ffffff;--color-background-edit:#ffffff;--color-background-gray-emp:#a4b1b8;--color-background-hover:rgba(82, 96, 105, 0.0784313725);--color-background-pressed:rgba(82, 96, 105, 0.1215686275);--color-background-selected:#e9f1fa;--color-selection-indicator:#00489d;--color-notification-badge:#000000;--color-notification-indicator:#832107;--color-hover:#e9f1fa;--color-curtain:#000000;--color-border:#6b7882;--color-border-disabled:#cfd8dd;--color-border-grid:#f4f5f7;--color-border-de-emp:#a4b1b8;--color-selection:#832107;--color-logo:#fa4616;--color-foreground:#273139;--color-foreground-light:#374652;--color-foreground-emp:#182027;--color-foreground-de-emp:#374652;--color-foreground-disable:#8a97a0;--color-foreground-inverse:#f8f9fa;--color-foreground-highlight:#832107;--color-foreground-highlight-selected:rgba(131, 33, 7, 0.1019607843);--color-foreground-highlight-selected-hover:rgba(131, 33, 7, 0.1490196078);--color-foreground-link:#00489d;--color-foreground-link-pressed:#00336f;--color-foreground-inv-de-emp:#cfd8dd;--color-primary:#00489d;--color-primary-lighter:#badaff;--color-primary-darker:#11508d;--color-primary-hover:#00336f;--color-primary-focused:#00336f;--color-primary-pressed:#00336f;--color-secondary-focused:#e9f1fa;--color-secondary-pressed:#b2d2f3;--color-chip-default-background:#cfd8dd;--color-chip-error-background:#fff0f1;--color-chip-warning-background:#fff3db;--color-chip-info-background:#e9f1fa;--color-chip-success-background:#eeffe5;--color-error-icon:#a6040a;--color-error-icon-inverse:#ff8484;--color-error-text:#a6040a;--color-error-background:#fff0f1;--color-error-foreground-inverse:#ff8484;--color-info-foreground:#11508d;--color-info-icon:#11508d;--color-info-icon-inverse:#42a1ff;--color-info-text:#11508d;--color-info-foreground-inverse:#42a1ff;--color-info-background:#e9f1fa;--color-success-icon:#005603;--color-success-icon-inverse:#74c94b;--color-success-text:#005603;--color-success-background:#eeffe5;--color-success-foreground-inverse:#74c94b;--color-warning-icon:#745107;--color-warning-icon-inverse:#ffbb27;--color-warning-text:#273139;--color-warning-background:#fff3db;--color-warning-foreground-inverse:#ffbb27;--color-icon-default:#374652;--color-icon-inverted-default:#cfd8dd;--color-toggle-off-hover:rgba(82, 96, 105, 0.0784313725);--color-toggle-off-focus:rgba(82, 96, 105, 0.0784313725);--color-toggle-off-pressed:rgba(82, 96, 105, 0.1215686275);--color-toggle-on-hover:rgba(0, 103, 223, 0.0784313725);--color-toggle-on-focus:rgba(0, 103, 223, 0.0784313725);--color-toggle-on-pressed:rgba(0, 103, 223, 0.1215686275);--color-callout-background:#00489d;--color-warn:#b6242b;--color-warn-darker:#a6040a;--color-warn-hover:rgba(182, 36, 43, 0.0392156863);--color-warn-focus:rgba(182, 36, 43, 0.1215686275);--color-warn-pressed:rgba(182, 36, 43, 0.0784313725);--color-carousel-background:#f5f9fe;--color-icon-button-hover:rgba(82, 96, 105, 0.0784313725);--color-icon-button-focus:rgba(82, 96, 105, 0.0784313725);--color-icon-button-pressed:rgba(82, 96, 105, 0.1215686275);--color-code-function:#8c338b;--color-code-operator:#00489d;--color-code-numeric:#176245;--color-code-text:#a60e3f;--color-toggle-thumb-off:#374652;--color-toggle-track-off:#cfd8dd;--color-toggle-thumb-on:#00489d;--color-toggle-track-on:#badaff;--color-toggle-thumb-off-disabled:#cfd8dd;--color-toggle-track-off-disabled:#ecedee;--color-toggle-thumb-on-disabled:rgba(0, 103, 223, 0.2784313725);--color-toggle-track-on-disabled:rgba(186, 218, 255, 0.3803921569);--color-skeleton:#ecedee;--color-skeleton-glow:#fbfbfc;--color-data-grid-hover:#f1f2f3;--color-data-grid-pressed:#eaeced;--color-focus-indicator:#00489d;--color-focus-indicator-inverse:#badaff;--color-chart-purple:#8c338b;--color-chart-pink:#d91153;--color-chart-blue-secondary:#1976d2;--color-chart-light-blue:#278aab;--color-chart-green:#038108;--color-chart-yellow:#9e6100;--border:1px solid #6b7882;color-scheme:light-hc}body.dark{--color-background:#182027;--color-background-secondary:#273139;--color-background-inverse:#f8f9fa;--color-background-gray:#526069;--color-background-disabled:#273139;--color-background-highlight:#3c383c;--color-background-raised:#273139;--color-background-edit:rgba(0, 0, 0, 0.6);--color-background-gray-emp:#8a97a0;--color-background-hover:rgba(207, 216, 221, 0.0784313725);--color-background-pressed:rgba(207, 216, 221, 0.1215686275);--color-background-selected:#374652;--color-selection-indicator:#66adff;--color-notification-badge:#ffffff;--color-notification-indicator:#ff8f70;--color-hover:#374652;--color-curtain:#000000;--color-border:#8a97a0;--color-border-disabled:#526069;--color-border-grid:#273139;--color-border-de-emp:#526069;--color-selection:#ff8f70;--color-logo:#ffffff;--color-foreground:#f4f5f7;--color-foreground-light:#a4b1b8;--color-foreground-emp:#f8f9fa;--color-foreground-de-emp:#cfd8dd;--color-foreground-disable:#a4b1b8;--color-foreground-inverse:#182027;--color-foreground-highlight:#ff8f70;--color-foreground-highlight-selected:rgba(255, 143, 112, 0.1019607843);--color-foreground-highlight-selected-hover:rgba(255, 143, 112, 0.1490196078);--color-foreground-link:#66adff;--color-foreground-link-pressed:#badaff;--color-foreground-inv-de-emp:#526069;--color-primary:#66adff;--color-primary-lighter:#00489d;--color-primary-darker:#badaff;--color-primary-hover:#87bfff;--color-primary-focused:#87bfff;--color-primary-pressed:#66adff;--color-secondary-focused:#374652;--color-secondary-pressed:#11508d;--color-chip-default-background:#526069;--color-chip-error-background:#000000;--color-chip-warning-background:#000000;--color-chip-info-background:#000000;--color-chip-success-background:#000000;--color-error-icon:#ff8484;--color-error-icon-inverse:#cc3d45;--color-error-text:#ff8484;--color-error-background:#000000;--color-error-foreground-inverse:#cc3d45;--color-info-foreground:#42a1ff;--color-info-icon:#42a1ff;--color-info-icon-inverse:#1976d2;--color-info-text:#42a1ff;--color-info-foreground-inverse:#1976d2;--color-info-background:#000000;--color-success-icon:#74c94b;--color-success-icon-inverse:#038108;--color-success-text:#74c94b;--color-success-background:#000000;--color-success-foreground-inverse:#6eb84a;--color-warning-icon:#ffbb27;--color-warning-icon-inverse:#ffb40e;--color-warning-text:#ffe19e;--color-warning-background:#000000;--color-warning-foreground-inverse:#ffb40e;--color-icon-default:#cfd8dd;--color-icon-inverted-default:#526069;--color-toggle-off-hover:rgba(207, 216, 221, 0.0784313725);--color-toggle-off-focus:rgba(207, 216, 221, 0.0784313725);--color-toggle-off-pressed:rgba(207, 216, 221, 0.1215686275);--color-toggle-on-hover:rgba(102, 173, 255, 0.0784313725);--color-toggle-on-focus:rgba(102, 173, 255, 0.0784313725);--color-toggle-on-pressed:rgba(102, 173, 255, 0.1215686275);--color-callout-background:#00489d;--color-warn:#ff8484;--color-warn-darker:#ffadad;--color-warn-hover:rgba(255, 132, 132, 0.0392156863);--color-warn-focus:rgba(255, 132, 132, 0.1215686275);--color-warn-pressed:rgba(255, 132, 132, 0.0784313725);--color-carousel-background:#1b2630;--color-icon-button-hover:rgba(207, 216, 221, 0.0784313725);--color-icon-button-focus:rgba(207, 216, 221, 0.0784313725);--color-icon-button-pressed:rgba(207, 216, 221, 0.1215686275);--color-code-function:#dc80db;--color-code-operator:#66adff;--color-code-numeric:#6ecdb6;--color-code-text:#f25a8c;--color-toggle-thumb-off:#cfd8dd;--color-toggle-track-off:#526069;--color-toggle-thumb-on:#66adff;--color-toggle-track-on:#00489d;--color-toggle-thumb-off-disabled:#526069;--color-toggle-track-off-disabled:#374652;--color-toggle-thumb-on-disabled:rgba(102, 173, 255, 0.2784313725);--color-toggle-track-on-disabled:rgba(0, 72, 157, 0.3803921569);--color-skeleton:#273139;--color-skeleton-glow:#525a61;--color-data-grid-hover:#272f36;--color-data-grid-pressed:#2e363d;--color-focus-indicator:#66adff;--color-focus-indicator-inverse:#0067df;--color-chart-purple:#b748b6;--color-chart-pink:#f25a8c;--color-chart-blue-secondary:#42a1ff;--color-chart-light-blue:#44cffc;--color-chart-green:#74c94b;--color-chart-yellow:#ffbb27;--border:1px solid #8a97a0;color-scheme:dark}body.dark-hc{--color-background:#182027;--color-background-secondary:#273139;--color-background-inverse:#f8f9fa;--color-background-gray:#374652;--color-background-disabled:#273139;--color-background-highlight:#3c383c;--color-background-raised:#273139;--color-background-edit:rgba(0, 0, 0, 0.6);--color-background-gray-emp:#a4b1b8;--color-background-hover:rgba(164, 177, 184, 0.0784313725);--color-background-pressed:rgba(207, 216, 221, 0.1215686275);--color-background-selected:#374652;--color-selection-indicator:#dae8fa;--color-notification-badge:#ffffff;--color-notification-indicator:#ff8f70;--color-hover:#273139;--color-curtain:#000000;--color-border:#bbc7cd;--color-border-disabled:#8a97a0;--color-border-grid:#273139;--color-border-de-emp:#a4b1b8;--color-selection:#fda891;--color-logo:#ffffff;--color-foreground:#f4f5f7;--color-foreground-light:#bbc7cd;--color-foreground-emp:#f8f9fa;--color-foreground-de-emp:#e9f0f3;--color-foreground-disable:#a4b1b8;--color-foreground-inverse:#182027;--color-foreground-highlight:#ff8f70;--color-foreground-highlight-selected:rgba(255, 143, 112, 0.1019607843);--color-foreground-highlight-selected-hover:rgba(255, 143, 112, 0.1490196078);--color-foreground-link:#66adff;--color-foreground-link-pressed:#badaff;--color-foreground-inv-de-emp:#374652;--color-primary:#badaff;--color-primary-lighter:#00336f;--color-primary-darker:#badaff;--color-primary-hover:#87bfff;--color-primary-focused:#87bfff;--color-primary-pressed:#66adff;--color-secondary-focused:#273139;--color-secondary-pressed:#11508d;--color-chip-default-background:#526069;--color-chip-error-background:#000000;--color-chip-warning-background:#000000;--color-chip-info-background:#000000;--color-chip-success-background:#000000;--color-error-icon:#ffadad;--color-error-icon-inverse:#b6242b;--color-error-text:#ffadad;--color-error-background:#000000;--color-error-foreground-inverse:#b6242b;--color-info-foreground:#42a1ff;--color-info-icon:#8cc2f8;--color-info-icon-inverse:#0056ba;--color-info-text:#42a1ff;--color-info-foreground-inverse:#8cc2f8;--color-info-background:#000000;--color-success-icon:#74c94b;--color-success-icon-inverse:#6eb84a;--color-success-text:#74c94b;--color-success-background:#000000;--color-success-foreground-inverse:#6eb84a;--color-warning-icon:#ffbb27;--color-warning-icon-inverse:#ffb40e;--color-warning-text:#ffe19e;--color-warning-background:#000000;--color-warning-foreground-inverse:#ffb40e;--color-icon-default:#e9f0f3;--color-icon-inverted-default:#374652;--color-toggle-off-hover:rgba(207, 216, 221, 0.0784313725);--color-toggle-off-focus:rgba(207, 216, 221, 0.0784313725);--color-toggle-off-pressed:rgba(207, 216, 221, 0.1215686275);--color-toggle-on-hover:rgba(102, 173, 255, 0.0784313725);--color-toggle-on-focus:rgba(102, 173, 255, 0.0784313725);--color-toggle-on-pressed:rgba(102, 173, 255, 0.1215686275);--color-callout-background:#00489d;--color-warn:#ffadad;--color-warn-darker:#ffadad;--color-warn-hover:rgba(255, 132, 132, 0.0392156863);--color-warn-focus:rgba(255, 132, 132, 0.1215686275);--color-warn-pressed:rgba(255, 132, 132, 0.0784313725);--color-carousel-background:#1b2630;--color-icon-button-hover:rgba(207, 216, 221, 0.0784313725);--color-icon-button-focus:rgba(207, 216, 221, 0.0784313725);--color-icon-button-pressed:rgba(207, 216, 221, 0.1215686275);--color-code-function:#dc80db;--color-code-operator:#66adff;--color-code-numeric:#6ecdb6;--color-code-text:#fd7da7;--color-toggle-thumb-off:#cfd8dd;--color-toggle-track-off:#526069;--color-toggle-thumb-on:#66adff;--color-toggle-track-on:#00489d;--color-toggle-thumb-off-disabled:#526069;--color-toggle-track-off-disabled:#374652;--color-toggle-thumb-on-disabled:rgba(102, 173, 255, 0.2784313725);--color-toggle-track-on-disabled:rgba(0, 72, 157, 0.3803921569);--color-skeleton:#273139;--color-skeleton-glow:#525a61;--color-data-grid-hover:#272f36;--color-data-grid-pressed:#2e363d;--color-focus-indicator:#b2d2f3;--color-focus-indicator-inverse:#0067df;--color-chart-purple:#dc80db;--color-chart-pink:#fd7da7;--color-chart-blue-secondary:#8cc2f8;--color-chart-light-blue:#44cffc;--color-chart-green:#74c94b;--color-chart-yellow:#ffbb27;--border:1px solid #bbc7cd;color-scheme:dark-hc}._pendo-guide-container_{background-color:var(--color-background-raised) !important;border-color:var(--color-border) !important;color:var(--color-foreground) !important;border-radius:3px !important}._pendo-guide-container_ ._pendo-guide-content_{color:var(--color-foreground) !important}._pendo-backdrop_{background-color:var(--color-curtain) !important;opacity:0.5 !important}._pendo-guide-arrow-top_{border-bottom-color:var(--color-background-raised) !important}._pendo-guide-arrow-left_{border-right-color:var(--color-background-raised) !important}._pendo-guide-arrow-right_{border-left-color:var(--color-background-raised) !important}._pendo-guide-arrow-bottom_{border-top-color:var(--color-background-raised) !important}._pendo-guide-arrow-border-top_{border-bottom-color:var(--color-background-raised) !important}._pendo-guide-arrow-border-left_{border-right-color:var(--color-background-raised) !important}._pendo-guide-arrow-border-right_{border-left-color:var(--color-background-raised) !important}._pendo-guide-arrow-border-bottom_{border-top-color:var(--color-background-raised) !important}._pendo-launcher_ ._pendo-launcher-header_{border-bottom-color:var(--color-border) !important}._pendo-launcher_ ._pendo-launcher-header_ ._pendo-launcher-title_{font-family:inherit;color:var(--color-foreground-de-emp) !important}._pendo-launcher_ ._pendo-launcher-header_ ._pendo-launcher-search-box_{border-color:var(--color-border) !important;background-color:var(--color-background-gray-emp) !important}._pendo-launcher_ ._pendo-launcher-footer_{color:var(--color-foreground-de-emp) !important}._pendo-guide-container_ ._pendo-poll-question_ ._pendo-poll-npsrating-choices_ label{color:var(--color-foreground-de-emp) !important;border-color:var(--color-border) !important}._pendo-guide-container_ ._pendo-poll-question_ ._pendo-poll-npsrating-choices_ label span{color:var(--color-foreground-de-emp) !important}._pendo-guide-container_ ._pendo-poll-npsreason_ textarea{border-color:var(--color-border) !important;width:100% !important}._pendo-guide-container_ ._pendo-poll-submit_{background-color:var(--color-primary) !important;color:var(--color-foreground-inverse) !important;font-family:inherit !important;border-color:var(--color-primary) !important;border-width:1px !important;border-style:solid !important;border-radius:3px !important}._pendo-poll_ ._pendo-poll-freeform_ textarea{color:var(--color-foreground) !important}._pendo-poll_ ._pendo-poll-npsrating_ ._pendo-poll-npsrating-choices_ label{color:var(--color-foreground-light) !important}._pendo-poll_ ._pendo-poll-npsrating_ ._pendo-poll-npsrating-choices_ label span{color:var(--color-foreground-light) !important}._pendo-poll_ ._pendo-poll-npsrating_ ._pendo-poll-npsrating-choices_ label:hover{border-color:var(--color-border) !important;background-color:var(--color-background-gray-emp) !important;color:var(--color-foreground-inverse) !important}._pendo-poll_ ._pendo-poll-npsrating_ ._pendo-poll-npsrating-choices_ ._pendo-poll-selected_{border-color:var(--color-foreground-de-emp) !important;background-color:var(--color-foreground-de-emp) !important;color:var(--color-foreground-inverse) !important}._pendo-poll_ ._pendo-poll-npsreason_{border-top-color:var(--color-border) !important}._pendo-poll_ ._pendo-poll-npsreason_ textarea{border-color:var(--color-border) !important}._pendo_row{background-color:transparent !important}.bb-text{color:var(--color-foreground) !important}._pendo-close-guide{color:var(--color-foreground-emp) !important;font-family:inherit !important;font-size:24px !important;height:24px !important;width:24px !important;border-radius:50% !important;top:10px !important;right:10px !important}._pendo-backdrop,.pendo-backdrop-region-right,.pendo-backdrop-region-top,.pendo-backdrop-region-bottom,.pendo-backdrop-region-left{background-color:var(--color-curtain) !important;opacity:0.5 !important}._pendo-close-guide:hover{background:var(--color-background-secondary) !important}._pendo-guide-container_{background-color:var(--color-background-raised) !important}._pendo-button{cursor:pointer !important}._pendo-button:disabled{background-color:var(--color-background-disabled) !important;color:var(--color-foreground-disable) !important}._pendo-button-primaryButton{background-color:var(--color-primary) !important;color:var(--color-foreground-inverse) !important;font-family:inherit !important;border-color:var(--color-primary) !important;border-width:1px !important;border-style:solid !important;min-width:112px !important;height:40px !important;margin-left:8px !important;margin-right:0px !important;margin-bottom:0px !important;font-weight:600 !important}._pendo-button-primaryButton:hover{background-color:var(--color-primary-hover) !important}._pendo-button-primaryButton:focus{background-color:var(--color-primary-focused) !important}._pendo-button-secondaryButton{background-color:var(--color-background-raised) !important;color:var(--color-primary) !important;font-family:inherit !important;border-color:var(--color-border) !important;border-width:1px !important;border-style:solid !important;min-width:112px !important;margin-left:8px !important;margin-right:0px !important;margin-bottom:0px !important;height:40px !important}._pendo-button-secondaryButton:hover,._pendo-button-tertiaryButton:hover{background-color:var(--color-hover) !important}._pendo-button-tertiaryButton{color:var(--color-primary) !important;border-color:var(--color-background-raised) !important;border-width:0px !important;font-family:inherit !important;min-width:112px !important;margin-left:8px !important;margin-right:0px !important;margin-bottom:0px !important;height:40px !important}._pendo-open-text-poll-input{background-color:var(--color-background) !important;color:inherit !important;border-radius:3px !important;font-family:inherit !important;width:100% !important;margin:0 !important}._pendo-nps-poll-wrapper input[type=radio]+label,._pendo-number-scale-poll-wrapper input[type=radio]+label{color:var(--color-foreground-de-emp) !important;border-color:var(--color-border) !important;border-width:1px !important;border-style:solid !important;font-family:inherit !important;font-weight:600 !important;height:32px !important;width:32px !important;line-height:32px !important;font-size:14px !important}._pendo-nps-poll-wrapper input[type=radio]+label{margin-right:8px !important}._pendo-number-scale-poll-wrapper input[type=radio]+label{margin-right:12px !important;margin-left:0px !important}._pendo-number-scale-poll-wrapper label+div{width:0px !important;font-weight:600 !important;margin-top:6px !important;font-size:12px !important}._pendo-number-scale-poll-wrapper div{color:var(--color-foreground-disable) !important}._pendo-open-text-poll-wrapper,._pendo-number-scale-poll-wrapper{width:430px !important}._pendo-nps-poll-wrapper input[type=radio]:not(:checked)+label,._pendo-number-scale-poll-wrapper input[type=radio]:not(:checked)+label{background-color:var(--color-background-raised) !important}label.pendo-radio:hover,._pendo-nps-poll-wrapper input[type=radio]:hover+label,._pendo-number-scale-poll-wrapper input[type=radio]:hover+label{background-color:var(--color-hover) !important}._pendo-nps-poll-wrapper input[type=radio]:checked+label,._pendo-number-scale-poll-wrapper input[type=radio]:checked+label{background-color:var(--color-primary) !important;color:var(--color-foreground-inverse) !important}._pendo-nps-poll-wrapper div[role=radiogroup],._pendo-number-scale-poll-wrapper div[role=radiogroup]{display:flex !important;flex-direction:row !important;justify-content:flex-start !important;align-items:flex-start !important}._pendo-nps-poll-wrapper div[role=radiogroup] span:last-of-type label,._pendo-number-scale-poll-wrapper div[role=radiogroup] span:last-of-type label{margin-right:0 !important}._pendo-nps-poll-wrapper div[role=radiogroup] span:last-of-type div,._pendo-number-scale-poll-wrapper div[role=radiogroup] span:last-of-type div{direction:rtl !important;white-space:nowrap !important;width:0px !important;margin-left:32px !important}._pendo-nps-poll-wrapper div[role=radiogroup] span:first-of-type div,._pendo-number-scale-poll-wrapper div[role=radiogroup] span:first-of-type div{margin-left:0 !important;white-space:nowrap !important;width:0px !important}._pendo-nps-poll-wrapper ._pendo-text-plain,._pendo-nps-poll-wrapper ._pendo-nps-poll-upperbound-label,._pendo-nps-poll-wrapper ._pendo-nps-poll-lowerbound-label{color:var(--color-foreground-de-emp) !important;font-weight:600 !important;font-style:normal !important;margin:40px 0px 0px 0px !important;overflow-wrap:normal !important;white-space:nowrap !important;width:0 !important;font-size:12px !important}._pendo-nps-poll-wrapper ._pendo-text-plain,._pendo-nps-poll-wrapper ._pendo-nps-poll-upperbound-label{margin:40px -8px 0px 0px !important;direction:rtl !important}._pendo-nps-open-text-poll-wrapper{display:block !important}._pendo-text-custom{margin-left:0px !important}._pendo-step-container-styles{box-shadow:0px 5px 10px rgba(0, 0, 0, 0.2), 0px 1px 5px rgba(0, 0, 0, 0.12), 0px 8px 10px rgba(0, 0, 0, 0.14) !important;border-color:var(--color-background-raised) !important;color:var(--color-foreground) !important;padding:24px !important;padding-top:20px !important;background-color:var(--color-background-raised) !important;border-radius:3px !important}._pendo-row[data-pendo-display-flex=true]{display:flex !important;margin:0px !important}._pendo-row[data-pendo-display-flex=true][data-pendo-justify-content=center] .pendo-mock-flexbox-row,._pendo-row[data-pendo-display-flex=true][data-pendo-justify-content=flex-end] .pendo-mock-flexbox-row{justify-content:flex-end !important}._pendo-row[data-pendo-display-flex=true][data-pendo-justify-content=flex-start] .pendo-mock-flexbox-row{justify-content:flex-start !important}.pendo-mock-flexbox-row{display:flex !important;flex-direction:row !important;align-items:flex-end !important;justify-content:space-between !important}.pendo-mock-flexbox-element{display:flex !important;position:relative !important;left:0px !important}._pendo-step-container-styles[data-caret-enabled=true]{background-color:#00489d !important;color:#ffffff !important;border-color:#00489d !important}._pendo-step-container-styles[data-caret-enabled=true] .bb-text{color:#ffffff !important;font-size:inherit !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-nps-poll-success-message,._pendo-step-container-styles[data-caret-enabled=true] ._pendo-text-title,._pendo-step-container-styles[data-caret-enabled=true] ._pendo-text-subTitle{font-size:20px !important;line-height:24px !important;font-weight:600 !important;margin-bottom:6px !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-text-paragraph{line-height:21px !important;margin-right:6px !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-close-guide{color:#ffffff !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-close-guide:hover,._pendo-step-container-styles[data-caret-enabled=true] ._pendo-close-guide:focus{background-color:#273139 !important;cursor:pointer !important}._pendo-step-container-styles[data-caret-enabled=true] .pendo-mock-flexbox-element{display:flex !important;position:relative !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button{font-weight:600 !important;margin-top:0px !important;margin-bottom:0px !important;cursor:pointer !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-primaryButton{background-color:#ffffff !important;color:#0067df !important;width:100px !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-primaryButton:hover,._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-primaryButton:focus{background-color:#00336f !important;color:#ffffff !important;font-weight:600 !important;border-color:#00336f !important;cursor:pointer !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-secondaryButton{background-color:transparent !important;color:#ffffff !important;width:100px !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-secondaryButton:hover,._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-secondaryButton:focus{background-color:#ffffff !important;color:#0067df !important;font-weight:600 !important;border-color:#ffffff !important;cursor:pointer !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-tertiaryButton{background-color:transparent !important;color:#ffffff !important;width:100px !important}._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-tertiaryButton:hover,._pendo-step-container-styles[data-caret-enabled=true] ._pendo-button-tertiaryButton:focus{background-color:#00336f !important;color:#87bfff !important;font-weight:600 !important;border-color:#ffffff !important;cursor:pointer !important}._pendo-step-container-styles[data-caret-enabled=true]+.pendo-tooltip-caret[style*=\"); top:\"],._pendo-step-container-styles[data-caret-enabled=true]~.pendo-tooltip-caret-border[style*=\"); top:\"]{border-bottom-color:#00489d !important}._pendo-step-container-styles[data-caret-enabled=true]+.pendo-tooltip-caret[style*=\"); right:\"],._pendo-step-container-styles[data-caret-enabled=true]~.pendo-tooltip-caret-border[style*=\"); right:\"]{border-left-color:#00489d !important}._pendo-step-container-styles[data-caret-enabled=true]+.pendo-tooltip-caret[style*=\"); bottom:\"],._pendo-step-container-styles[data-caret-enabled=true]~.pendo-tooltip-caret-border[style*=\"); bottom:\"]{border-top-color:#00489d !important}._pendo-step-container-styles[data-caret-enabled=true]+.pendo-tooltip-caret[style*=\"); left\"],._pendo-step-container-styles[data-caret-enabled=true]~.pendo-tooltip-caret-border[style*=\"); left:\"]{border-right-color:#00489d !important}._pendo-step-container-size[data-vertical-alignment=\"Bottom Aligned\"]{height:auto !important}._pendo-step-container-size[data-vertical-alignment=\"Bottom Aligned\"] ._pendo-step-container-styles{background:var(--color-background-raised) !important;box-shadow:0px -5px 10px rgba(0, 0, 0, 0.2), 0px 1px 5px rgba(0, 0, 0, 0.12), 0px 8px 10px rgba(0, 0, 0, 0.14) !important}div[data-layout=bannerNpsSurvey]>._pendo-step-container-size,div[data-layout=bannerSuccessMessage]>._pendo-step-container-size{width:480px !important;margin-right:auto !important}div[data-layout=bannerNpsSurvey]>._pendo-step-container-size ._pendo-text-paragraph,div[data-layout=bannerSuccessMessage]>._pendo-step-container-size ._pendo-text-paragraph{margin:0px !important}div[data-layout=bannerNpsSurvey]>._pendo-step-container-size .pendo-block-controls,div[data-layout=bannerSuccessMessage]>._pendo-step-container-size .pendo-block-controls{margin-right:0px !important}._pendo-nps-open-text-poll-question{color:var(--color-foreground) !important;margin:24px 0px 8px 0px !important;font-size:14px !important;font-weight:600 !important}._pendo-nps-poll-success-message,._pendo-nps-poll-question{margin-left:0 !important;margin-bottom:16px !important;font-size:20px !important;line-height:24px !important;font-weight:600 !important}._pendo-text-title,._pendo-text-plain,._pendo-simple-text,._pendo-text-subTitle,._pendo-text-paragraph,._pendo-nps-poll-question,[id^=pendo-radio-select-],.pendo-radio,._pendo-text-paragraph._pendo-open-text-poll-question._pendo-nps-open-text-poll-question{color:inherit !important}._pendo-text-link{color:var(--color-primary) !important}._pendo-text-link:hover{text-decoration:underline !important;color:var(--color-primary-hover) !important}[id^=pendo-radio-select-] span{display:flex !important;align-items:center}[id^=pendo-radio-select-] .pendo-radio{color:var(--color-foreground) !important}[id^=pendo-radio-select-] input[type=radio]{background-color:var(--color-background-raised) !important;border:0.15em solid var(--color-foreground-de-emp) !important}[id^=pendo-radio-select-] input[type=radio]::before{content:\"\" !important;width:0.6em !important;height:0.6em !important;border-radius:50% !important;transform:scale(0) !important;transition:120ms transform ease-in-out !important;box-shadow:inset 1em 1em var(--color-primary) !important;background-color:var(--color-primary) !important}[id^=pendo-radio-select-] input[type=radio]:checked::before{transform:scale(1)}select._pendo-multi-choice-poll-select{color:inherit !important;background-color:transparent !important;border:none !important;padding:0 10px 0 0.5em !important;margin:0 !important;width:100% !important;font-family:inherit !important;line-height:inherit !important;border-radius:3px !important}._pendo-multi-choice-poll-select-border{background-color:inherit !important}._pendo-multi-choice-poll-select-border[data-_pendo-multi-choice-poll-select-border-1]{border:none !important}._pendo-multi-choice-poll-select-border[data-_pendo-multi-choice-poll-select-border-1]:hover{border-color:var(--color-foreground-de-emp) !important}._pendo-multi-choice-poll-select-border option._pendo-option{background-color:var(--color-background-raised) !important}._pendo_badge_{background-color:var(--color-background) !important}._pendo-badge-text{color:var(--color-primary) !important;font-size:14px !important;font-weight:600 !important}:focus,:focus-visible{outline-color:var(--color-focus-indicator) !important}button:focus,button:focus-visible,li:focus,li:focus-visible,a[role=tab]:focus,a[role=tab]:focus-visible,a[role=menuitem]:focus,a[role=menuitem]:focus-visible{outline-width:2px;outline-offset:-2px}";
const globalStyles = globalStyleGeneratedCss;

class Accessibility {
    constructor(axeStore) {
        this.accessibilityStore = axeStore;
        this.accessibilityStore.set('isAccessibleMode', !!Accessibility.getCachedAccessibilitySettings());
        handleAccessibilityChange((isAccessibleMode) => {
            this.setIsAccessibleModeNoPut(isAccessibleMode);
        });
    }
    get isAccessibleMode() {
        return this.accessibilityStore.get('isAccessibleMode');
    }
    set isAccessibleMode(value) {
        var _a, _b;
        this.setIsAccessibleModeNoPut(value);
        const organizationStandardUrl = getOrganizationStandardUrl();
        const token = getAccessToken();
        if (!organizationStandardUrl || !token) {
            return;
        }
        Logger.debug('accessibility', `put: ${value}`, { object: new Error('accessibility').stack });
        setUserAccessibility(value, organizationStandardUrl, token, (_a = sessionStore.get('organizationId')) !== null && _a !== void 0 ? _a : '', (_b = sessionStore.get('userId')) !== null && _b !== void 0 ? _b : '');
    }
    async fetchAccessibilitySettings() {
        Logger.debug('accessibility', 'fetch');
        const cachedAccessibilitySettings = Accessibility.getCachedAccessibilitySettings();
        const { refetchCondition, setLocalStorageLastRefetched, } = shouldRefetch(Features.AccessibilityRefetchThrottleMs.name, `${Accessibility.accessibilityModeLastRefetchedPrefix}_${getAccountName()}`);
        const organizationStandardUrl = getOrganizationStandardUrl();
        const token = getAccessToken();
        let accessibilitySetting = cachedAccessibilitySettings;
        if (refetchCondition && organizationStandardUrl && token) {
            const settings = await SettingsInstanceResolver.Instance.getSettings([ApolloSettingKey.UserAccessibility]);
            accessibilitySetting = settings[ApolloSettingKey.UserAccessibility] === 'true';
            setLocalStorageLastRefetched();
        }
        this.setIsAccessibleModeNoPut(!!accessibilitySetting);
    }
    static getCachedAccessibilitySettings() {
        const cached = localStorage.getItem(`${Accessibility.accessibilityModeCacheKey}_${getAccountName()}`);
        return cached ? cached === 'true' : null;
    }
    static setCachedAccessibilitySettings(value) {
        localStorage.setItem(`${Accessibility.accessibilityModeCacheKey}_${getAccountName()}`, value.toString());
    }
    setIsAccessibleModeNoPut(value) {
        Logger.debug('accessibility', `set: ${value}`);
        this.accessibilityStore.set('isAccessibleMode', value);
        Accessibility.setCachedAccessibilitySettings(value);
        GlobalEventEmitter.emitAccessiblityModeChanged(value);
    }
}
Accessibility.accessibilityModeCacheKey = STORAGE_KEYS.PORTAL_SHELL_ACCESSIBILITY_MODE;
Accessibility.accessibilityModeLastRefetchedPrefix = STORAGE_KEYS.PORTAL_SHELL_ACCESSIBILITY_MODE_LAST_REFETCHED;
const accessibilityStore = createStore({ isAccessibleMode: false });

class Alert {
    constructor(alertStore) {
        this.activeToast = [];
        this.alertStore = alertStore;
        this.alertStore.set('alertDuration', Alert.getCachedAlertDuration());
        handleAlertDurationChange((alertDuration) => {
            this.setAlertDurationNoPut(alertDuration);
        });
        document.addEventListener('keydown', (event) => {
            var _a;
            if (event.key === 'Escape' && this.activeToast.length > 0) {
                const oldestToast = this.activeToast[0];
                (_a = AlertInstanceResolver.Instance) === null || _a === void 0 ? void 0 : _a.dismissToast(oldestToast);
            }
        });
    }
    get alertDuration() {
        var _a;
        const storedDuration = this.alertStore.get('alertDuration');
        const isAccessibleMode = getFeatureFlag(Features.EnableAccessibilityApi.name)
            ? (_a = AccessibilityInstanceResolver.Instance) === null || _a === void 0 ? void 0 : _a.isAccessibleMode
            : false;
        const isAlertDurationEnabled = getFeatureFlag(Features.EnableAlertDuration.name);
        const stayOpen = isAlertDurationEnabled
            // If duration is set it should override accessible mode
            ? (!storedDuration && isAccessibleMode) || (storedDuration === AlertDurationEnum.NODISMISS)
            : isAccessibleMode;
        if (stayOpen) {
            return null;
        }
        return isAlertDurationEnabled && storedDuration ? storedDuration : AlertDurationDefault;
    }
    set alertDuration(duration) {
        var _a, _b;
        this.setAlertDurationNoPut(duration);
        const organizationStandardUrl = getOrganizationStandardUrl();
        const token = getAccessToken();
        if (!organizationStandardUrl || !token) {
            return;
        }
        Logger.debug('alertDuration', `put: ${duration}`, { object: new Error('alertDuration').stack });
        setUserAlertDuration(duration, organizationStandardUrl, token, (_a = sessionStore.get('organizationId')) !== null && _a !== void 0 ? _a : '', (_b = sessionStore.get('userId')) !== null && _b !== void 0 ? _b : '');
    }
    async fetchAlertDurationSettings() {
        var _a;
        Logger.debug('alertDuration', 'fetch');
        const cachedAlertDuration = Alert.getCachedAlertDuration();
        const { refetchCondition, setLocalStorageLastRefetched, } = shouldRefetch(Features.AlertDurationRefetchThrottleMs.name, `${Alert.alertDurationLastRefetchedPrefix}_${getAccountName()}`);
        const organizationStandardUrl = getOrganizationStandardUrl();
        const token = getAccessToken();
        let alertDurationSettings = cachedAlertDuration;
        if (refetchCondition && organizationStandardUrl && token) {
            const settings = await SettingsInstanceResolver.Instance.getSettings([ApolloSettingKey.UserAlert]);
            try {
                alertDurationSettings = JSON.parse((_a = settings[ApolloSettingKey.UserAlert]) !== null && _a !== void 0 ? _a : 'null');
            }
            catch (error) {
                Logger.error('alertDuration', 'error parsing alert duration settings', { object: error });
                alertDurationSettings = null;
            }
            setLocalStorageLastRefetched();
        }
        if (!alertDurationSettings) {
            this.alertStore.set('alertDuration', cachedAlertDuration);
        }
        else {
            this.setAlertDurationNoPut(alertDurationSettings);
        }
    }
    static getCachedAlertDuration() {
        const cached = localStorage.getItem(`${Alert.alertDurationCacheKey}_${getAccountName()}`);
        if (cached) {
            return JSON.parse(cached);
        }
        return null;
    }
    /**
     * Function to enqueue a toast and return the id of the toast
     * @param message Toast message
     * @param status Status of toast 'success' | 'error' | 'warning' | 'info'
     * @returns SnackbarKey: id of the toast
     */
    enqueueToast(message, status, dataCy, politeness) {
        const snackbarId = enqueueSnackbar(message, {
            variant: status !== null && status !== void 0 ? status : StatusTypes.INFO,
            autoHideDuration: this.alertDuration,
            politeness: politeness !== null && politeness !== void 0 ? politeness : 'polite',
            onClose: (_event, _reason, id) => {
                if (id) {
                    this.stopTrackingToast(id);
                }
            },
            dataCy: dataCy !== null && dataCy !== void 0 ? dataCy : '',
        });
        this.activeToast.push(snackbarId);
        return snackbarId;
    }
    /**
     * Dismiss a toast by id
     * @param id SnackbarKey: id of the toast to dismiss
     */
    dismissToast(id) {
        this.stopTrackingToast(id);
        closeSnackbar(id);
    }
    static setCacheAlertDuration(duration) {
        localStorage.setItem(`${Alert.alertDurationCacheKey}_${getAccountName()}`, JSON.stringify(duration));
    }
    setAlertDurationNoPut(duration) {
        Logger.debug('alertDuration', `set: ${duration}`);
        this.alertStore.set('alertDuration', duration);
        Alert.setCacheAlertDuration(duration);
        const alertDurationEvent = { alertDuration: duration };
        GlobalEventEmitter.emitAlertDurationChanged(alertDurationEvent);
    }
    stopTrackingToast(id) {
        const index = this.activeToast.indexOf(id);
        if (index > -1) {
            this.activeToast.splice(index, 1);
        }
    }
}
Alert.alertDurationCacheKey = STORAGE_KEYS.PORTAL_SHELL_ALERT_DURATION;
Alert.alertDurationLastRefetchedPrefix = STORAGE_KEYS.PORTAL_SHELL_ALERT_DURATION_LAST_REFETCHED;
const alertStore = createStore({ alertDuration: null });

var PlatformActivationType;
(function (PlatformActivationType) {
    PlatformActivationType["AutomationCloud"] = "AutomationCloud";
    PlatformActivationType["AutomationSuite"] = "AutomationSuite";
})(PlatformActivationType || (PlatformActivationType = {}));

function addGlobalUnHandledExceptionCatcher(instance) {
    Logger.debug('appInsights', 'initializing globalUnhandledExceptionCatcher');
    window.addEventListener('error', (event) => {
        var _a;
        // Get the error properties from the error event object
        const { message, filename, lineno, colno, error, } = event;
        // If the error is not from an apollo package, we let it bubble
        if (!(filename === null || filename === void 0 ? void 0 : filename.includes('/apollo-packages/'))) {
            return;
        }
        (_a = instance.trackerMethods) === null || _a === void 0 ? void 0 : _a.trackError(new Error('[globalUnhandledExceptionCatcher] - ' + message), SeverityLevel.Critical, {
            component: 'globalUnhandledExceptionCatcher',
            object: {
                error,
                filename,
                lineno,
                colno,
            },
            stack: error === null || error === void 0 ? void 0 : error.stack,
            accountName: getAccountName(),
            tenantName: getTenantName(),
        });
        event.stopPropagation();
    });
}

class AppInsightsClient {
    constructor(opts) {
        this._platformInfo = {};
        this._metadata = {};
        const config = this._getAppInsightsConfig(opts);
        if (!config) {
            return;
        }
        Logger.debug('AppInsights', `Setting up driver and tracker`);
        try {
            this._tracker = new Tracker(this._deploymentType, WellKnownApplication.PortalShell, AppInsightDriverBuilder.withConfig(config).build());
        }
        catch (exception) {
            Logger.error('AppInsights', 'Error initializing AppInsights, exception: ' + JSON.stringify(exception));
            return;
        }
    }
    get trackerMethods() {
        return this._tracker ? {
            trackEvent: this._tracker.trackEvent.bind(this._tracker),
            trackNavigation: this._tracker.trackNavigation.bind(this._tracker),
            trackLogin: this._tracker.trackLogin.bind(this._tracker),
            trackApplicationStart: this._tracker.trackApplicationStart.bind(this._tracker),
            trackError: this._tracker.trackError.bind(this._tracker),
            trackTrace: this._tracker.trackTrace.bind(this._tracker),
        } : undefined;
    }
    setPlatformInfo(platformInfo) {
        var _a;
        this._platformInfo = Object.assign(Object.assign({}, this._platformInfo), platformInfo);
        Logger.debug('AppInsights', `Setting platform info with new data: ${JSON.stringify(platformInfo)}`);
        (_a = this._tracker) === null || _a === void 0 ? void 0 : _a.setPlatformInfo(this._getPlatformInfoByDeployment());
        GlobalEventEmitter.emitPlatformInfoChanged(this._platformInfo);
    }
    getPlatformInfoField(field) {
        return this._platformInfo[field];
    }
    getPlatformInfo() {
        return this._platformInfo;
    }
    setPlatformMetadata(metadata) {
        var _a;
        this._metadata = Object.assign(Object.assign({}, this._metadata), metadata);
        Logger.debug('AppInsights', `Setting metadata with new data: ${JSON.stringify(metadata)}`);
        (_a = this._tracker) === null || _a === void 0 ? void 0 : _a.setMetadata(this._metadata);
    }
    /**
     * @returns ActivationType for Licensing data
     */
    getPlatformActivationType() {
        switch (this._deploymentType) {
            case DeploymentType.AutomationCloud:
                return PlatformActivationType.AutomationCloud;
            case DeploymentType.AutomationSuite:
                return PlatformActivationType.AutomationSuite;
        }
    }
    initializeTracker(devMode, language) {
        var _a, _b;
        Logger.debug('AppInsights', `Initializing tracker with devMode:${devMode}, language:${language}`);
        (_a = this._tracker) === null || _a === void 0 ? void 0 : _a.initialize({
            isDevelopment: devMode,
            isPageViewTrackingEnabled: true,
            isPageViewPerformanceTrackingEnabled: true,
            pageViewTrackingOptions: {
                name: document.title,
                uri: sanitizeUrl(location.href),
            },
            language,
            initializer: this._telemetryInitializer,
        });
        (_b = this._tracker) === null || _b === void 0 ? void 0 : _b.trackNavigation({ targetRoute: sanitizeUrl(location.href) });
        addGlobalUnHandledExceptionCatcher(this);
    }
    setLanguage(language) {
        var _a;
        Logger.debug('AppInsights', `Changing tracker language to ${language}`);
        (_a = this._tracker) === null || _a === void 0 ? void 0 : _a.setLanguage(language);
    }
    _telemetryInitializer(envelope) {
        var _a, _b;
        if (envelope.tags) {
            envelope.tags['ai.cloud.role'] = 'apollo';
            envelope.tags['apollo.spaceName'] = getSpaceName();
            envelope.tags['apollo.accountName'] = getAccountName();
            envelope.tags['apollo.tenantName'] = getTenantName();
        }
        // PLT-13335: Sanitize query / token parameters
        if ((_b = (_a = envelope.data) === null || _a === void 0 ? void 0 : _a.baseData) === null || _b === void 0 ? void 0 : _b.url) {
            const originalUrl = envelope.data.baseData.url;
            envelope.data.baseData.url = sanitizeUrl(originalUrl);
        }
        return undefined;
    }
    _getPlatformInfoByDeployment() {
        const { productVersion = '', cloudTenantId = '', cloudOrganizationId = '', sessionId = '', cloudUserId = '', clusterId = '', 
        // TODO: add server region when we add it on portal BE (PLT-47839)
        serverRegion = '', subscriptionType = '', isHost, } = this._platformInfo;
        switch (this._deploymentType) {
            case DeploymentType.AutomationCloud:
                return {
                    cloudUserId,
                    cloudOrganizationId,
                    cloudTenantId,
                    sessionId,
                    productVersion,
                    serverRegion,
                    subscriptionType,
                };
            case DeploymentType.AutomationSuite:
                return {
                    cloudUserId,
                    cloudOrganizationId,
                    cloudTenantId,
                    sessionId,
                    isHost,
                    productVersion,
                    clusterId,
                };
        }
    }
    get _deploymentType() {
        const env = getEnvironment();
        return env === 'k8s'
            ? DeploymentType.AutomationSuite
            : DeploymentType.AutomationCloud;
    }
    _getAppInsightsConfig(opts) {
        var _a, _b;
        let appInsightsKey = getEnvConfigField('APPINSIGHTS_KEY')
            || ((_a = window.env) === null || _a === void 0 ? void 0 : _a.APPLICATION_INSIGHTS_INSTRUMENTATION_KEY);
        let appInsightsString = getEnvConfigField('APPINSIGHTS_CONNECTION_STRING')
            || ((_b = window.env) === null || _b === void 0 ? void 0 : _b.APPLICATION_INSIGHTS_CONNECTION_STRING);
        if ((opts === null || opts === void 0 ? void 0 : opts.instrumentationKey) && (opts === null || opts === void 0 ? void 0 : opts.connectionString)) {
            appInsightsKey = opts.instrumentationKey;
            appInsightsString = opts.connectionString;
        }
        const noKeyOrString = !appInsightsKey && !appInsightsString;
        if (noKeyOrString) {
            Logger.error('AppInsights', 'Error initializing AppInsights: key or connection string is missing');
            return;
        }
        return {
            instrumentationKey: appInsightsKey,
            connectionString: appInsightsString,
            accountId: getAccountName(),
            appId: getSpaceName(),
        };
    }
}

class Navigation {
    constructor(navigationStore) {
        this.navigationStore = navigationStore;
    }
    openServicesNav(props) {
        this.navigationStore.set('target', props.target);
        this.navigationStore.set('hideWaffle', props.hideWaffle);
        this.navigationStore.set('isSap', props.isSap);
        GlobalEventEmitter.emitOpenServicesNav();
    }
    closeServicesNav() {
        this.navigationStore.set('isSap', false);
        GlobalEventEmitter.emitCloseServicesNav();
    }
    getHideWaffleButton() {
        return this.navigationStore.get('hideWaffle');
    }
    getTarget() {
        return this.navigationStore.get('target');
    }
    getIsSap() {
        return this.navigationStore.get('isSap');
    }
}
const navigationStore = createStore({
    target: '_self',
    hideWaffle: false,
    isSap: false,
});

class Pendo {
    constructor(pendoApiKey = Pendo.getApiKey()) {
        this.isInitialized = false;
        // We cache the current visitor and account metadata so that we can detect changes and update the pendo instance when necessary
        // Updating too frequently can cause pendo guides to flash
        this.currentVisitorMetadata = {};
        this.currentAccountMetadata = {};
        this.apiKey = pendoApiKey;
        if (this.apiKey) {
            try {
                // @ts-ignore
                if (!window.pendo) {
                    Pendo.mountScript(this.apiKey);
                }
                // @ts-ignore
                this.pendoInstance = window.pendo;
            }
            catch (e) {
                Logger.error('pendo', `Error mounting pendo script and/or styles: ${e}`);
            }
        }
        else {
            Logger.error('pendo', 'Missing api key', { printToConsoleOnly: true });
        }
    }
    initializeFromStore() {
        this.initialize({
            visitor: Pendo.mapStoreToVisitorProps(),
            account: Pendo.mapStoreToAccountProps(),
        });
    }
    initialize(options) {
        if (!this.apiKey || !this.pendoInstance || this.isInitialized) {
            Logger.debug('pendo', `not initializing: key=${this.apiKey} pendo=${this.pendoInstance} initialized=${this.isInitialized}`);
            return;
        }
        try {
            const visitor = this.getVisitorOptions(options.visitor);
            const account = this.getAccountOptions(options.account);
            this.currentVisitorMetadata = visitor;
            this.currentAccountMetadata = account;
            Logger.debug('pendo', 'calling Pendo initialize');
            this.pendoInstance.initialize({
                visitor,
                account,
                excludeAllText: true,
                events: { ready: () => Pendo.onReadyCallback(this) },
            });
        }
        catch (exception) {
            Logger.error('pendo', 'Error initializing Pendo, exception: ' + exception, { stack: exception.stack });
        }
    }
    updateOptionsFromStore() {
        if (!this.isInitialized) {
            Logger.debug('pendo', 'not updating options, pendo DNE or not initialized');
            return;
        }
        Logger.debug('pendo', 'update pendo options from store');
        this.updateOptions({
            visitor: Pendo.mapStoreToVisitorProps(),
            account: Pendo.mapStoreToAccountProps(),
        });
    }
    updateOptions(options) {
        if (!this.isInitialized) {
            Logger.debug('pendo', 'not updating options, pendo DNE or not initialized');
            return;
        }
        // Get metadata and check if there are any changes
        let metadata = undefined;
        const visitor = this.getVisitorOptions(options.visitor);
        if (visitor) {
            metadata = { visitor };
            this.currentVisitorMetadata = Object.assign(Object.assign({}, this.currentVisitorMetadata), visitor);
        }
        const account = this.getAccountOptions(options.account);
        if (account) {
            metadata = Object.assign(Object.assign({}, metadata), { account });
            this.currentAccountMetadata = Object.assign(Object.assign({}, this.currentAccountMetadata), account);
        }
        // Only update if there are changes or the guides will flash
        if (metadata) {
            Logger.debug('pendo', 'update pendo options');
            this.pendoInstance.updateOptions(metadata);
        }
        else {
            Logger.debug('pendo', 'skipping; no change in options');
        }
    }
    updateVisitorOptions(visitorOptions) {
        this.updateOptions({
            visitor: visitorOptions,
            account: {},
        });
    }
    updateAccountOptions(accountOptions) {
        this.updateOptions({
            visitor: {},
            account: accountOptions,
        });
    }
    openGuide(guideId) {
        if (!this.isInitialized) {
            Logger.debug('pendo', 'not opening guide, pendo DNE or not initialized');
            return;
        }
        Logger.debug('pendo', `showing guide ${guideId}`);
        this.pendoInstance.showGuideById(guideId);
    }
    getCurrentMetadata() {
        return {
            visitor: this.currentVisitorMetadata,
            account: this.currentAccountMetadata,
        };
    }
    trackEvent(eventName, eventMetadata) {
        if (!this.isInitialized) {
            Logger.debug('pendo', 'not tracking event, pendo DNE or not initialized');
            return;
        }
        Logger.debug('pendo', `tracking event ${eventName}`, { object: eventMetadata });
        this.pendoInstance.track(eventName, eventMetadata);
    }
    static getApiKey() {
        const spaceName = getSpaceName();
        const env = getEnvironment();
        if (env === 'alp' || env === 'stg' || env === 'prd') {
            return PendoApiKey[spaceName] || SHARED_PENDO_API_KEY;
        }
        return '';
    }
    static mountScript(pendoApiKey) {
        /* eslint-disable */
        (function (p, e, n, d, o) {
            // @ts-ignore
            let v, w, x, y, z;
            o = p[d] = p[d] || {};
            o._q = []; //NOSONAR script provided by pendo
            v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
            for (w = 0, x = v.length; w < x; ++w) {
                (function (m) {
                    // @ts-ignore
                    o[m] = o[m] || function () {
                        // @ts-ignore
                        o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0)));
                    };
                })(v[w]);
            }
            y = e.createElement(n);
            y.async = !0;
            y.src = 'https://content.usage.uipath.com/agent/static/' + pendoApiKey + '/pendo.js';
            z = e.getElementsByTagName(n)[0];
            z.parentNode.insertBefore(y, z);
        })(window, document, 'script', 'pendo');
        /* eslint-enable */
    }
    static mapStoreToVisitorProps() {
        var _a, _b, _c;
        const urlTenantName = accountAndTenantsStore.get('currentTenant');
        const activeTenantId = urlTenantName
            ? (_b = (_a = accountAndTenantsStore.state.tenants) === null || _a === void 0 ? void 0 : _a.find(tenant => { var _a; return ((_a = tenant.name) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === urlTenantName.toLowerCase(); })) === null || _b === void 0 ? void 0 : _b.id
            : undefined;
        return {
            userId: sessionStore.get('userId'),
            language: (_c = LocaleInstanceResolver.Instance) === null || _c === void 0 ? void 0 : _c.locale,
            activeTenantId,
            userGroups: accountAndTenantsStore.state.userGroups,
        };
    }
    static mapStoreToAccountProps() {
        var _a, _b, _c, _d, _e, _f, _g;
        const provisionedServices = [...new Set([
                ...((_b = (_a = accountAndTenantsStore === null || accountAndTenantsStore === void 0 ? void 0 : accountAndTenantsStore.state.organization) === null || _a === void 0 ? void 0 : _a.accountServices) !== null && _b !== void 0 ? _b : []),
                ...((_d = (_c = accountAndTenantsStore === null || accountAndTenantsStore === void 0 ? void 0 : accountAndTenantsStore.state.tenants) === null || _c === void 0 ? void 0 : _c.flatMap(tenant => tenant.enabledServices)) !== null && _d !== void 0 ? _d : []),
                'home',
                'help',
            ])];
        return {
            organizationId: sessionStore.get('organizationId'),
            accountType: (_e = accountAndTenantsStore.state.organization) === null || _e === void 0 ? void 0 : _e.accountType,
            provisionedServices,
            userLicensingEnabled: !!((_f = accountAndTenantsStore.state.organization) === null || _f === void 0 ? void 0 : _f.userLicensingEnabled),
            entitlements: (_g = accountAndTenantsStore.state.organization) === null || _g === void 0 ? void 0 : _g.entitlements,
        };
    }
    // The delegate has to be passed in or the instance's isInitialized field doesn't get set properly
    static onReadyCallback(delegate) {
        var _a, _b;
        delegate.isInitialized = true;
        Logger.debug('pendo', 'ready, loading guides');
        delegate.pendoInstance.loadGuides();
        Logger.debug('pendo', `Pendo initialized successfully: ${delegate.isInitialized}`);
        const pendoEvent = { pendoOptions: delegate.getCurrentMetadata() };
        GlobalEventEmitter.emitPendoInitialized(pendoEvent);
        (_b = (_a = EventHandlersInstanceResolver.Instance) === null || _a === void 0 ? void 0 : _a.onPendoInitialized) === null || _b === void 0 ? void 0 : _b.call(_a, pendoEvent);
        Logger.debug(['pendo', 'event'], 'init emitted');
    }
    getVisitorOptions({ userId, language, activeTenantId, userGroups, customOptions, }) {
        Logger.debug('pendo', 'getting Pendo visitor options');
        const visitorOptions = Object.assign(Object.assign({}, customOptions), { id: userId, language: language === null || language === void 0 ? void 0 : language.replace('-', '_').toLowerCase(), language_uipath: language === null || language === void 0 ? void 0 : language.toLowerCase(), groups: userGroups, admin: userGroups === null || userGroups === void 0 ? void 0 : userGroups.includes(BuiltInGroups.Admin), activeTenantId });
        const filtered = removeObjectUndefinedProperties(visitorOptions);
        return this.hasNotUpdated(this.currentVisitorMetadata, filtered) ? undefined : filtered;
    }
    getAccountOptions({ organizationId, accountType, provisionedServices, userLicensingEnabled, customOptions, entitlements, }) {
        Logger.debug('pendo', 'getting Pendo account options');
        const accountOptions = Object.assign(Object.assign({}, customOptions), { id: organizationId, accountType,
            provisionedServices,
            userLicensingEnabled,
            entitlements });
        const filtered = removeObjectUndefinedProperties(accountOptions);
        return this.hasNotUpdated(this.currentAccountMetadata, filtered) ? undefined : filtered;
    }
    hasNotUpdated(options, subset) {
        const temp = Object.assign(Object.assign({}, options), subset);
        return JSON.stringify(temp) === JSON.stringify(options);
    }
}

// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.
/** Error thrown when an HTTP request fails. */
class HttpError extends Error {
    /** Constructs a new instance of {@link @microsoft/signalr.HttpError}.
     *
     * @param {string} errorMessage A descriptive error message.
     * @param {number} statusCode The HTTP status code represented by this error.
     */
    constructor(errorMessage, statusCode) {
        const trueProto = new.target.prototype;
        super(`${errorMessage}: Status code '${statusCode}'`);
        this.statusCode = statusCode;
        // Workaround issue in Typescript compiler
        // https://github.com/Microsoft/TypeScript/issues/13965#issuecomment-278570200
        this.__proto__ = trueProto;
    }
}
/** Error thrown when a timeout elapses. */
class TimeoutError extends Error {
    /** Constructs a new instance of {@link @microsoft/signalr.TimeoutError}.
     *
     * @param {string} errorMessage A descriptive error message.
     */
    constructor(errorMessage = "A timeout occurred.") {
        const trueProto = new.target.prototype;
        super(errorMessage);
        // Workaround issue in Typescript compiler
        // https://github.com/Microsoft/TypeScript/issues/13965#issuecomment-278570200
        this.__proto__ = trueProto;
    }
}
/** Error thrown when an action is aborted. */
class AbortError extends Error {
    /** Constructs a new instance of {@link AbortError}.
     *
     * @param {string} errorMessage A descriptive error message.
     */
    constructor(errorMessage = "An abort occurred.") {
        const trueProto = new.target.prototype;
        super(errorMessage);
        // Workaround issue in Typescript compiler
        // https://github.com/Microsoft/TypeScript/issues/13965#issuecomment-278570200
        this.__proto__ = trueProto;
    }
}
/** Error thrown when the selected transport is unsupported by the browser. */
/** @private */
class UnsupportedTransportError extends Error {
    /** Constructs a new instance of {@link @microsoft/signalr.UnsupportedTransportError}.
     *
     * @param {string} message A descriptive error message.
     * @param {HttpTransportType} transport The {@link @microsoft/signalr.HttpTransportType} this error occurred on.
     */
    constructor(message, transport) {
        const trueProto = new.target.prototype;
        super(message);
        this.transport = transport;
        this.errorType = 'UnsupportedTransportError';
        // Workaround issue in Typescript compiler
        // https://github.com/Microsoft/TypeScript/issues/13965#issuecomment-278570200
        this.__proto__ = trueProto;
    }
}
/** Error thrown when the selected transport is disabled by the browser. */
/** @private */
class DisabledTransportError extends Error {
    /** Constructs a new instance of {@link @microsoft/signalr.DisabledTransportError}.
     *
     * @param {string} message A descriptive error message.
     * @param {HttpTransportType} transport The {@link @microsoft/signalr.HttpTransportType} this error occurred on.
     */
    constructor(message, transport) {
        const trueProto = new.target.prototype;
        super(message);
        this.transport = transport;
        this.errorType = 'DisabledTransportError';
        // Workaround issue in Typescript compiler
        // https://github.com/Microsoft/TypeScript/issues/13965#issuecomment-278570200
        this.__proto__ = trueProto;
    }
}
/** Error thrown when the selected transport cannot be started. */
/** @private */
class FailedToStartTransportError extends Error {
    /** Constructs a new instance of {@link @microsoft/signalr.FailedToStartTransportError}.
     *
     * @param {string} message A descriptive error message.
     * @param {HttpTransportType} transport The {@link @microsoft/signalr.HttpTransportType} this error occurred on.
     */
    constructor(message, transport) {
        const trueProto = new.target.prototype;
        super(message);
        this.transport = transport;
        this.errorType = 'FailedToStartTransportError';
        // Workaround issue in Typescript compiler
        // https://github.com/Microsoft/TypeScript/issues/13965#issuecomment-278570200
        this.__proto__ = trueProto;
    }
}
/** Error thrown when the negotiation with the server failed to complete. */
/** @private */
class FailedToNegotiateWithServerError extends Error {
    /** Constructs a new instance of {@link @microsoft/signalr.FailedToNegotiateWithServerError}.
     *
     * @param {string} message A descriptive error message.
     */
    constructor(message) {
        const trueProto = new.target.prototype;
        super(message);
        this.errorType = 'FailedToNegotiateWithServerError';
        // Workaround issue in Typescript compiler
        // https://github.com/Microsoft/TypeScript/issues/13965#issuecomment-278570200
        this.__proto__ = trueProto;
    }
}
/** Error thrown when multiple errors have occurred. */
/** @private */
class AggregateErrors extends Error {
    /** Constructs a new instance of {@link @microsoft/signalr.AggregateErrors}.
     *
     * @param {string} message A descriptive error message.
     * @param {Error[]} innerErrors The collection of errors this error is aggregating.
     */
    constructor(message, innerErrors) {
        const trueProto = new.target.prototype;
        super(message);
        this.innerErrors = innerErrors;
        // Workaround issue in Typescript compiler
        // https://github.com/Microsoft/TypeScript/issues/13965#issuecomment-278570200
        this.__proto__ = trueProto;
    }
}

// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.
/** Represents an HTTP response. */
class HttpResponse {
    constructor(statusCode, statusText, content) {
        this.statusCode = statusCode;
        this.statusText = statusText;
        this.content = content;
    }
}
/** Abstraction over an HTTP client.
 *
 * This class provides an abstraction over an HTTP client so that a different implementation can be provided on different platforms.
 */
class HttpClient {
    get(url, options) {
        return this.send({
            ...options,
            method: "GET",
            url,
        });
    }
    post(url, options) {
        return this.send({
            ...options,
            method: "POST",
            url,
        });
    }
    delete(url, options) {
        return this.send({
            ...options,
            method: "DELETE",
            url,
        });
    }
    /** Gets all cookies that apply to the specified URL.
     *
     * @param url The URL that the cookies are valid for.
     * @returns {string} A string containing all the key-value cookie pairs for the specified URL.
     */
    // @ts-ignore
    getCookieString(url) {
        return "";
    }
}

// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.
// These values are designed to match the ASP.NET Log Levels since that's the pattern we're emulating here.
/** Indicates the severity of a log message.
 *
 * Log Levels are ordered in increasing severity. So `Debug` is more severe than `Trace`, etc.
 */
var LogLevel;
(function (LogLevel) {
    /** Log level for very low severity diagnostic messages. */
    LogLevel[LogLevel["Trace"] = 0] = "Trace";
    /** Log level for low severity diagnostic messages. */
    LogLevel[LogLevel["Debug"] = 1] = "Debug";
    /** Log level for informational diagnostic messages. */
    LogLevel[LogLevel["Information"] = 2] = "Information";
    /** Log level for diagnostic messages that indicate a non-fatal problem. */
    LogLevel[LogLevel["Warning"] = 3] = "Warning";
    /** Log level for diagnostic messages that indicate a failure in the current operation. */
    LogLevel[LogLevel["Error"] = 4] = "Error";
    /** Log level for diagnostic messages that indicate a failure that will terminate the entire application. */
    LogLevel[LogLevel["Critical"] = 5] = "Critical";
    /** The highest possible log level. Used when configuring logging to indicate that no log messages should be emitted. */
    LogLevel[LogLevel["None"] = 6] = "None";
})(LogLevel || (LogLevel = {}));

// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.
/** A logger that does nothing when log messages are sent to it. */
class NullLogger {
    constructor() { }
    /** @inheritDoc */
    // eslint-disable-next-line
    log(_logLevel, _message) {
    }
}
/** The singleton instance of the {@link @microsoft/signalr.NullLogger}. */
NullLogger.instance = new NullLogger();

// Version token that will be replaced by the prepack command
/** The version of the SignalR client. */
const VERSION = "7.0.14";
/** @private */
class Arg {
    static isRequired(val, name) {
        if (val === null || val === undefined) {
            throw new Error(`The '${name}' argument is required.`);
        }
    }
    static isNotEmpty(val, name) {
        if (!val || val.match(/^\s*$/)) {
            throw new Error(`The '${name}' argument should not be empty.`);
        }
    }
    static isIn(val, values, name) {
        // TypeScript enums have keys for **both** the name and the value of each enum member on the type itself.
        if (!(val in values)) {
            throw new Error(`Unknown ${name} value: ${val}.`);
        }
    }
}
/** @private */
class Platform {
    // react-native has a window but no document so we should check both
    static get isBrowser() {
        return typeof window === "object" && typeof window.document === "object";
    }
    // WebWorkers don't have a window object so the isBrowser check would fail
    static get isWebWorker() {
        return typeof self === "object" && "importScripts" in self;
    }
    // react-native has a window but no document
    static get isReactNative() {
        return typeof window === "object" && typeof window.document === "undefined";
    }
    // Node apps shouldn't have a window object, but WebWorkers don't either
    // so we need to check for both WebWorker and window
    static get isNode() {
        return !this.isBrowser && !this.isWebWorker && !this.isReactNative;
    }
}
/** @private */
function getDataDetail(data, includeContent) {
    let detail = "";
    if (isArrayBuffer(data)) {
        detail = `Binary data of length ${data.byteLength}`;
        if (includeContent) {
            detail += `. Content: '${formatArrayBuffer(data)}'`;
        }
    }
    else if (typeof data === "string") {
        detail = `String data of length ${data.length}`;
        if (includeContent) {
            detail += `. Content: '${data}'`;
        }
    }
    return detail;
}
/** @private */
function formatArrayBuffer(data) {
    const view = new Uint8Array(data);
    // Uint8Array.map only supports returning another Uint8Array?
    let str = "";
    view.forEach((num) => {
        const pad = num < 16 ? "0" : "";
        str += `0x${pad}${num.toString(16)} `;
    });
    // Trim of trailing space.
    return str.substr(0, str.length - 1);
}
// Also in signalr-protocol-msgpack/Utils.ts
/** @private */
function isArrayBuffer(val) {
    return val && typeof ArrayBuffer !== "undefined" &&
        (val instanceof ArrayBuffer ||
            // Sometimes we get an ArrayBuffer that doesn't satisfy instanceof
            (val.constructor && val.constructor.name === "ArrayBuffer"));
}
/** @private */
async function sendMessage(logger, transportName, httpClient, url, content, options) {
    const headers = {};
    const [name, value] = getUserAgentHeader();
    headers[name] = value;
    logger.log(LogLevel.Trace, `(${transportName} transport) sending data. ${getDataDetail(content, options.logMessageContent)}.`);
    const responseType = isArrayBuffer(content) ? "arraybuffer" : "text";
    const response = await httpClient.post(url, {
        content,
        headers: { ...headers, ...options.headers },
        responseType,
        timeout: options.timeout,
        withCredentials: options.withCredentials,
    });
    logger.log(LogLevel.Trace, `(${transportName} transport) request complete. Response status: ${response.statusCode}.`);
}
/** @private */
function createLogger(logger) {
    if (logger === undefined) {
        return new ConsoleLogger(LogLevel.Information);
    }
    if (logger === null) {
        return NullLogger.instance;
    }
    if (logger.log !== undefined) {
        return logger;
    }
    return new ConsoleLogger(logger);
}
/** @private */
class SubjectSubscription {
    constructor(subject, observer) {
        this._subject = subject;
        this._observer = observer;
    }
    dispose() {
        const index = this._subject.observers.indexOf(this._observer);
        if (index > -1) {
            this._subject.observers.splice(index, 1);
        }
        if (this._subject.observers.length === 0 && this._subject.cancelCallback) {
            this._subject.cancelCallback().catch((_) => { });
        }
    }
}
/** @private */
class ConsoleLogger {
    constructor(minimumLogLevel) {
        this._minLevel = minimumLogLevel;
        this.out = console;
    }
    log(logLevel, message) {
        if (logLevel >= this._minLevel) {
            const msg = `[${new Date().toISOString()}] ${LogLevel[logLevel]}: ${message}`;
            switch (logLevel) {
                case LogLevel.Critical:
                case LogLevel.Error:
                    this.out.error(msg);
                    break;
                case LogLevel.Warning:
                    this.out.warn(msg);
                    break;
                case LogLevel.Information:
                    this.out.info(msg);
                    break;
                default:
                    // console.debug only goes to attached debuggers in Node, so we use console.log for Trace and Debug
                    this.out.log(msg);
                    break;
            }
        }
    }
}
/** @private */
function getUserAgentHeader() {
    let userAgentHeaderName = "X-SignalR-User-Agent";
    if (Platform.isNode) {
        userAgentHeaderName = "User-Agent";
    }
    return [userAgentHeaderName, constructUserAgent(VERSION, getOsName(), getRuntime(), getRuntimeVersion())];
}
/** @private */
function constructUserAgent(version, os, runtime, runtimeVersion) {
    // Microsoft SignalR/[Version] ([Detailed Version]; [Operating System]; [Runtime]; [Runtime Version])
    let userAgent = "Microsoft SignalR/";
    const majorAndMinor = version.split(".");
    userAgent += `${majorAndMinor[0]}.${majorAndMinor[1]}`;
    userAgent += ` (${version}; `;
    if (os && os !== "") {
        userAgent += `${os}; `;
    }
    else {
        userAgent += "Unknown OS; ";
    }
    userAgent += `${runtime}`;
    if (runtimeVersion) {
        userAgent += `; ${runtimeVersion}`;
    }
    else {
        userAgent += "; Unknown Runtime Version";
    }
    userAgent += ")";
    return userAgent;
}
// eslint-disable-next-line spaced-comment
 function getOsName() {
    if (Platform.isNode) {
        switch (process.platform) {
            case "win32":
                return "Windows NT";
            case "darwin":
                return "macOS";
            case "linux":
                return "Linux";
            default:
                return process.platform;
        }
    }
    else {
        return "";
    }
}
// eslint-disable-next-line spaced-comment
 function getRuntimeVersion() {
    if (Platform.isNode) {
        return process.versions.node;
    }
    return undefined;
}
function getRuntime() {
    if (Platform.isNode) {
        return "NodeJS";
    }
    else {
        return "Browser";
    }
}
/** @private */
function getErrorString(e) {
    if (e.stack) {
        return e.stack;
    }
    else if (e.message) {
        return e.message;
    }
    return `${e}`;
}
/** @private */
function getGlobalThis() {
    // globalThis is semi-new and not available in Node until v12
    if (typeof globalThis !== "undefined") {
        return globalThis;
    }
    if (typeof self !== "undefined") {
        return self;
    }
    if (typeof window !== "undefined") {
        return window;
    }
    if (typeof global !== "undefined") {
        return global;
    }
    throw new Error("could not find global");
}

// Licensed to the .NET Foundation under one or more agreements.
class FetchHttpClient extends HttpClient {
    constructor(logger) {
        super();
        this._logger = logger;
        if (typeof fetch === "undefined") {
            // In order to ignore the dynamic require in webpack builds we need to do this magic
            // @ts-ignore: TS doesn't know about these names
            const requireFunc = typeof __webpack_require__ === "function" ? __non_webpack_require__ : require;
            // Cookies aren't automatically handled in Node so we need to add a CookieJar to preserve cookies across requests
            this._jar = new (requireFunc("tough-cookie")).CookieJar();
            this._fetchType = requireFunc("node-fetch");
            // node-fetch doesn't have a nice API for getting and setting cookies
            // fetch-cookie will wrap a fetch implementation with a default CookieJar or a provided one
            this._fetchType = requireFunc("fetch-cookie")(this._fetchType, this._jar);
        }
        else {
            this._fetchType = fetch.bind(getGlobalThis());
        }
        if (typeof AbortController === "undefined") {
            // In order to ignore the dynamic require in webpack builds we need to do this magic
            // @ts-ignore: TS doesn't know about these names
            const requireFunc = typeof __webpack_require__ === "function" ? __non_webpack_require__ : require;
            // Node needs EventListener methods on AbortController which our custom polyfill doesn't provide
            this._abortControllerType = requireFunc("abort-controller");
        }
        else {
            this._abortControllerType = AbortController;
        }
    }
    /** @inheritDoc */
    async send(request) {
        // Check that abort was not signaled before calling send
        if (request.abortSignal && request.abortSignal.aborted) {
            throw new AbortError();
        }
        if (!request.method) {
            throw new Error("No method defined.");
        }
        if (!request.url) {
            throw new Error("No url defined.");
        }
        const abortController = new this._abortControllerType();
        let error;
        // Hook our abortSignal into the abort controller
        if (request.abortSignal) {
            request.abortSignal.onabort = () => {
                abortController.abort();
                error = new AbortError();
            };
        }
        // If a timeout has been passed in, setup a timeout to call abort
        // Type needs to be any to fit window.setTimeout and NodeJS.setTimeout
        let timeoutId = null;
        if (request.timeout) {
            const msTimeout = request.timeout;
            timeoutId = setTimeout(() => {
                abortController.abort();
                this._logger.log(LogLevel.Warning, `Timeout from HTTP request.`);
                error = new TimeoutError();
            }, msTimeout);
        }
        if (request.content === "") {
            request.content = undefined;
        }
        if (request.content) {
            // Explicitly setting the Content-Type header for React Native on Android platform.
            request.headers = request.headers || {};
            if (isArrayBuffer(request.content)) {
                request.headers["Content-Type"] = "application/octet-stream";
            }
            else {
                request.headers["Content-Type"] = "text/plain;charset=UTF-8";
            }
        }
        let response;
        try {
            response = await this._fetchType(request.url, {
                body: request.content,
                cache: "no-cache",
                credentials: request.withCredentials === true ? "include" : "same-origin",
                headers: {
                    "X-Requested-With": "XMLHttpRequest",
                    ...request.headers,
                },
                method: request.method,
                mode: "cors",
                redirect: "follow",
                signal: abortController.signal,
            });
        }
        catch (e) {
            if (error) {
                throw error;
            }
            this._logger.log(LogLevel.Warning, `Error from HTTP request. ${e}.`);
            throw e;
        }
        finally {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (request.abortSignal) {
                request.abortSignal.onabort = null;
            }
        }
        if (!response.ok) {
            const errorMessage = await deserializeContent(response, "text");
            throw new HttpError(errorMessage || response.statusText, response.status);
        }
        const content = deserializeContent(response, request.responseType);
        const payload = await content;
        return new HttpResponse(response.status, response.statusText, payload);
    }
    getCookieString(url) {
        let cookies = "";
        if (Platform.isNode && this._jar) {
            // @ts-ignore: unused variable
            this._jar.getCookies(url, (e, c) => cookies = c.join("; "));
        }
        return cookies;
    }
}
function deserializeContent(response, responseType) {
    let content;
    switch (responseType) {
        case "arraybuffer":
            content = response.arrayBuffer();
            break;
        case "text":
            content = response.text();
            break;
        case "blob":
        case "document":
        case "json":
            throw new Error(`${responseType} is not supported.`);
        default:
            content = response.text();
            break;
    }
    return content;
}

// Licensed to the .NET Foundation under one or more agreements.
class XhrHttpClient extends HttpClient {
    constructor(logger) {
        super();
        this._logger = logger;
    }
    /** @inheritDoc */
    send(request) {
        // Check that abort was not signaled before calling send
        if (request.abortSignal && request.abortSignal.aborted) {
            return Promise.reject(new AbortError());
        }
        if (!request.method) {
            return Promise.reject(new Error("No method defined."));
        }
        if (!request.url) {
            return Promise.reject(new Error("No url defined."));
        }
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open(request.method, request.url, true);
            xhr.withCredentials = request.withCredentials === undefined ? true : request.withCredentials;
            xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
            if (request.content === "") {
                request.content = undefined;
            }
            if (request.content) {
                // Explicitly setting the Content-Type header for React Native on Android platform.
                if (isArrayBuffer(request.content)) {
                    xhr.setRequestHeader("Content-Type", "application/octet-stream");
                }
                else {
                    xhr.setRequestHeader("Content-Type", "text/plain;charset=UTF-8");
                }
            }
            const headers = request.headers;
            if (headers) {
                Object.keys(headers)
                    .forEach((header) => {
                    xhr.setRequestHeader(header, headers[header]);
                });
            }
            if (request.responseType) {
                xhr.responseType = request.responseType;
            }
            if (request.abortSignal) {
                request.abortSignal.onabort = () => {
                    xhr.abort();
                    reject(new AbortError());
                };
            }
            if (request.timeout) {
                xhr.timeout = request.timeout;
            }
            xhr.onload = () => {
                if (request.abortSignal) {
                    request.abortSignal.onabort = null;
                }
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve(new HttpResponse(xhr.status, xhr.statusText, xhr.response || xhr.responseText));
                }
                else {
                    reject(new HttpError(xhr.response || xhr.responseText || xhr.statusText, xhr.status));
                }
            };
            xhr.onerror = () => {
                this._logger.log(LogLevel.Warning, `Error from HTTP request. ${xhr.status}: ${xhr.statusText}.`);
                reject(new HttpError(xhr.statusText, xhr.status));
            };
            xhr.ontimeout = () => {
                this._logger.log(LogLevel.Warning, `Timeout from HTTP request.`);
                reject(new TimeoutError());
            };
            xhr.send(request.content);
        });
    }
}

// Licensed to the .NET Foundation under one or more agreements.
/** Default implementation of {@link @microsoft/signalr.HttpClient}. */
class DefaultHttpClient extends HttpClient {
    /** Creates a new instance of the {@link @microsoft/signalr.DefaultHttpClient}, using the provided {@link @microsoft/signalr.ILogger} to log messages. */
    constructor(logger) {
        super();
        if (typeof fetch !== "undefined" || Platform.isNode) {
            this._httpClient = new FetchHttpClient(logger);
        }
        else if (typeof XMLHttpRequest !== "undefined") {
            this._httpClient = new XhrHttpClient(logger);
        }
        else {
            throw new Error("No usable HttpClient found.");
        }
    }
    /** @inheritDoc */
    send(request) {
        // Check that abort was not signaled before calling send
        if (request.abortSignal && request.abortSignal.aborted) {
            return Promise.reject(new AbortError());
        }
        if (!request.method) {
            return Promise.reject(new Error("No method defined."));
        }
        if (!request.url) {
            return Promise.reject(new Error("No url defined."));
        }
        return this._httpClient.send(request);
    }
    getCookieString(url) {
        return this._httpClient.getCookieString(url);
    }
}

// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.
// Not exported from index
/** @private */
class TextMessageFormat {
    static write(output) {
        return `${output}${TextMessageFormat.RecordSeparator}`;
    }
    static parse(input) {
        if (input[input.length - 1] !== TextMessageFormat.RecordSeparator) {
            throw new Error("Message is incomplete.");
        }
        const messages = input.split(TextMessageFormat.RecordSeparator);
        messages.pop();
        return messages;
    }
}
TextMessageFormat.RecordSeparatorCode = 0x1e;
TextMessageFormat.RecordSeparator = String.fromCharCode(TextMessageFormat.RecordSeparatorCode);

// Licensed to the .NET Foundation under one or more agreements.
/** @private */
class HandshakeProtocol {
    // Handshake request is always JSON
    writeHandshakeRequest(handshakeRequest) {
        return TextMessageFormat.write(JSON.stringify(handshakeRequest));
    }
    parseHandshakeResponse(data) {
        let messageData;
        let remainingData;
        if (isArrayBuffer(data)) {
            // Format is binary but still need to read JSON text from handshake response
            const binaryData = new Uint8Array(data);
            const separatorIndex = binaryData.indexOf(TextMessageFormat.RecordSeparatorCode);
            if (separatorIndex === -1) {
                throw new Error("Message is incomplete.");
            }
            // content before separator is handshake response
            // optional content after is additional messages
            const responseLength = separatorIndex + 1;
            messageData = String.fromCharCode.apply(null, Array.prototype.slice.call(binaryData.slice(0, responseLength)));
            remainingData = (binaryData.byteLength > responseLength) ? binaryData.slice(responseLength).buffer : null;
        }
        else {
            const textData = data;
            const separatorIndex = textData.indexOf(TextMessageFormat.RecordSeparator);
            if (separatorIndex === -1) {
                throw new Error("Message is incomplete.");
            }
            // content before separator is handshake response
            // optional content after is additional messages
            const responseLength = separatorIndex + 1;
            messageData = textData.substring(0, responseLength);
            remainingData = (textData.length > responseLength) ? textData.substring(responseLength) : null;
        }
        // At this point we should have just the single handshake message
        const messages = TextMessageFormat.parse(messageData);
        const response = JSON.parse(messages[0]);
        if (response.type) {
            throw new Error("Expected a handshake response from the server.");
        }
        const responseMessage = response;
        // multiple messages could have arrived with handshake
        // return additional data to be parsed as usual, or null if all parsed
        return [remainingData, responseMessage];
    }
}

// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.
/** Defines the type of a Hub Message. */
var MessageType;
(function (MessageType) {
    /** Indicates the message is an Invocation message and implements the {@link @microsoft/signalr.InvocationMessage} interface. */
    MessageType[MessageType["Invocation"] = 1] = "Invocation";
    /** Indicates the message is a StreamItem message and implements the {@link @microsoft/signalr.StreamItemMessage} interface. */
    MessageType[MessageType["StreamItem"] = 2] = "StreamItem";
    /** Indicates the message is a Completion message and implements the {@link @microsoft/signalr.CompletionMessage} interface. */
    MessageType[MessageType["Completion"] = 3] = "Completion";
    /** Indicates the message is a Stream Invocation message and implements the {@link @microsoft/signalr.StreamInvocationMessage} interface. */
    MessageType[MessageType["StreamInvocation"] = 4] = "StreamInvocation";
    /** Indicates the message is a Cancel Invocation message and implements the {@link @microsoft/signalr.CancelInvocationMessage} interface. */
    MessageType[MessageType["CancelInvocation"] = 5] = "CancelInvocation";
    /** Indicates the message is a Ping message and implements the {@link @microsoft/signalr.PingMessage} interface. */
    MessageType[MessageType["Ping"] = 6] = "Ping";
    /** Indicates the message is a Close message and implements the {@link @microsoft/signalr.CloseMessage} interface. */
    MessageType[MessageType["Close"] = 7] = "Close";
})(MessageType || (MessageType = {}));

// Licensed to the .NET Foundation under one or more agreements.
/** Stream implementation to stream items to the server. */
class Subject {
    constructor() {
        this.observers = [];
    }
    next(item) {
        for (const observer of this.observers) {
            observer.next(item);
        }
    }
    error(err) {
        for (const observer of this.observers) {
            if (observer.error) {
                observer.error(err);
            }
        }
    }
    complete() {
        for (const observer of this.observers) {
            if (observer.complete) {
                observer.complete();
            }
        }
    }
    subscribe(observer) {
        this.observers.push(observer);
        return new SubjectSubscription(this, observer);
    }
}

// Licensed to the .NET Foundation under one or more agreements.
const DEFAULT_TIMEOUT_IN_MS = 30 * 1000;
const DEFAULT_PING_INTERVAL_IN_MS = 15 * 1000;
/** Describes the current state of the {@link HubConnection} to the server. */
var HubConnectionState;
(function (HubConnectionState) {
    /** The hub connection is disconnected. */
    HubConnectionState["Disconnected"] = "Disconnected";
    /** The hub connection is connecting. */
    HubConnectionState["Connecting"] = "Connecting";
    /** The hub connection is connected. */
    HubConnectionState["Connected"] = "Connected";
    /** The hub connection is disconnecting. */
    HubConnectionState["Disconnecting"] = "Disconnecting";
    /** The hub connection is reconnecting. */
    HubConnectionState["Reconnecting"] = "Reconnecting";
})(HubConnectionState || (HubConnectionState = {}));
/** Represents a connection to a SignalR Hub. */
class HubConnection {
    constructor(connection, logger, protocol, reconnectPolicy) {
        this._nextKeepAlive = 0;
        this._freezeEventListener = () => {
            this._logger.log(LogLevel.Warning, "The page is being frozen, this will likely lead to the connection being closed and messages being lost. For more information see the docs at https://docs.microsoft.com/aspnet/core/signalr/javascript-client#bsleep");
        };
        Arg.isRequired(connection, "connection");
        Arg.isRequired(logger, "logger");
        Arg.isRequired(protocol, "protocol");
        this.serverTimeoutInMilliseconds = DEFAULT_TIMEOUT_IN_MS;
        this.keepAliveIntervalInMilliseconds = DEFAULT_PING_INTERVAL_IN_MS;
        this._logger = logger;
        this._protocol = protocol;
        this.connection = connection;
        this._reconnectPolicy = reconnectPolicy;
        this._handshakeProtocol = new HandshakeProtocol();
        this.connection.onreceive = (data) => this._processIncomingData(data);
        this.connection.onclose = (error) => this._connectionClosed(error);
        this._callbacks = {};
        this._methods = {};
        this._closedCallbacks = [];
        this._reconnectingCallbacks = [];
        this._reconnectedCallbacks = [];
        this._invocationId = 0;
        this._receivedHandshakeResponse = false;
        this._connectionState = HubConnectionState.Disconnected;
        this._connectionStarted = false;
        this._cachedPingMessage = this._protocol.writeMessage({ type: MessageType.Ping });
    }
    /** @internal */
    // Using a public static factory method means we can have a private constructor and an _internal_
    // create method that can be used by HubConnectionBuilder. An "internal" constructor would just
    // be stripped away and the '.d.ts' file would have no constructor, which is interpreted as a
    // public parameter-less constructor.
    static create(connection, logger, protocol, reconnectPolicy) {
        return new HubConnection(connection, logger, protocol, reconnectPolicy);
    }
    /** Indicates the state of the {@link HubConnection} to the server. */
    get state() {
        return this._connectionState;
    }
    /** Represents the connection id of the {@link HubConnection} on the server. The connection id will be null when the connection is either
     *  in the disconnected state or if the negotiation step was skipped.
     */
    get connectionId() {
        return this.connection ? (this.connection.connectionId || null) : null;
    }
    /** Indicates the url of the {@link HubConnection} to the server. */
    get baseUrl() {
        return this.connection.baseUrl || "";
    }
    /**
     * Sets a new url for the HubConnection. Note that the url can only be changed when the connection is in either the Disconnected or
     * Reconnecting states.
     * @param {string} url The url to connect to.
     */
    set baseUrl(url) {
        if (this._connectionState !== HubConnectionState.Disconnected && this._connectionState !== HubConnectionState.Reconnecting) {
            throw new Error("The HubConnection must be in the Disconnected or Reconnecting state to change the url.");
        }
        if (!url) {
            throw new Error("The HubConnection url must be a valid url.");
        }
        this.connection.baseUrl = url;
    }
    /** Starts the connection.
     *
     * @returns {Promise<void>} A Promise that resolves when the connection has been successfully established, or rejects with an error.
     */
    start() {
        this._startPromise = this._startWithStateTransitions();
        return this._startPromise;
    }
    async _startWithStateTransitions() {
        if (this._connectionState !== HubConnectionState.Disconnected) {
            return Promise.reject(new Error("Cannot start a HubConnection that is not in the 'Disconnected' state."));
        }
        this._connectionState = HubConnectionState.Connecting;
        this._logger.log(LogLevel.Debug, "Starting HubConnection.");
        try {
            await this._startInternal();
            if (Platform.isBrowser) {
                // Log when the browser freezes the tab so users know why their connection unexpectedly stopped working
                window.document.addEventListener("freeze", this._freezeEventListener);
            }
            this._connectionState = HubConnectionState.Connected;
            this._connectionStarted = true;
            this._logger.log(LogLevel.Debug, "HubConnection connected successfully.");
        }
        catch (e) {
            this._connectionState = HubConnectionState.Disconnected;
            this._logger.log(LogLevel.Debug, `HubConnection failed to start successfully because of error '${e}'.`);
            return Promise.reject(e);
        }
    }
    async _startInternal() {
        this._stopDuringStartError = undefined;
        this._receivedHandshakeResponse = false;
        // Set up the promise before any connection is (re)started otherwise it could race with received messages
        const handshakePromise = new Promise((resolve, reject) => {
            this._handshakeResolver = resolve;
            this._handshakeRejecter = reject;
        });
        await this.connection.start(this._protocol.transferFormat);
        try {
            const handshakeRequest = {
                protocol: this._protocol.name,
                version: this._protocol.version,
            };
            this._logger.log(LogLevel.Debug, "Sending handshake request.");
            await this._sendMessage(this._handshakeProtocol.writeHandshakeRequest(handshakeRequest));
            this._logger.log(LogLevel.Information, `Using HubProtocol '${this._protocol.name}'.`);
            // defensively cleanup timeout in case we receive a message from the server before we finish start
            this._cleanupTimeout();
            this._resetTimeoutPeriod();
            this._resetKeepAliveInterval();
            await handshakePromise;
            // It's important to check the stopDuringStartError instead of just relying on the handshakePromise
            // being rejected on close, because this continuation can run after both the handshake completed successfully
            // and the connection was closed.
            if (this._stopDuringStartError) {
                // It's important to throw instead of returning a rejected promise, because we don't want to allow any state
                // transitions to occur between now and the calling code observing the exceptions. Returning a rejected promise
                // will cause the calling continuation to get scheduled to run later.
                // eslint-disable-next-line @typescript-eslint/no-throw-literal
                throw this._stopDuringStartError;
            }
            if (!this.connection.features.inherentKeepAlive) {
                await this._sendMessage(this._cachedPingMessage);
            }
        }
        catch (e) {
            this._logger.log(LogLevel.Debug, `Hub handshake failed with error '${e}' during start(). Stopping HubConnection.`);
            this._cleanupTimeout();
            this._cleanupPingTimer();
            // HttpConnection.stop() should not complete until after the onclose callback is invoked.
            // This will transition the HubConnection to the disconnected state before HttpConnection.stop() completes.
            await this.connection.stop(e);
            throw e;
        }
    }
    /** Stops the connection.
     *
     * @returns {Promise<void>} A Promise that resolves when the connection has been successfully terminated, or rejects with an error.
     */
    async stop() {
        // Capture the start promise before the connection might be restarted in an onclose callback.
        const startPromise = this._startPromise;
        this._stopPromise = this._stopInternal();
        await this._stopPromise;
        try {
            // Awaiting undefined continues immediately
            await startPromise;
        }
        catch (e) {
            // This exception is returned to the user as a rejected Promise from the start method.
        }
    }
    _stopInternal(error) {
        if (this._connectionState === HubConnectionState.Disconnected) {
            this._logger.log(LogLevel.Debug, `Call to HubConnection.stop(${error}) ignored because it is already in the disconnected state.`);
            return Promise.resolve();
        }
        if (this._connectionState === HubConnectionState.Disconnecting) {
            this._logger.log(LogLevel.Debug, `Call to HttpConnection.stop(${error}) ignored because the connection is already in the disconnecting state.`);
            return this._stopPromise;
        }
        this._connectionState = HubConnectionState.Disconnecting;
        this._logger.log(LogLevel.Debug, "Stopping HubConnection.");
        if (this._reconnectDelayHandle) {
            // We're in a reconnect delay which means the underlying connection is currently already stopped.
            // Just clear the handle to stop the reconnect loop (which no one is waiting on thankfully) and
            // fire the onclose callbacks.
            this._logger.log(LogLevel.Debug, "Connection stopped during reconnect delay. Done reconnecting.");
            clearTimeout(this._reconnectDelayHandle);
            this._reconnectDelayHandle = undefined;
            this._completeClose();
            return Promise.resolve();
        }
        this._cleanupTimeout();
        this._cleanupPingTimer();
        this._stopDuringStartError = error || new AbortError("The connection was stopped before the hub handshake could complete.");
        // HttpConnection.stop() should not complete until after either HttpConnection.start() fails
        // or the onclose callback is invoked. The onclose callback will transition the HubConnection
        // to the disconnected state if need be before HttpConnection.stop() completes.
        return this.connection.stop(error);
    }
    /** Invokes a streaming hub method on the server using the specified name and arguments.
     *
     * @typeparam T The type of the items returned by the server.
     * @param {string} methodName The name of the server method to invoke.
     * @param {any[]} args The arguments used to invoke the server method.
     * @returns {IStreamResult<T>} An object that yields results from the server as they are received.
     */
    stream(methodName, ...args) {
        const [streams, streamIds] = this._replaceStreamingParams(args);
        const invocationDescriptor = this._createStreamInvocation(methodName, args, streamIds);
        // eslint-disable-next-line prefer-const
        let promiseQueue;
        const subject = new Subject();
        subject.cancelCallback = () => {
            const cancelInvocation = this._createCancelInvocation(invocationDescriptor.invocationId);
            delete this._callbacks[invocationDescriptor.invocationId];
            return promiseQueue.then(() => {
                return this._sendWithProtocol(cancelInvocation);
            });
        };
        this._callbacks[invocationDescriptor.invocationId] = (invocationEvent, error) => {
            if (error) {
                subject.error(error);
                return;
            }
            else if (invocationEvent) {
                // invocationEvent will not be null when an error is not passed to the callback
                if (invocationEvent.type === MessageType.Completion) {
                    if (invocationEvent.error) {
                        subject.error(new Error(invocationEvent.error));
                    }
                    else {
                        subject.complete();
                    }
                }
                else {
                    subject.next((invocationEvent.item));
                }
            }
        };
        promiseQueue = this._sendWithProtocol(invocationDescriptor)
            .catch((e) => {
            subject.error(e);
            delete this._callbacks[invocationDescriptor.invocationId];
        });
        this._launchStreams(streams, promiseQueue);
        return subject;
    }
    _sendMessage(message) {
        this._resetKeepAliveInterval();
        return this.connection.send(message);
    }
    /**
     * Sends a js object to the server.
     * @param message The js object to serialize and send.
     */
    _sendWithProtocol(message) {
        return this._sendMessage(this._protocol.writeMessage(message));
    }
    /** Invokes a hub method on the server using the specified name and arguments. Does not wait for a response from the receiver.
     *
     * The Promise returned by this method resolves when the client has sent the invocation to the server. The server may still
     * be processing the invocation.
     *
     * @param {string} methodName The name of the server method to invoke.
     * @param {any[]} args The arguments used to invoke the server method.
     * @returns {Promise<void>} A Promise that resolves when the invocation has been successfully sent, or rejects with an error.
     */
    send(methodName, ...args) {
        const [streams, streamIds] = this._replaceStreamingParams(args);
        const sendPromise = this._sendWithProtocol(this._createInvocation(methodName, args, true, streamIds));
        this._launchStreams(streams, sendPromise);
        return sendPromise;
    }
    /** Invokes a hub method on the server using the specified name and arguments.
     *
     * The Promise returned by this method resolves when the server indicates it has finished invoking the method. When the promise
     * resolves, the server has finished invoking the method. If the server method returns a result, it is produced as the result of
     * resolving the Promise.
     *
     * @typeparam T The expected return type.
     * @param {string} methodName The name of the server method to invoke.
     * @param {any[]} args The arguments used to invoke the server method.
     * @returns {Promise<T>} A Promise that resolves with the result of the server method (if any), or rejects with an error.
     */
    invoke(methodName, ...args) {
        const [streams, streamIds] = this._replaceStreamingParams(args);
        const invocationDescriptor = this._createInvocation(methodName, args, false, streamIds);
        const p = new Promise((resolve, reject) => {
            // invocationId will always have a value for a non-blocking invocation
            this._callbacks[invocationDescriptor.invocationId] = (invocationEvent, error) => {
                if (error) {
                    reject(error);
                    return;
                }
                else if (invocationEvent) {
                    // invocationEvent will not be null when an error is not passed to the callback
                    if (invocationEvent.type === MessageType.Completion) {
                        if (invocationEvent.error) {
                            reject(new Error(invocationEvent.error));
                        }
                        else {
                            resolve(invocationEvent.result);
                        }
                    }
                    else {
                        reject(new Error(`Unexpected message type: ${invocationEvent.type}`));
                    }
                }
            };
            const promiseQueue = this._sendWithProtocol(invocationDescriptor)
                .catch((e) => {
                reject(e);
                // invocationId will always have a value for a non-blocking invocation
                delete this._callbacks[invocationDescriptor.invocationId];
            });
            this._launchStreams(streams, promiseQueue);
        });
        return p;
    }
    on(methodName, newMethod) {
        if (!methodName || !newMethod) {
            return;
        }
        methodName = methodName.toLowerCase();
        if (!this._methods[methodName]) {
            this._methods[methodName] = [];
        }
        // Preventing adding the same handler multiple times.
        if (this._methods[methodName].indexOf(newMethod) !== -1) {
            return;
        }
        this._methods[methodName].push(newMethod);
    }
    off(methodName, method) {
        if (!methodName) {
            return;
        }
        methodName = methodName.toLowerCase();
        const handlers = this._methods[methodName];
        if (!handlers) {
            return;
        }
        if (method) {
            const removeIdx = handlers.indexOf(method);
            if (removeIdx !== -1) {
                handlers.splice(removeIdx, 1);
                if (handlers.length === 0) {
                    delete this._methods[methodName];
                }
            }
        }
        else {
            delete this._methods[methodName];
        }
    }
    /** Registers a handler that will be invoked when the connection is closed.
     *
     * @param {Function} callback The handler that will be invoked when the connection is closed. Optionally receives a single argument containing the error that caused the connection to close (if any).
     */
    onclose(callback) {
        if (callback) {
            this._closedCallbacks.push(callback);
        }
    }
    /** Registers a handler that will be invoked when the connection starts reconnecting.
     *
     * @param {Function} callback The handler that will be invoked when the connection starts reconnecting. Optionally receives a single argument containing the error that caused the connection to start reconnecting (if any).
     */
    onreconnecting(callback) {
        if (callback) {
            this._reconnectingCallbacks.push(callback);
        }
    }
    /** Registers a handler that will be invoked when the connection successfully reconnects.
     *
     * @param {Function} callback The handler that will be invoked when the connection successfully reconnects.
     */
    onreconnected(callback) {
        if (callback) {
            this._reconnectedCallbacks.push(callback);
        }
    }
    _processIncomingData(data) {
        this._cleanupTimeout();
        if (!this._receivedHandshakeResponse) {
            data = this._processHandshakeResponse(data);
            this._receivedHandshakeResponse = true;
        }
        // Data may have all been read when processing handshake response
        if (data) {
            // Parse the messages
            const messages = this._protocol.parseMessages(data, this._logger);
            for (const message of messages) {
                switch (message.type) {
                    case MessageType.Invocation:
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        this._invokeClientMethod(message);
                        break;
                    case MessageType.StreamItem:
                    case MessageType.Completion: {
                        const callback = this._callbacks[message.invocationId];
                        if (callback) {
                            if (message.type === MessageType.Completion) {
                                delete this._callbacks[message.invocationId];
                            }
                            try {
                                callback(message);
                            }
                            catch (e) {
                                this._logger.log(LogLevel.Error, `Stream callback threw error: ${getErrorString(e)}`);
                            }
                        }
                        break;
                    }
                    case MessageType.Ping:
                        // Don't care about pings
                        break;
                    case MessageType.Close: {
                        this._logger.log(LogLevel.Information, "Close message received from server.");
                        const error = message.error ? new Error("Server returned an error on close: " + message.error) : undefined;
                        if (message.allowReconnect === true) {
                            // It feels wrong not to await connection.stop() here, but processIncomingData is called as part of an onreceive callback which is not async,
                            // this is already the behavior for serverTimeout(), and HttpConnection.Stop() should catch and log all possible exceptions.
                            // eslint-disable-next-line @typescript-eslint/no-floating-promises
                            this.connection.stop(error);
                        }
                        else {
                            // We cannot await stopInternal() here, but subsequent calls to stop() will await this if stopInternal() is still ongoing.
                            this._stopPromise = this._stopInternal(error);
                        }
                        break;
                    }
                    default:
                        this._logger.log(LogLevel.Warning, `Invalid message type: ${message.type}.`);
                        break;
                }
            }
        }
        this._resetTimeoutPeriod();
    }
    _processHandshakeResponse(data) {
        let responseMessage;
        let remainingData;
        try {
            [remainingData, responseMessage] = this._handshakeProtocol.parseHandshakeResponse(data);
        }
        catch (e) {
            const message = "Error parsing handshake response: " + e;
            this._logger.log(LogLevel.Error, message);
            const error = new Error(message);
            this._handshakeRejecter(error);
            throw error;
        }
        if (responseMessage.error) {
            const message = "Server returned handshake error: " + responseMessage.error;
            this._logger.log(LogLevel.Error, message);
            const error = new Error(message);
            this._handshakeRejecter(error);
            throw error;
        }
        else {
            this._logger.log(LogLevel.Debug, "Server handshake complete.");
        }
        this._handshakeResolver();
        return remainingData;
    }
    _resetKeepAliveInterval() {
        if (this.connection.features.inherentKeepAlive) {
            return;
        }
        // Set the time we want the next keep alive to be sent
        // Timer will be setup on next message receive
        this._nextKeepAlive = new Date().getTime() + this.keepAliveIntervalInMilliseconds;
        this._cleanupPingTimer();
    }
    _resetTimeoutPeriod() {
        if (!this.connection.features || !this.connection.features.inherentKeepAlive) {
            // Set the timeout timer
            this._timeoutHandle = setTimeout(() => this.serverTimeout(), this.serverTimeoutInMilliseconds);
            // Set keepAlive timer if there isn't one
            if (this._pingServerHandle === undefined) {
                let nextPing = this._nextKeepAlive - new Date().getTime();
                if (nextPing < 0) {
                    nextPing = 0;
                }
                // The timer needs to be set from a networking callback to avoid Chrome timer throttling from causing timers to run once a minute
                this._pingServerHandle = setTimeout(async () => {
                    if (this._connectionState === HubConnectionState.Connected) {
                        try {
                            await this._sendMessage(this._cachedPingMessage);
                        }
                        catch {
                            // We don't care about the error. It should be seen elsewhere in the client.
                            // The connection is probably in a bad or closed state now, cleanup the timer so it stops triggering
                            this._cleanupPingTimer();
                        }
                    }
                }, nextPing);
            }
        }
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    serverTimeout() {
        // The server hasn't talked to us in a while. It doesn't like us anymore ... :(
        // Terminate the connection, but we don't need to wait on the promise. This could trigger reconnecting.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.connection.stop(new Error("Server timeout elapsed without receiving a message from the server."));
    }
    async _invokeClientMethod(invocationMessage) {
        const methodName = invocationMessage.target.toLowerCase();
        const methods = this._methods[methodName];
        if (!methods) {
            this._logger.log(LogLevel.Warning, `No client method with the name '${methodName}' found.`);
            // No handlers provided by client but the server is expecting a response still, so we send an error
            if (invocationMessage.invocationId) {
                this._logger.log(LogLevel.Warning, `No result given for '${methodName}' method and invocation ID '${invocationMessage.invocationId}'.`);
                await this._sendWithProtocol(this._createCompletionMessage(invocationMessage.invocationId, "Client didn't provide a result.", null));
            }
            return;
        }
        // Avoid issues with handlers removing themselves thus modifying the list while iterating through it
        const methodsCopy = methods.slice();
        // Server expects a response
        const expectsResponse = invocationMessage.invocationId ? true : false;
        // We preserve the last result or exception but still call all handlers
        let res;
        let exception;
        let completionMessage;
        for (const m of methodsCopy) {
            try {
                const prevRes = res;
                res = await m.apply(this, invocationMessage.arguments);
                if (expectsResponse && res && prevRes) {
                    this._logger.log(LogLevel.Error, `Multiple results provided for '${methodName}'. Sending error to server.`);
                    completionMessage = this._createCompletionMessage(invocationMessage.invocationId, `Client provided multiple results.`, null);
                }
                // Ignore exception if we got a result after, the exception will be logged
                exception = undefined;
            }
            catch (e) {
                exception = e;
                this._logger.log(LogLevel.Error, `A callback for the method '${methodName}' threw error '${e}'.`);
            }
        }
        if (completionMessage) {
            await this._sendWithProtocol(completionMessage);
        }
        else if (expectsResponse) {
            // If there is an exception that means either no result was given or a handler after a result threw
            if (exception) {
                completionMessage = this._createCompletionMessage(invocationMessage.invocationId, `${exception}`, null);
            }
            else if (res !== undefined) {
                completionMessage = this._createCompletionMessage(invocationMessage.invocationId, null, res);
            }
            else {
                this._logger.log(LogLevel.Warning, `No result given for '${methodName}' method and invocation ID '${invocationMessage.invocationId}'.`);
                // Client didn't provide a result or throw from a handler, server expects a response so we send an error
                completionMessage = this._createCompletionMessage(invocationMessage.invocationId, "Client didn't provide a result.", null);
            }
            await this._sendWithProtocol(completionMessage);
        }
        else {
            if (res) {
                this._logger.log(LogLevel.Error, `Result given for '${methodName}' method but server is not expecting a result.`);
            }
        }
    }
    _connectionClosed(error) {
        this._logger.log(LogLevel.Debug, `HubConnection.connectionClosed(${error}) called while in state ${this._connectionState}.`);
        // Triggering this.handshakeRejecter is insufficient because it could already be resolved without the continuation having run yet.
        this._stopDuringStartError = this._stopDuringStartError || error || new AbortError("The underlying connection was closed before the hub handshake could complete.");
        // If the handshake is in progress, start will be waiting for the handshake promise, so we complete it.
        // If it has already completed, this should just noop.
        if (this._handshakeResolver) {
            this._handshakeResolver();
        }
        this._cancelCallbacksWithError(error || new Error("Invocation canceled due to the underlying connection being closed."));
        this._cleanupTimeout();
        this._cleanupPingTimer();
        if (this._connectionState === HubConnectionState.Disconnecting) {
            this._completeClose(error);
        }
        else if (this._connectionState === HubConnectionState.Connected && this._reconnectPolicy) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this._reconnect(error);
        }
        else if (this._connectionState === HubConnectionState.Connected) {
            this._completeClose(error);
        }
        // If none of the above if conditions were true were called the HubConnection must be in either:
        // 1. The Connecting state in which case the handshakeResolver will complete it and stopDuringStartError will fail it.
        // 2. The Reconnecting state in which case the handshakeResolver will complete it and stopDuringStartError will fail the current reconnect attempt
        //    and potentially continue the reconnect() loop.
        // 3. The Disconnected state in which case we're already done.
    }
    _completeClose(error) {
        if (this._connectionStarted) {
            this._connectionState = HubConnectionState.Disconnected;
            this._connectionStarted = false;
            if (Platform.isBrowser) {
                window.document.removeEventListener("freeze", this._freezeEventListener);
            }
            try {
                this._closedCallbacks.forEach((c) => c.apply(this, [error]));
            }
            catch (e) {
                this._logger.log(LogLevel.Error, `An onclose callback called with error '${error}' threw error '${e}'.`);
            }
        }
    }
    async _reconnect(error) {
        const reconnectStartTime = Date.now();
        let previousReconnectAttempts = 0;
        let retryError = error !== undefined ? error : new Error("Attempting to reconnect due to a unknown error.");
        let nextRetryDelay = this._getNextRetryDelay(previousReconnectAttempts++, 0, retryError);
        if (nextRetryDelay === null) {
            this._logger.log(LogLevel.Debug, "Connection not reconnecting because the IRetryPolicy returned null on the first reconnect attempt.");
            this._completeClose(error);
            return;
        }
        this._connectionState = HubConnectionState.Reconnecting;
        if (error) {
            this._logger.log(LogLevel.Information, `Connection reconnecting because of error '${error}'.`);
        }
        else {
            this._logger.log(LogLevel.Information, "Connection reconnecting.");
        }
        if (this._reconnectingCallbacks.length !== 0) {
            try {
                this._reconnectingCallbacks.forEach((c) => c.apply(this, [error]));
            }
            catch (e) {
                this._logger.log(LogLevel.Error, `An onreconnecting callback called with error '${error}' threw error '${e}'.`);
            }
            // Exit early if an onreconnecting callback called connection.stop().
            if (this._connectionState !== HubConnectionState.Reconnecting) {
                this._logger.log(LogLevel.Debug, "Connection left the reconnecting state in onreconnecting callback. Done reconnecting.");
                return;
            }
        }
        while (nextRetryDelay !== null) {
            this._logger.log(LogLevel.Information, `Reconnect attempt number ${previousReconnectAttempts} will start in ${nextRetryDelay} ms.`);
            await new Promise((resolve) => {
                this._reconnectDelayHandle = setTimeout(resolve, nextRetryDelay);
            });
            this._reconnectDelayHandle = undefined;
            if (this._connectionState !== HubConnectionState.Reconnecting) {
                this._logger.log(LogLevel.Debug, "Connection left the reconnecting state during reconnect delay. Done reconnecting.");
                return;
            }
            try {
                await this._startInternal();
                this._connectionState = HubConnectionState.Connected;
                this._logger.log(LogLevel.Information, "HubConnection reconnected successfully.");
                if (this._reconnectedCallbacks.length !== 0) {
                    try {
                        this._reconnectedCallbacks.forEach((c) => c.apply(this, [this.connection.connectionId]));
                    }
                    catch (e) {
                        this._logger.log(LogLevel.Error, `An onreconnected callback called with connectionId '${this.connection.connectionId}; threw error '${e}'.`);
                    }
                }
                return;
            }
            catch (e) {
                this._logger.log(LogLevel.Information, `Reconnect attempt failed because of error '${e}'.`);
                if (this._connectionState !== HubConnectionState.Reconnecting) {
                    this._logger.log(LogLevel.Debug, `Connection moved to the '${this._connectionState}' from the reconnecting state during reconnect attempt. Done reconnecting.`);
                    // The TypeScript compiler thinks that connectionState must be Connected here. The TypeScript compiler is wrong.
                    if (this._connectionState === HubConnectionState.Disconnecting) {
                        this._completeClose();
                    }
                    return;
                }
                retryError = e instanceof Error ? e : new Error(e.toString());
                nextRetryDelay = this._getNextRetryDelay(previousReconnectAttempts++, Date.now() - reconnectStartTime, retryError);
            }
        }
        this._logger.log(LogLevel.Information, `Reconnect retries have been exhausted after ${Date.now() - reconnectStartTime} ms and ${previousReconnectAttempts} failed attempts. Connection disconnecting.`);
        this._completeClose();
    }
    _getNextRetryDelay(previousRetryCount, elapsedMilliseconds, retryReason) {
        try {
            return this._reconnectPolicy.nextRetryDelayInMilliseconds({
                elapsedMilliseconds,
                previousRetryCount,
                retryReason,
            });
        }
        catch (e) {
            this._logger.log(LogLevel.Error, `IRetryPolicy.nextRetryDelayInMilliseconds(${previousRetryCount}, ${elapsedMilliseconds}) threw error '${e}'.`);
            return null;
        }
    }
    _cancelCallbacksWithError(error) {
        const callbacks = this._callbacks;
        this._callbacks = {};
        Object.keys(callbacks)
            .forEach((key) => {
            const callback = callbacks[key];
            try {
                callback(null, error);
            }
            catch (e) {
                this._logger.log(LogLevel.Error, `Stream 'error' callback called with '${error}' threw error: ${getErrorString(e)}`);
            }
        });
    }
    _cleanupPingTimer() {
        if (this._pingServerHandle) {
            clearTimeout(this._pingServerHandle);
            this._pingServerHandle = undefined;
        }
    }
    _cleanupTimeout() {
        if (this._timeoutHandle) {
            clearTimeout(this._timeoutHandle);
        }
    }
    _createInvocation(methodName, args, nonblocking, streamIds) {
        if (nonblocking) {
            if (streamIds.length !== 0) {
                return {
                    arguments: args,
                    streamIds,
                    target: methodName,
                    type: MessageType.Invocation,
                };
            }
            else {
                return {
                    arguments: args,
                    target: methodName,
                    type: MessageType.Invocation,
                };
            }
        }
        else {
            const invocationId = this._invocationId;
            this._invocationId++;
            if (streamIds.length !== 0) {
                return {
                    arguments: args,
                    invocationId: invocationId.toString(),
                    streamIds,
                    target: methodName,
                    type: MessageType.Invocation,
                };
            }
            else {
                return {
                    arguments: args,
                    invocationId: invocationId.toString(),
                    target: methodName,
                    type: MessageType.Invocation,
                };
            }
        }
    }
    _launchStreams(streams, promiseQueue) {
        if (streams.length === 0) {
            return;
        }
        // Synchronize stream data so they arrive in-order on the server
        if (!promiseQueue) {
            promiseQueue = Promise.resolve();
        }
        // We want to iterate over the keys, since the keys are the stream ids
        // eslint-disable-next-line guard-for-in
        for (const streamId in streams) {
            streams[streamId].subscribe({
                complete: () => {
                    promiseQueue = promiseQueue.then(() => this._sendWithProtocol(this._createCompletionMessage(streamId)));
                },
                error: (err) => {
                    let message;
                    if (err instanceof Error) {
                        message = err.message;
                    }
                    else if (err && err.toString) {
                        message = err.toString();
                    }
                    else {
                        message = "Unknown error";
                    }
                    promiseQueue = promiseQueue.then(() => this._sendWithProtocol(this._createCompletionMessage(streamId, message)));
                },
                next: (item) => {
                    promiseQueue = promiseQueue.then(() => this._sendWithProtocol(this._createStreamItemMessage(streamId, item)));
                },
            });
        }
    }
    _replaceStreamingParams(args) {
        const streams = [];
        const streamIds = [];
        for (let i = 0; i < args.length; i++) {
            const argument = args[i];
            if (this._isObservable(argument)) {
                const streamId = this._invocationId;
                this._invocationId++;
                // Store the stream for later use
                streams[streamId] = argument;
                streamIds.push(streamId.toString());
                // remove stream from args
                args.splice(i, 1);
            }
        }
        return [streams, streamIds];
    }
    _isObservable(arg) {
        // This allows other stream implementations to just work (like rxjs)
        return arg && arg.subscribe && typeof arg.subscribe === "function";
    }
    _createStreamInvocation(methodName, args, streamIds) {
        const invocationId = this._invocationId;
        this._invocationId++;
        if (streamIds.length !== 0) {
            return {
                arguments: args,
                invocationId: invocationId.toString(),
                streamIds,
                target: methodName,
                type: MessageType.StreamInvocation,
            };
        }
        else {
            return {
                arguments: args,
                invocationId: invocationId.toString(),
                target: methodName,
                type: MessageType.StreamInvocation,
            };
        }
    }
    _createCancelInvocation(id) {
        return {
            invocationId: id,
            type: MessageType.CancelInvocation,
        };
    }
    _createStreamItemMessage(id, item) {
        return {
            invocationId: id,
            item,
            type: MessageType.StreamItem,
        };
    }
    _createCompletionMessage(id, error, result) {
        if (error) {
            return {
                error,
                invocationId: id,
                type: MessageType.Completion,
            };
        }
        return {
            invocationId: id,
            result,
            type: MessageType.Completion,
        };
    }
}

// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.
// 0, 2, 10, 30 second delays before reconnect attempts.
const DEFAULT_RETRY_DELAYS_IN_MILLISECONDS = [0, 2000, 10000, 30000, null];
/** @private */
class DefaultReconnectPolicy {
    constructor(retryDelays) {
        this._retryDelays = retryDelays !== undefined ? [...retryDelays, null] : DEFAULT_RETRY_DELAYS_IN_MILLISECONDS;
    }
    nextRetryDelayInMilliseconds(retryContext) {
        return this._retryDelays[retryContext.previousRetryCount];
    }
}

// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.
class HeaderNames {
}
HeaderNames.Authorization = "Authorization";
HeaderNames.Cookie = "Cookie";

// Licensed to the .NET Foundation under one or more agreements.
/** @private */
class AccessTokenHttpClient extends HttpClient {
    constructor(innerClient, accessTokenFactory) {
        super();
        this._innerClient = innerClient;
        this._accessTokenFactory = accessTokenFactory;
    }
    async send(request) {
        let allowRetry = true;
        if (this._accessTokenFactory && (!this._accessToken || (request.url && request.url.indexOf("/negotiate?") > 0))) {
            // don't retry if the request is a negotiate or if we just got a potentially new token from the access token factory
            allowRetry = false;
            this._accessToken = await this._accessTokenFactory();
        }
        this._setAuthorizationHeader(request);
        const response = await this._innerClient.send(request);
        if (allowRetry && response.statusCode === 401 && this._accessTokenFactory) {
            this._accessToken = await this._accessTokenFactory();
            this._setAuthorizationHeader(request);
            return await this._innerClient.send(request);
        }
        return response;
    }
    _setAuthorizationHeader(request) {
        if (!request.headers) {
            request.headers = {};
        }
        if (this._accessToken) {
            request.headers[HeaderNames.Authorization] = `Bearer ${this._accessToken}`;
        }
        // don't remove the header if there isn't an access token factory, the user manually added the header in this case
        else if (this._accessTokenFactory) {
            if (request.headers[HeaderNames.Authorization]) {
                delete request.headers[HeaderNames.Authorization];
            }
        }
    }
    getCookieString(url) {
        return this._innerClient.getCookieString(url);
    }
}

// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.
// This will be treated as a bit flag in the future, so we keep it using power-of-two values.
/** Specifies a specific HTTP transport type. */
var HttpTransportType;
(function (HttpTransportType) {
    /** Specifies no transport preference. */
    HttpTransportType[HttpTransportType["None"] = 0] = "None";
    /** Specifies the WebSockets transport. */
    HttpTransportType[HttpTransportType["WebSockets"] = 1] = "WebSockets";
    /** Specifies the Server-Sent Events transport. */
    HttpTransportType[HttpTransportType["ServerSentEvents"] = 2] = "ServerSentEvents";
    /** Specifies the Long Polling transport. */
    HttpTransportType[HttpTransportType["LongPolling"] = 4] = "LongPolling";
})(HttpTransportType || (HttpTransportType = {}));
/** Specifies the transfer format for a connection. */
var TransferFormat;
(function (TransferFormat) {
    /** Specifies that only text data will be transmitted over the connection. */
    TransferFormat[TransferFormat["Text"] = 1] = "Text";
    /** Specifies that binary data will be transmitted over the connection. */
    TransferFormat[TransferFormat["Binary"] = 2] = "Binary";
})(TransferFormat || (TransferFormat = {}));

// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.
// Rough polyfill of https://developer.mozilla.org/en-US/docs/Web/API/AbortController
// We don't actually ever use the API being polyfilled, we always use the polyfill because
// it's a very new API right now.
// Not exported from index.
/** @private */
class AbortController$1 {
    constructor() {
        this._isAborted = false;
        this.onabort = null;
    }
    abort() {
        if (!this._isAborted) {
            this._isAborted = true;
            if (this.onabort) {
                this.onabort();
            }
        }
    }
    get signal() {
        return this;
    }
    get aborted() {
        return this._isAborted;
    }
}

// Licensed to the .NET Foundation under one or more agreements.
// Not exported from 'index', this type is internal.
/** @private */
class LongPollingTransport {
    constructor(httpClient, logger, options) {
        this._httpClient = httpClient;
        this._logger = logger;
        this._pollAbort = new AbortController$1();
        this._options = options;
        this._running = false;
        this.onreceive = null;
        this.onclose = null;
    }
    // This is an internal type, not exported from 'index' so this is really just internal.
    get pollAborted() {
        return this._pollAbort.aborted;
    }
    async connect(url, transferFormat) {
        Arg.isRequired(url, "url");
        Arg.isRequired(transferFormat, "transferFormat");
        Arg.isIn(transferFormat, TransferFormat, "transferFormat");
        this._url = url;
        this._logger.log(LogLevel.Trace, "(LongPolling transport) Connecting.");
        // Allow binary format on Node and Browsers that support binary content (indicated by the presence of responseType property)
        if (transferFormat === TransferFormat.Binary &&
            (typeof XMLHttpRequest !== "undefined" && typeof new XMLHttpRequest().responseType !== "string")) {
            throw new Error("Binary protocols over XmlHttpRequest not implementing advanced features are not supported.");
        }
        const [name, value] = getUserAgentHeader();
        const headers = { [name]: value, ...this._options.headers };
        const pollOptions = {
            abortSignal: this._pollAbort.signal,
            headers,
            timeout: 100000,
            withCredentials: this._options.withCredentials,
        };
        if (transferFormat === TransferFormat.Binary) {
            pollOptions.responseType = "arraybuffer";
        }
        // Make initial long polling request
        // Server uses first long polling request to finish initializing connection and it returns without data
        const pollUrl = `${url}&_=${Date.now()}`;
        this._logger.log(LogLevel.Trace, `(LongPolling transport) polling: ${pollUrl}.`);
        const response = await this._httpClient.get(pollUrl, pollOptions);
        if (response.statusCode !== 200) {
            this._logger.log(LogLevel.Error, `(LongPolling transport) Unexpected response code: ${response.statusCode}.`);
            // Mark running as false so that the poll immediately ends and runs the close logic
            this._closeError = new HttpError(response.statusText || "", response.statusCode);
            this._running = false;
        }
        else {
            this._running = true;
        }
        this._receiving = this._poll(this._url, pollOptions);
    }
    async _poll(url, pollOptions) {
        try {
            while (this._running) {
                try {
                    const pollUrl = `${url}&_=${Date.now()}`;
                    this._logger.log(LogLevel.Trace, `(LongPolling transport) polling: ${pollUrl}.`);
                    const response = await this._httpClient.get(pollUrl, pollOptions);
                    if (response.statusCode === 204) {
                        this._logger.log(LogLevel.Information, "(LongPolling transport) Poll terminated by server.");
                        this._running = false;
                    }
                    else if (response.statusCode !== 200) {
                        this._logger.log(LogLevel.Error, `(LongPolling transport) Unexpected response code: ${response.statusCode}.`);
                        // Unexpected status code
                        this._closeError = new HttpError(response.statusText || "", response.statusCode);
                        this._running = false;
                    }
                    else {
                        // Process the response
                        if (response.content) {
                            this._logger.log(LogLevel.Trace, `(LongPolling transport) data received. ${getDataDetail(response.content, this._options.logMessageContent)}.`);
                            if (this.onreceive) {
                                this.onreceive(response.content);
                            }
                        }
                        else {
                            // This is another way timeout manifest.
                            this._logger.log(LogLevel.Trace, "(LongPolling transport) Poll timed out, reissuing.");
                        }
                    }
                }
                catch (e) {
                    if (!this._running) {
                        // Log but disregard errors that occur after stopping
                        this._logger.log(LogLevel.Trace, `(LongPolling transport) Poll errored after shutdown: ${e.message}`);
                    }
                    else {
                        if (e instanceof TimeoutError) {
                            // Ignore timeouts and reissue the poll.
                            this._logger.log(LogLevel.Trace, "(LongPolling transport) Poll timed out, reissuing.");
                        }
                        else {
                            // Close the connection with the error as the result.
                            this._closeError = e;
                            this._running = false;
                        }
                    }
                }
            }
        }
        finally {
            this._logger.log(LogLevel.Trace, "(LongPolling transport) Polling complete.");
            // We will reach here with pollAborted==false when the server returned a response causing the transport to stop.
            // If pollAborted==true then client initiated the stop and the stop method will raise the close event after DELETE is sent.
            if (!this.pollAborted) {
                this._raiseOnClose();
            }
        }
    }
    async send(data) {
        if (!this._running) {
            return Promise.reject(new Error("Cannot send until the transport is connected"));
        }
        return sendMessage(this._logger, "LongPolling", this._httpClient, this._url, data, this._options);
    }
    async stop() {
        this._logger.log(LogLevel.Trace, "(LongPolling transport) Stopping polling.");
        // Tell receiving loop to stop, abort any current request, and then wait for it to finish
        this._running = false;
        this._pollAbort.abort();
        try {
            await this._receiving;
            // Send DELETE to clean up long polling on the server
            this._logger.log(LogLevel.Trace, `(LongPolling transport) sending DELETE request to ${this._url}.`);
            const headers = {};
            const [name, value] = getUserAgentHeader();
            headers[name] = value;
            const deleteOptions = {
                headers: { ...headers, ...this._options.headers },
                timeout: this._options.timeout,
                withCredentials: this._options.withCredentials,
            };
            await this._httpClient.delete(this._url, deleteOptions);
            this._logger.log(LogLevel.Trace, "(LongPolling transport) DELETE request sent.");
        }
        finally {
            this._logger.log(LogLevel.Trace, "(LongPolling transport) Stop finished.");
            // Raise close event here instead of in polling
            // It needs to happen after the DELETE request is sent
            this._raiseOnClose();
        }
    }
    _raiseOnClose() {
        if (this.onclose) {
            let logMessage = "(LongPolling transport) Firing onclose event.";
            if (this._closeError) {
                logMessage += " Error: " + this._closeError;
            }
            this._logger.log(LogLevel.Trace, logMessage);
            this.onclose(this._closeError);
        }
    }
}

// Licensed to the .NET Foundation under one or more agreements.
/** @private */
class ServerSentEventsTransport {
    constructor(httpClient, accessToken, logger, options) {
        this._httpClient = httpClient;
        this._accessToken = accessToken;
        this._logger = logger;
        this._options = options;
        this.onreceive = null;
        this.onclose = null;
    }
    async connect(url, transferFormat) {
        Arg.isRequired(url, "url");
        Arg.isRequired(transferFormat, "transferFormat");
        Arg.isIn(transferFormat, TransferFormat, "transferFormat");
        this._logger.log(LogLevel.Trace, "(SSE transport) Connecting.");
        // set url before accessTokenFactory because this._url is only for send and we set the auth header instead of the query string for send
        this._url = url;
        if (this._accessToken) {
            url += (url.indexOf("?") < 0 ? "?" : "&") + `access_token=${encodeURIComponent(this._accessToken)}`;
        }
        return new Promise((resolve, reject) => {
            let opened = false;
            if (transferFormat !== TransferFormat.Text) {
                reject(new Error("The Server-Sent Events transport only supports the 'Text' transfer format"));
                return;
            }
            let eventSource;
            if (Platform.isBrowser || Platform.isWebWorker) {
                eventSource = new this._options.EventSource(url, { withCredentials: this._options.withCredentials });
            }
            else {
                // Non-browser passes cookies via the dictionary
                const cookies = this._httpClient.getCookieString(url);
                const headers = {};
                headers.Cookie = cookies;
                const [name, value] = getUserAgentHeader();
                headers[name] = value;
                eventSource = new this._options.EventSource(url, { withCredentials: this._options.withCredentials, headers: { ...headers, ...this._options.headers } });
            }
            try {
                eventSource.onmessage = (e) => {
                    if (this.onreceive) {
                        try {
                            this._logger.log(LogLevel.Trace, `(SSE transport) data received. ${getDataDetail(e.data, this._options.logMessageContent)}.`);
                            this.onreceive(e.data);
                        }
                        catch (error) {
                            this._close(error);
                            return;
                        }
                    }
                };
                // @ts-ignore: not using event on purpose
                eventSource.onerror = (e) => {
                    // EventSource doesn't give any useful information about server side closes.
                    if (opened) {
                        this._close();
                    }
                    else {
                        reject(new Error("EventSource failed to connect. The connection could not be found on the server,"
                            + " either the connection ID is not present on the server, or a proxy is refusing/buffering the connection."
                            + " If you have multiple servers check that sticky sessions are enabled."));
                    }
                };
                eventSource.onopen = () => {
                    this._logger.log(LogLevel.Information, `SSE connected to ${this._url}`);
                    this._eventSource = eventSource;
                    opened = true;
                    resolve();
                };
            }
            catch (e) {
                reject(e);
                return;
            }
        });
    }
    async send(data) {
        if (!this._eventSource) {
            return Promise.reject(new Error("Cannot send until the transport is connected"));
        }
        return sendMessage(this._logger, "SSE", this._httpClient, this._url, data, this._options);
    }
    stop() {
        this._close();
        return Promise.resolve();
    }
    _close(e) {
        if (this._eventSource) {
            this._eventSource.close();
            this._eventSource = undefined;
            if (this.onclose) {
                this.onclose(e);
            }
        }
    }
}

// Licensed to the .NET Foundation under one or more agreements.
/** @private */
class WebSocketTransport {
    constructor(httpClient, accessTokenFactory, logger, logMessageContent, webSocketConstructor, headers) {
        this._logger = logger;
        this._accessTokenFactory = accessTokenFactory;
        this._logMessageContent = logMessageContent;
        this._webSocketConstructor = webSocketConstructor;
        this._httpClient = httpClient;
        this.onreceive = null;
        this.onclose = null;
        this._headers = headers;
    }
    async connect(url, transferFormat) {
        Arg.isRequired(url, "url");
        Arg.isRequired(transferFormat, "transferFormat");
        Arg.isIn(transferFormat, TransferFormat, "transferFormat");
        this._logger.log(LogLevel.Trace, "(WebSockets transport) Connecting.");
        let token;
        if (this._accessTokenFactory) {
            token = await this._accessTokenFactory();
        }
        return new Promise((resolve, reject) => {
            url = url.replace(/^http/, "ws");
            let webSocket;
            const cookies = this._httpClient.getCookieString(url);
            let opened = false;
            if (Platform.isNode || Platform.isReactNative) {
                const headers = {};
                const [name, value] = getUserAgentHeader();
                headers[name] = value;
                if (token) {
                    headers[HeaderNames.Authorization] = `Bearer ${token}`;
                }
                if (cookies) {
                    headers[HeaderNames.Cookie] = cookies;
                }
                // Only pass headers when in non-browser environments
                webSocket = new this._webSocketConstructor(url, undefined, {
                    headers: { ...headers, ...this._headers },
                });
            }
            else {
                if (token) {
                    url += (url.indexOf("?") < 0 ? "?" : "&") + `access_token=${encodeURIComponent(token)}`;
                }
            }
            if (!webSocket) {
                // Chrome is not happy with passing 'undefined' as protocol
                webSocket = new this._webSocketConstructor(url);
            }
            if (transferFormat === TransferFormat.Binary) {
                webSocket.binaryType = "arraybuffer";
            }
            webSocket.onopen = (_event) => {
                this._logger.log(LogLevel.Information, `WebSocket connected to ${url}.`);
                this._webSocket = webSocket;
                opened = true;
                resolve();
            };
            webSocket.onerror = (event) => {
                let error = null;
                // ErrorEvent is a browser only type we need to check if the type exists before using it
                if (typeof ErrorEvent !== "undefined" && event instanceof ErrorEvent) {
                    error = event.error;
                }
                else {
                    error = "There was an error with the transport";
                }
                this._logger.log(LogLevel.Information, `(WebSockets transport) ${error}.`);
            };
            webSocket.onmessage = (message) => {
                this._logger.log(LogLevel.Trace, `(WebSockets transport) data received. ${getDataDetail(message.data, this._logMessageContent)}.`);
                if (this.onreceive) {
                    try {
                        this.onreceive(message.data);
                    }
                    catch (error) {
                        this._close(error);
                        return;
                    }
                }
            };
            webSocket.onclose = (event) => {
                // Don't call close handler if connection was never established
                // We'll reject the connect call instead
                if (opened) {
                    this._close(event);
                }
                else {
                    let error = null;
                    // ErrorEvent is a browser only type we need to check if the type exists before using it
                    if (typeof ErrorEvent !== "undefined" && event instanceof ErrorEvent) {
                        error = event.error;
                    }
                    else {
                        error = "WebSocket failed to connect. The connection could not be found on the server,"
                            + " either the endpoint may not be a SignalR endpoint,"
                            + " the connection ID is not present on the server, or there is a proxy blocking WebSockets."
                            + " If you have multiple servers check that sticky sessions are enabled.";
                    }
                    reject(new Error(error));
                }
            };
        });
    }
    send(data) {
        if (this._webSocket && this._webSocket.readyState === this._webSocketConstructor.OPEN) {
            this._logger.log(LogLevel.Trace, `(WebSockets transport) sending data. ${getDataDetail(data, this._logMessageContent)}.`);
            this._webSocket.send(data);
            return Promise.resolve();
        }
        return Promise.reject("WebSocket is not in the OPEN state");
    }
    stop() {
        if (this._webSocket) {
            // Manually invoke onclose callback inline so we know the HttpConnection was closed properly before returning
            // This also solves an issue where websocket.onclose could take 18+ seconds to trigger during network disconnects
            this._close(undefined);
        }
        return Promise.resolve();
    }
    _close(event) {
        // webSocket will be null if the transport did not start successfully
        if (this._webSocket) {
            // Clear websocket handlers because we are considering the socket closed now
            this._webSocket.onclose = () => { };
            this._webSocket.onmessage = () => { };
            this._webSocket.onerror = () => { };
            this._webSocket.close();
            this._webSocket = undefined;
        }
        this._logger.log(LogLevel.Trace, "(WebSockets transport) socket closed.");
        if (this.onclose) {
            if (this._isCloseEvent(event) && (event.wasClean === false || event.code !== 1000)) {
                this.onclose(new Error(`WebSocket closed with status code: ${event.code} (${event.reason || "no reason given"}).`));
            }
            else if (event instanceof Error) {
                this.onclose(event);
            }
            else {
                this.onclose();
            }
        }
    }
    _isCloseEvent(event) {
        return event && typeof event.wasClean === "boolean" && typeof event.code === "number";
    }
}

// Licensed to the .NET Foundation under one or more agreements.
const MAX_REDIRECTS = 100;
/** @private */
class HttpConnection {
    constructor(url, options = {}) {
        this._stopPromiseResolver = () => { };
        this.features = {};
        this._negotiateVersion = 1;
        Arg.isRequired(url, "url");
        this._logger = createLogger(options.logger);
        this.baseUrl = this._resolveUrl(url);
        options = options || {};
        options.logMessageContent = options.logMessageContent === undefined ? false : options.logMessageContent;
        if (typeof options.withCredentials === "boolean" || options.withCredentials === undefined) {
            options.withCredentials = options.withCredentials === undefined ? true : options.withCredentials;
        }
        else {
            throw new Error("withCredentials option was not a 'boolean' or 'undefined' value");
        }
        options.timeout = options.timeout === undefined ? 100 * 1000 : options.timeout;
        let webSocketModule = null;
        let eventSourceModule = null;
        if (Platform.isNode && typeof require !== "undefined") {
            // In order to ignore the dynamic require in webpack builds we need to do this magic
            // @ts-ignore: TS doesn't know about these names
            const requireFunc = typeof __webpack_require__ === "function" ? __non_webpack_require__ : require;
            webSocketModule = requireFunc("ws");
            eventSourceModule = requireFunc("eventsource");
        }
        if (!Platform.isNode && typeof WebSocket !== "undefined" && !options.WebSocket) {
            options.WebSocket = WebSocket;
        }
        else if (Platform.isNode && !options.WebSocket) {
            if (webSocketModule) {
                options.WebSocket = webSocketModule;
            }
        }
        if (!Platform.isNode && typeof EventSource !== "undefined" && !options.EventSource) {
            options.EventSource = EventSource;
        }
        else if (Platform.isNode && !options.EventSource) {
            if (typeof eventSourceModule !== "undefined") {
                options.EventSource = eventSourceModule;
            }
        }
        this._httpClient = new AccessTokenHttpClient(options.httpClient || new DefaultHttpClient(this._logger), options.accessTokenFactory);
        this._connectionState = "Disconnected" /* Disconnected */;
        this._connectionStarted = false;
        this._options = options;
        this.onreceive = null;
        this.onclose = null;
    }
    async start(transferFormat) {
        transferFormat = transferFormat || TransferFormat.Binary;
        Arg.isIn(transferFormat, TransferFormat, "transferFormat");
        this._logger.log(LogLevel.Debug, `Starting connection with transfer format '${TransferFormat[transferFormat]}'.`);
        if (this._connectionState !== "Disconnected" /* Disconnected */) {
            return Promise.reject(new Error("Cannot start an HttpConnection that is not in the 'Disconnected' state."));
        }
        this._connectionState = "Connecting" /* Connecting */;
        this._startInternalPromise = this._startInternal(transferFormat);
        await this._startInternalPromise;
        // The TypeScript compiler thinks that connectionState must be Connecting here. The TypeScript compiler is wrong.
        if (this._connectionState === "Disconnecting" /* Disconnecting */) {
            // stop() was called and transitioned the client into the Disconnecting state.
            const message = "Failed to start the HttpConnection before stop() was called.";
            this._logger.log(LogLevel.Error, message);
            // We cannot await stopPromise inside startInternal since stopInternal awaits the startInternalPromise.
            await this._stopPromise;
            return Promise.reject(new AbortError(message));
        }
        else if (this._connectionState !== "Connected" /* Connected */) {
            // stop() was called and transitioned the client into the Disconnecting state.
            const message = "HttpConnection.startInternal completed gracefully but didn't enter the connection into the connected state!";
            this._logger.log(LogLevel.Error, message);
            return Promise.reject(new AbortError(message));
        }
        this._connectionStarted = true;
    }
    send(data) {
        if (this._connectionState !== "Connected" /* Connected */) {
            return Promise.reject(new Error("Cannot send data if the connection is not in the 'Connected' State."));
        }
        if (!this._sendQueue) {
            this._sendQueue = new TransportSendQueue(this.transport);
        }
        // Transport will not be null if state is connected
        return this._sendQueue.send(data);
    }
    async stop(error) {
        if (this._connectionState === "Disconnected" /* Disconnected */) {
            this._logger.log(LogLevel.Debug, `Call to HttpConnection.stop(${error}) ignored because the connection is already in the disconnected state.`);
            return Promise.resolve();
        }
        if (this._connectionState === "Disconnecting" /* Disconnecting */) {
            this._logger.log(LogLevel.Debug, `Call to HttpConnection.stop(${error}) ignored because the connection is already in the disconnecting state.`);
            return this._stopPromise;
        }
        this._connectionState = "Disconnecting" /* Disconnecting */;
        this._stopPromise = new Promise((resolve) => {
            // Don't complete stop() until stopConnection() completes.
            this._stopPromiseResolver = resolve;
        });
        // stopInternal should never throw so just observe it.
        await this._stopInternal(error);
        await this._stopPromise;
    }
    async _stopInternal(error) {
        // Set error as soon as possible otherwise there is a race between
        // the transport closing and providing an error and the error from a close message
        // We would prefer the close message error.
        this._stopError = error;
        try {
            await this._startInternalPromise;
        }
        catch (e) {
            // This exception is returned to the user as a rejected Promise from the start method.
        }
        // The transport's onclose will trigger stopConnection which will run our onclose event.
        // The transport should always be set if currently connected. If it wasn't set, it's likely because
        // stop was called during start() and start() failed.
        if (this.transport) {
            try {
                await this.transport.stop();
            }
            catch (e) {
                this._logger.log(LogLevel.Error, `HttpConnection.transport.stop() threw error '${e}'.`);
                this._stopConnection();
            }
            this.transport = undefined;
        }
        else {
            this._logger.log(LogLevel.Debug, "HttpConnection.transport is undefined in HttpConnection.stop() because start() failed.");
        }
    }
    async _startInternal(transferFormat) {
        // Store the original base url and the access token factory since they may change
        // as part of negotiating
        let url = this.baseUrl;
        this._accessTokenFactory = this._options.accessTokenFactory;
        this._httpClient._accessTokenFactory = this._accessTokenFactory;
        try {
            if (this._options.skipNegotiation) {
                if (this._options.transport === HttpTransportType.WebSockets) {
                    // No need to add a connection ID in this case
                    this.transport = this._constructTransport(HttpTransportType.WebSockets);
                    // We should just call connect directly in this case.
                    // No fallback or negotiate in this case.
                    await this._startTransport(url, transferFormat);
                }
                else {
                    throw new Error("Negotiation can only be skipped when using the WebSocket transport directly.");
                }
            }
            else {
                let negotiateResponse = null;
                let redirects = 0;
                do {
                    negotiateResponse = await this._getNegotiationResponse(url);
                    // the user tries to stop the connection when it is being started
                    if (this._connectionState === "Disconnecting" /* Disconnecting */ || this._connectionState === "Disconnected" /* Disconnected */) {
                        throw new AbortError("The connection was stopped during negotiation.");
                    }
                    if (negotiateResponse.error) {
                        throw new Error(negotiateResponse.error);
                    }
                    if (negotiateResponse.ProtocolVersion) {
                        throw new Error("Detected a connection attempt to an ASP.NET SignalR Server. This client only supports connecting to an ASP.NET Core SignalR Server. See https://aka.ms/signalr-core-differences for details.");
                    }
                    if (negotiateResponse.url) {
                        url = negotiateResponse.url;
                    }
                    if (negotiateResponse.accessToken) {
                        // Replace the current access token factory with one that uses
                        // the returned access token
                        const accessToken = negotiateResponse.accessToken;
                        this._accessTokenFactory = () => accessToken;
                        // set the factory to undefined so the AccessTokenHttpClient won't retry with the same token, since we know it won't change until a connection restart
                        this._httpClient._accessToken = accessToken;
                        this._httpClient._accessTokenFactory = undefined;
                    }
                    redirects++;
                } while (negotiateResponse.url && redirects < MAX_REDIRECTS);
                if (redirects === MAX_REDIRECTS && negotiateResponse.url) {
                    throw new Error("Negotiate redirection limit exceeded.");
                }
                await this._createTransport(url, this._options.transport, negotiateResponse, transferFormat);
            }
            if (this.transport instanceof LongPollingTransport) {
                this.features.inherentKeepAlive = true;
            }
            if (this._connectionState === "Connecting" /* Connecting */) {
                // Ensure the connection transitions to the connected state prior to completing this.startInternalPromise.
                // start() will handle the case when stop was called and startInternal exits still in the disconnecting state.
                this._logger.log(LogLevel.Debug, "The HttpConnection connected successfully.");
                this._connectionState = "Connected" /* Connected */;
            }
            // stop() is waiting on us via this.startInternalPromise so keep this.transport around so it can clean up.
            // This is the only case startInternal can exit in neither the connected nor disconnected state because stopConnection()
            // will transition to the disconnected state. start() will wait for the transition using the stopPromise.
        }
        catch (e) {
            this._logger.log(LogLevel.Error, "Failed to start the connection: " + e);
            this._connectionState = "Disconnected" /* Disconnected */;
            this.transport = undefined;
            // if start fails, any active calls to stop assume that start will complete the stop promise
            this._stopPromiseResolver();
            return Promise.reject(e);
        }
    }
    async _getNegotiationResponse(url) {
        const headers = {};
        const [name, value] = getUserAgentHeader();
        headers[name] = value;
        const negotiateUrl = this._resolveNegotiateUrl(url);
        this._logger.log(LogLevel.Debug, `Sending negotiation request: ${negotiateUrl}.`);
        try {
            const response = await this._httpClient.post(negotiateUrl, {
                content: "",
                headers: { ...headers, ...this._options.headers },
                timeout: this._options.timeout,
                withCredentials: this._options.withCredentials,
            });
            if (response.statusCode !== 200) {
                return Promise.reject(new Error(`Unexpected status code returned from negotiate '${response.statusCode}'`));
            }
            const negotiateResponse = JSON.parse(response.content);
            if (!negotiateResponse.negotiateVersion || negotiateResponse.negotiateVersion < 1) {
                // Negotiate version 0 doesn't use connectionToken
                // So we set it equal to connectionId so all our logic can use connectionToken without being aware of the negotiate version
                negotiateResponse.connectionToken = negotiateResponse.connectionId;
            }
            return negotiateResponse;
        }
        catch (e) {
            let errorMessage = "Failed to complete negotiation with the server: " + e;
            if (e instanceof HttpError) {
                if (e.statusCode === 404) {
                    errorMessage = errorMessage + " Either this is not a SignalR endpoint or there is a proxy blocking the connection.";
                }
            }
            this._logger.log(LogLevel.Error, errorMessage);
            return Promise.reject(new FailedToNegotiateWithServerError(errorMessage));
        }
    }
    _createConnectUrl(url, connectionToken) {
        if (!connectionToken) {
            return url;
        }
        return url + (url.indexOf("?") === -1 ? "?" : "&") + `id=${connectionToken}`;
    }
    async _createTransport(url, requestedTransport, negotiateResponse, requestedTransferFormat) {
        let connectUrl = this._createConnectUrl(url, negotiateResponse.connectionToken);
        if (this._isITransport(requestedTransport)) {
            this._logger.log(LogLevel.Debug, "Connection was provided an instance of ITransport, using that directly.");
            this.transport = requestedTransport;
            await this._startTransport(connectUrl, requestedTransferFormat);
            this.connectionId = negotiateResponse.connectionId;
            return;
        }
        const transportExceptions = [];
        const transports = negotiateResponse.availableTransports || [];
        let negotiate = negotiateResponse;
        for (const endpoint of transports) {
            const transportOrError = this._resolveTransportOrError(endpoint, requestedTransport, requestedTransferFormat);
            if (transportOrError instanceof Error) {
                // Store the error and continue, we don't want to cause a re-negotiate in these cases
                transportExceptions.push(`${endpoint.transport} failed:`);
                transportExceptions.push(transportOrError);
            }
            else if (this._isITransport(transportOrError)) {
                this.transport = transportOrError;
                if (!negotiate) {
                    try {
                        negotiate = await this._getNegotiationResponse(url);
                    }
                    catch (ex) {
                        return Promise.reject(ex);
                    }
                    connectUrl = this._createConnectUrl(url, negotiate.connectionToken);
                }
                try {
                    await this._startTransport(connectUrl, requestedTransferFormat);
                    this.connectionId = negotiate.connectionId;
                    return;
                }
                catch (ex) {
                    this._logger.log(LogLevel.Error, `Failed to start the transport '${endpoint.transport}': ${ex}`);
                    negotiate = undefined;
                    transportExceptions.push(new FailedToStartTransportError(`${endpoint.transport} failed: ${ex}`, HttpTransportType[endpoint.transport]));
                    if (this._connectionState !== "Connecting" /* Connecting */) {
                        const message = "Failed to select transport before stop() was called.";
                        this._logger.log(LogLevel.Debug, message);
                        return Promise.reject(new AbortError(message));
                    }
                }
            }
        }
        if (transportExceptions.length > 0) {
            return Promise.reject(new AggregateErrors(`Unable to connect to the server with any of the available transports. ${transportExceptions.join(" ")}`, transportExceptions));
        }
        return Promise.reject(new Error("None of the transports supported by the client are supported by the server."));
    }
    _constructTransport(transport) {
        switch (transport) {
            case HttpTransportType.WebSockets:
                if (!this._options.WebSocket) {
                    throw new Error("'WebSocket' is not supported in your environment.");
                }
                return new WebSocketTransport(this._httpClient, this._accessTokenFactory, this._logger, this._options.logMessageContent, this._options.WebSocket, this._options.headers || {});
            case HttpTransportType.ServerSentEvents:
                if (!this._options.EventSource) {
                    throw new Error("'EventSource' is not supported in your environment.");
                }
                return new ServerSentEventsTransport(this._httpClient, this._httpClient._accessToken, this._logger, this._options);
            case HttpTransportType.LongPolling:
                return new LongPollingTransport(this._httpClient, this._logger, this._options);
            default:
                throw new Error(`Unknown transport: ${transport}.`);
        }
    }
    _startTransport(url, transferFormat) {
        this.transport.onreceive = this.onreceive;
        this.transport.onclose = (e) => this._stopConnection(e);
        return this.transport.connect(url, transferFormat);
    }
    _resolveTransportOrError(endpoint, requestedTransport, requestedTransferFormat) {
        const transport = HttpTransportType[endpoint.transport];
        if (transport === null || transport === undefined) {
            this._logger.log(LogLevel.Debug, `Skipping transport '${endpoint.transport}' because it is not supported by this client.`);
            return new Error(`Skipping transport '${endpoint.transport}' because it is not supported by this client.`);
        }
        else {
            if (transportMatches(requestedTransport, transport)) {
                const transferFormats = endpoint.transferFormats.map((s) => TransferFormat[s]);
                if (transferFormats.indexOf(requestedTransferFormat) >= 0) {
                    if ((transport === HttpTransportType.WebSockets && !this._options.WebSocket) ||
                        (transport === HttpTransportType.ServerSentEvents && !this._options.EventSource)) {
                        this._logger.log(LogLevel.Debug, `Skipping transport '${HttpTransportType[transport]}' because it is not supported in your environment.'`);
                        return new UnsupportedTransportError(`'${HttpTransportType[transport]}' is not supported in your environment.`, transport);
                    }
                    else {
                        this._logger.log(LogLevel.Debug, `Selecting transport '${HttpTransportType[transport]}'.`);
                        try {
                            return this._constructTransport(transport);
                        }
                        catch (ex) {
                            return ex;
                        }
                    }
                }
                else {
                    this._logger.log(LogLevel.Debug, `Skipping transport '${HttpTransportType[transport]}' because it does not support the requested transfer format '${TransferFormat[requestedTransferFormat]}'.`);
                    return new Error(`'${HttpTransportType[transport]}' does not support ${TransferFormat[requestedTransferFormat]}.`);
                }
            }
            else {
                this._logger.log(LogLevel.Debug, `Skipping transport '${HttpTransportType[transport]}' because it was disabled by the client.`);
                return new DisabledTransportError(`'${HttpTransportType[transport]}' is disabled by the client.`, transport);
            }
        }
    }
    _isITransport(transport) {
        return transport && typeof (transport) === "object" && "connect" in transport;
    }
    _stopConnection(error) {
        this._logger.log(LogLevel.Debug, `HttpConnection.stopConnection(${error}) called while in state ${this._connectionState}.`);
        this.transport = undefined;
        // If we have a stopError, it takes precedence over the error from the transport
        error = this._stopError || error;
        this._stopError = undefined;
        if (this._connectionState === "Disconnected" /* Disconnected */) {
            this._logger.log(LogLevel.Debug, `Call to HttpConnection.stopConnection(${error}) was ignored because the connection is already in the disconnected state.`);
            return;
        }
        if (this._connectionState === "Connecting" /* Connecting */) {
            this._logger.log(LogLevel.Warning, `Call to HttpConnection.stopConnection(${error}) was ignored because the connection is still in the connecting state.`);
            throw new Error(`HttpConnection.stopConnection(${error}) was called while the connection is still in the connecting state.`);
        }
        if (this._connectionState === "Disconnecting" /* Disconnecting */) {
            // A call to stop() induced this call to stopConnection and needs to be completed.
            // Any stop() awaiters will be scheduled to continue after the onclose callback fires.
            this._stopPromiseResolver();
        }
        if (error) {
            this._logger.log(LogLevel.Error, `Connection disconnected with error '${error}'.`);
        }
        else {
            this._logger.log(LogLevel.Information, "Connection disconnected.");
        }
        if (this._sendQueue) {
            this._sendQueue.stop().catch((e) => {
                this._logger.log(LogLevel.Error, `TransportSendQueue.stop() threw error '${e}'.`);
            });
            this._sendQueue = undefined;
        }
        this.connectionId = undefined;
        this._connectionState = "Disconnected" /* Disconnected */;
        if (this._connectionStarted) {
            this._connectionStarted = false;
            try {
                if (this.onclose) {
                    this.onclose(error);
                }
            }
            catch (e) {
                this._logger.log(LogLevel.Error, `HttpConnection.onclose(${error}) threw error '${e}'.`);
            }
        }
    }
    _resolveUrl(url) {
        // startsWith is not supported in IE
        if (url.lastIndexOf("https://", 0) === 0 || url.lastIndexOf("http://", 0) === 0) {
            return url;
        }
        if (!Platform.isBrowser) {
            throw new Error(`Cannot resolve '${url}'.`);
        }
        // Setting the url to the href propery of an anchor tag handles normalization
        // for us. There are 3 main cases.
        // 1. Relative path normalization e.g "b" -> "http://localhost:5000/a/b"
        // 2. Absolute path normalization e.g "/a/b" -> "http://localhost:5000/a/b"
        // 3. Networkpath reference normalization e.g "//localhost:5000/a/b" -> "http://localhost:5000/a/b"
        const aTag = window.document.createElement("a");
        aTag.href = url;
        this._logger.log(LogLevel.Information, `Normalizing '${url}' to '${aTag.href}'.`);
        return aTag.href;
    }
    _resolveNegotiateUrl(url) {
        const index = url.indexOf("?");
        let negotiateUrl = url.substring(0, index === -1 ? url.length : index);
        if (negotiateUrl[negotiateUrl.length - 1] !== "/") {
            negotiateUrl += "/";
        }
        negotiateUrl += "negotiate";
        negotiateUrl += index === -1 ? "" : url.substring(index);
        if (negotiateUrl.indexOf("negotiateVersion") === -1) {
            negotiateUrl += index === -1 ? "?" : "&";
            negotiateUrl += "negotiateVersion=" + this._negotiateVersion;
        }
        return negotiateUrl;
    }
}
function transportMatches(requestedTransport, actualTransport) {
    return !requestedTransport || ((actualTransport & requestedTransport) !== 0);
}
/** @private */
class TransportSendQueue {
    constructor(_transport) {
        this._transport = _transport;
        this._buffer = [];
        this._executing = true;
        this._sendBufferedData = new PromiseSource();
        this._transportResult = new PromiseSource();
        this._sendLoopPromise = this._sendLoop();
    }
    send(data) {
        this._bufferData(data);
        if (!this._transportResult) {
            this._transportResult = new PromiseSource();
        }
        return this._transportResult.promise;
    }
    stop() {
        this._executing = false;
        this._sendBufferedData.resolve();
        return this._sendLoopPromise;
    }
    _bufferData(data) {
        if (this._buffer.length && typeof (this._buffer[0]) !== typeof (data)) {
            throw new Error(`Expected data to be of type ${typeof (this._buffer)} but was of type ${typeof (data)}`);
        }
        this._buffer.push(data);
        this._sendBufferedData.resolve();
    }
    async _sendLoop() {
        while (true) {
            await this._sendBufferedData.promise;
            if (!this._executing) {
                if (this._transportResult) {
                    this._transportResult.reject("Connection stopped.");
                }
                break;
            }
            this._sendBufferedData = new PromiseSource();
            const transportResult = this._transportResult;
            this._transportResult = undefined;
            const data = typeof (this._buffer[0]) === "string" ?
                this._buffer.join("") :
                TransportSendQueue._concatBuffers(this._buffer);
            this._buffer.length = 0;
            try {
                await this._transport.send(data);
                transportResult.resolve();
            }
            catch (error) {
                transportResult.reject(error);
            }
        }
    }
    static _concatBuffers(arrayBuffers) {
        const totalLength = arrayBuffers.map((b) => b.byteLength).reduce((a, b) => a + b);
        const result = new Uint8Array(totalLength);
        let offset = 0;
        for (const item of arrayBuffers) {
            result.set(new Uint8Array(item), offset);
            offset += item.byteLength;
        }
        return result.buffer;
    }
}
class PromiseSource {
    constructor() {
        this.promise = new Promise((resolve, reject) => [this._resolver, this._rejecter] = [resolve, reject]);
    }
    resolve() {
        this._resolver();
    }
    reject(reason) {
        this._rejecter(reason);
    }
}

// Licensed to the .NET Foundation under one or more agreements.
const JSON_HUB_PROTOCOL_NAME = "json";
/** Implements the JSON Hub Protocol. */
class JsonHubProtocol {
    constructor() {
        /** @inheritDoc */
        this.name = JSON_HUB_PROTOCOL_NAME;
        /** @inheritDoc */
        this.version = 1;
        /** @inheritDoc */
        this.transferFormat = TransferFormat.Text;
    }
    /** Creates an array of {@link @microsoft/signalr.HubMessage} objects from the specified serialized representation.
     *
     * @param {string} input A string containing the serialized representation.
     * @param {ILogger} logger A logger that will be used to log messages that occur during parsing.
     */
    parseMessages(input, logger) {
        // The interface does allow "ArrayBuffer" to be passed in, but this implementation does not. So let's throw a useful error.
        if (typeof input !== "string") {
            throw new Error("Invalid input for JSON hub protocol. Expected a string.");
        }
        if (!input) {
            return [];
        }
        if (logger === null) {
            logger = NullLogger.instance;
        }
        // Parse the messages
        const messages = TextMessageFormat.parse(input);
        const hubMessages = [];
        for (const message of messages) {
            const parsedMessage = JSON.parse(message);
            if (typeof parsedMessage.type !== "number") {
                throw new Error("Invalid payload.");
            }
            switch (parsedMessage.type) {
                case MessageType.Invocation:
                    this._isInvocationMessage(parsedMessage);
                    break;
                case MessageType.StreamItem:
                    this._isStreamItemMessage(parsedMessage);
                    break;
                case MessageType.Completion:
                    this._isCompletionMessage(parsedMessage);
                    break;
                case MessageType.Ping:
                    // Single value, no need to validate
                    break;
                case MessageType.Close:
                    // All optional values, no need to validate
                    break;
                default:
                    // Future protocol changes can add message types, old clients can ignore them
                    logger.log(LogLevel.Information, "Unknown message type '" + parsedMessage.type + "' ignored.");
                    continue;
            }
            hubMessages.push(parsedMessage);
        }
        return hubMessages;
    }
    /** Writes the specified {@link @microsoft/signalr.HubMessage} to a string and returns it.
     *
     * @param {HubMessage} message The message to write.
     * @returns {string} A string containing the serialized representation of the message.
     */
    writeMessage(message) {
        return TextMessageFormat.write(JSON.stringify(message));
    }
    _isInvocationMessage(message) {
        this._assertNotEmptyString(message.target, "Invalid payload for Invocation message.");
        if (message.invocationId !== undefined) {
            this._assertNotEmptyString(message.invocationId, "Invalid payload for Invocation message.");
        }
    }
    _isStreamItemMessage(message) {
        this._assertNotEmptyString(message.invocationId, "Invalid payload for StreamItem message.");
        if (message.item === undefined) {
            throw new Error("Invalid payload for StreamItem message.");
        }
    }
    _isCompletionMessage(message) {
        if (message.result && message.error) {
            throw new Error("Invalid payload for Completion message.");
        }
        if (!message.result && message.error) {
            this._assertNotEmptyString(message.error, "Invalid payload for Completion message.");
        }
        this._assertNotEmptyString(message.invocationId, "Invalid payload for Completion message.");
    }
    _assertNotEmptyString(value, errorMessage) {
        if (typeof value !== "string" || value === "") {
            throw new Error(errorMessage);
        }
    }
}

// Licensed to the .NET Foundation under one or more agreements.
const LogLevelNameMapping = {
    trace: LogLevel.Trace,
    debug: LogLevel.Debug,
    info: LogLevel.Information,
    information: LogLevel.Information,
    warn: LogLevel.Warning,
    warning: LogLevel.Warning,
    error: LogLevel.Error,
    critical: LogLevel.Critical,
    none: LogLevel.None,
};
function parseLogLevel(name) {
    // Case-insensitive matching via lower-casing
    // Yes, I know case-folding is a complicated problem in Unicode, but we only support
    // the ASCII strings defined in LogLevelNameMapping anyway, so it's fine -anurse.
    const mapping = LogLevelNameMapping[name.toLowerCase()];
    if (typeof mapping !== "undefined") {
        return mapping;
    }
    else {
        throw new Error(`Unknown log level: ${name}`);
    }
}
/** A builder for configuring {@link @microsoft/signalr.HubConnection} instances. */
class HubConnectionBuilder {
    configureLogging(logging) {
        Arg.isRequired(logging, "logging");
        if (isLogger(logging)) {
            this.logger = logging;
        }
        else if (typeof logging === "string") {
            const logLevel = parseLogLevel(logging);
            this.logger = new ConsoleLogger(logLevel);
        }
        else {
            this.logger = new ConsoleLogger(logging);
        }
        return this;
    }
    withUrl(url, transportTypeOrOptions) {
        Arg.isRequired(url, "url");
        Arg.isNotEmpty(url, "url");
        this.url = url;
        // Flow-typing knows where it's at. Since HttpTransportType is a number and IHttpConnectionOptions is guaranteed
        // to be an object, we know (as does TypeScript) this comparison is all we need to figure out which overload was called.
        if (typeof transportTypeOrOptions === "object") {
            this.httpConnectionOptions = { ...this.httpConnectionOptions, ...transportTypeOrOptions };
        }
        else {
            this.httpConnectionOptions = {
                ...this.httpConnectionOptions,
                transport: transportTypeOrOptions,
            };
        }
        return this;
    }
    /** Configures the {@link @microsoft/signalr.HubConnection} to use the specified Hub Protocol.
     *
     * @param {IHubProtocol} protocol The {@link @microsoft/signalr.IHubProtocol} implementation to use.
     */
    withHubProtocol(protocol) {
        Arg.isRequired(protocol, "protocol");
        this.protocol = protocol;
        return this;
    }
    withAutomaticReconnect(retryDelaysOrReconnectPolicy) {
        if (this.reconnectPolicy) {
            throw new Error("A reconnectPolicy has already been set.");
        }
        if (!retryDelaysOrReconnectPolicy) {
            this.reconnectPolicy = new DefaultReconnectPolicy();
        }
        else if (Array.isArray(retryDelaysOrReconnectPolicy)) {
            this.reconnectPolicy = new DefaultReconnectPolicy(retryDelaysOrReconnectPolicy);
        }
        else {
            this.reconnectPolicy = retryDelaysOrReconnectPolicy;
        }
        return this;
    }
    /** Creates a {@link @microsoft/signalr.HubConnection} from the configuration options specified in this builder.
     *
     * @returns {HubConnection} The configured {@link @microsoft/signalr.HubConnection}.
     */
    build() {
        // If httpConnectionOptions has a logger, use it. Otherwise, override it with the one
        // provided to configureLogger
        const httpConnectionOptions = this.httpConnectionOptions || {};
        // If it's 'null', the user **explicitly** asked for null, don't mess with it.
        if (httpConnectionOptions.logger === undefined) {
            // If our logger is undefined or null, that's OK, the HttpConnection constructor will handle it.
            httpConnectionOptions.logger = this.logger;
        }
        // Now create the connection
        if (!this.url) {
            throw new Error("The 'HubConnectionBuilder.withUrl' method must be called before building the connection.");
        }
        const connection = new HttpConnection(this.url, httpConnectionOptions);
        return HubConnection.create(connection, this.logger || NullLogger.instance, this.protocol || new JsonHubProtocol(), this.reconnectPolicy);
    }
}
function isLogger(logger) {
    return logger.log !== undefined;
}

class SignalRHelper {
    constructor() {
        this.isConnected = false;
        this.connection = new HubConnectionBuilder()
            .withUrl(`${getPushNotificationsBaseUrl()}${SignalRHelper.notificationHubUrl}`, { accessTokenFactory: () => getAccessToken() })
            .configureLogging(LogLevel.Information)
            .withAutomaticReconnect()
            .build();
    }
    updateConnection() {
        if (this.isConnected) {
            return;
        }
        this.start();
        this.connection.onreconnecting(error => {
            this.isConnected = false;
            Logger.error(['portal-notification', 'signalR'], `Connection lost due to error "${error}". Reconnecting.`);
        });
        this.connection.onreconnected(connectionId => {
            this.isConnected = true;
            Logger.debug(['portal-notification', 'signalR'], `Connection reestablished. Connected with connectionId "${connectionId}".`);
        });
        this.connection.onclose(error => {
            this.isConnected = false;
            const errorMessage = `Connection closed due to error "${JSON.stringify(error, getCircularReplacer)}". Try refreshing this page to restart the connection.`;
            Logger.critical(['portal-notification', 'signalR'], errorMessage, { state: store.state });
        });
    }
    start() {
        this.connection.start().then(() => {
            Logger.debug(['portal-notification', 'signalR'], 'Connection established');
            this.isConnected = true;
        })
            .catch(err => {
            const errorMessage = `Failed to establish the connection due to error "${JSON.stringify(err, getCircularReplacer)}}". Try refreshing this page to restart the connection.`;
            Logger.critical(['portal-notification', 'signalR'], errorMessage, { state: store.state });
        });
    }
}
SignalRHelper.notificationHubUrl = '/hub/notification';
const getSignalRServiceInstance = () => new SignalRHelper();

class Theme {
    constructor(themeStore) {
        this.themeStore = themeStore;
        handleThemeChange(theme => this.selectedTheme = theme);
    }
    get _isHighContrastEnabled() {
        return getFeatureFlag(Features.EnableHighContrastLightTheme.name) && getFeatureFlag(Features.EnableHighContrastDarkTheme.name);
    }
    /**
     * Get the theme as ApolloThemeType (no autoTheme)
     */
    get theme() {
        let theme = this.selectedTheme;
        if (theme === 'autoTheme') {
            theme = getAutoThemeValue(this._isHighContrastEnabled);
        }
        return theme;
    }
    /**
     * Get the theme with a passed in override
     */
    getTheme(override) {
        const apolloTheme = this.theme;
        const validThemeOverride = override && validateThemeCode(override);
        let shouldUseOverride = validThemeOverride && validThemeOverride !== 'autoTheme';
        const isRunningInStorybook = getIsRunningInStorybook();
        const lightHCThemeDisabled = !getFeatureFlag(Features.EnableHighContrastLightTheme.name) && !isRunningInStorybook;
        const darkHCThemeDisabled = !getFeatureFlag(Features.EnableHighContrastDarkTheme.name) && !isRunningInStorybook;
        if (validThemeOverride === 'light-hc' && lightHCThemeDisabled) {
            shouldUseOverride = false;
        }
        if (validThemeOverride === 'dark-hc' && darkHCThemeDisabled) {
            shouldUseOverride = false;
        }
        const theme = shouldUseOverride ? validThemeOverride : apolloTheme;
        return theme;
    }
    /**
     * Get the theme as ThemeType (with autoTheme)
     */
    get selectedTheme() {
        const theme = this.overrideTheme || this.themeStore.get('theme') || getCachedTheme() || defaultTheme;
        const isRunningInStorybook = getIsRunningInStorybook();
        const lightHCThemeDisabled = !getFeatureFlag(Features.EnableHighContrastLightTheme.name) && !isRunningInStorybook;
        const darkHCThemeDisabled = !getFeatureFlag(Features.EnableHighContrastDarkTheme.name) && !isRunningInStorybook;
        if (theme === 'light-hc' && lightHCThemeDisabled) {
            return 'light';
        }
        if (theme === 'dark-hc' && darkHCThemeDisabled) {
            return 'dark';
        }
        return theme;
    }
    /** INTERNAL ONLY: DO NOT USE */
    set theme(theme) {
        var _a, _b, _c, _d, _e, _f, _g;
        Logger.debug('theme', `set ${theme}`);
        let newApolloTheme = validateThemeCode(this.themeStore.get('overrideTheme') || theme);
        const lightHCThemeDisabled = !getFeatureFlag(Features.EnableHighContrastLightTheme.name);
        const darkHCThemeDisabled = !getFeatureFlag(Features.EnableHighContrastDarkTheme.name);
        const isLightHcTheme = newApolloTheme === 'light-hc';
        const isDarkHcTheme = newApolloTheme === 'dark-hc';
        if (lightHCThemeDisabled && theme === 'light-hc') {
            newApolloTheme = 'light';
        }
        if (darkHCThemeDisabled && theme === 'dark-hc') {
            newApolloTheme = 'dark';
        }
        const isAccessibleMode = isLightHcTheme || isDarkHcTheme;
        if (!lightHCThemeDisabled || !darkHCThemeDisabled) {
            if (((_a = AccessibilityInstanceResolver.Instance) === null || _a === void 0 ? void 0 : _a.isAccessibleMode) !== isAccessibleMode) {
                AccessibilityInstanceResolver.Instance.isAccessibleMode = isAccessibleMode;
            }
        }
        const themeEvent = { selectedThemeId: newApolloTheme };
        GlobalEventEmitter.emitThemeChanged(themeEvent);
        (_c = (_b = EventHandlersInstanceResolver.Instance) === null || _b === void 0 ? void 0 : _b.onThemeChanged) === null || _c === void 0 ? void 0 : _c.call(_b, themeEvent);
        // update body class list based on theme.
        // note this works only for angular and not for react
        Logger.debug('store', `set body theme: ${newApolloTheme}`);
        (_e = (_d = document.body) === null || _d === void 0 ? void 0 : _d.classList) === null || _e === void 0 ? void 0 : _e.remove('light', 'light-hc', 'dark', 'dark-hc');
        (_g = (_f = document.body) === null || _f === void 0 ? void 0 : _f.classList) === null || _g === void 0 ? void 0 : _g.add(newApolloTheme);
        // Force update is required to trigger rerender of components that depend on theme
        this.themeStore.forceUpdate('theme');
    }
    set selectedTheme(theme) {
        var _a, _b, _c, _d;
        Logger.debug('theme', `set selected ${theme}`);
        let newTheme = validateThemeCode(this.themeStore.get('overrideTheme') || theme) || defaultTheme;
        const currentTheme = this.themeStore.get('theme');
        if (currentTheme === newTheme) {
            Logger.debug('theme', 'unchanged');
            return;
        }
        const isRunningInStorybook = getIsRunningInStorybook();
        const lightHCThemeDisabled = !getFeatureFlag(Features.EnableHighContrastLightTheme.name) && !isRunningInStorybook;
        const darkHCThemeDisabled = !getFeatureFlag(Features.EnableHighContrastDarkTheme.name) && !isRunningInStorybook;
        if (theme === 'light-hc' && lightHCThemeDisabled) {
            newTheme = 'light';
        }
        if (theme === 'dark-hc' && darkHCThemeDisabled) {
            newTheme = 'dark';
        }
        // Set the store theme
        this.themeStore.set('theme', newTheme);
        // Emit the selected theme
        const selectedThemeEvent = { selectedThemeId: theme };
        GlobalEventEmitter.emitThemeSelectChanged(selectedThemeEvent);
        (_b = (_a = EventHandlersInstanceResolver.Instance) === null || _a === void 0 ? void 0 : _a.onThemeSelectChanged) === null || _b === void 0 ? void 0 : _b.call(_a, selectedThemeEvent);
        // Emit the theme without autotheme (i.e. ApolloThemeType)
        const apolloTheme = newTheme === 'autoTheme' ? getAutoThemeValue(this._isHighContrastEnabled) : newTheme;
        this.theme = apolloTheme;
        // Cache the theme
        setTheme(newTheme);
        // Emit themeLoadedEvent
        if (!currentTheme) {
            const themeLoadedEvent = { selectedThemeId: apolloTheme };
            GlobalEventEmitter.emitThemeLoaded(themeLoadedEvent);
            (_d = (_c = EventHandlersInstanceResolver.Instance) === null || _c === void 0 ? void 0 : _c.onThemeLoaded) === null || _d === void 0 ? void 0 : _d.call(_c, themeLoadedEvent);
        }
        // Add listener for autoTheme
        const themeMediaChangeCallback = (systemThemeIsDark) => Theme.handleThemeMediaChange(this, systemThemeIsDark, this._isHighContrastEnabled);
        const highContrastChangeCallback = (isHighContrast) => Theme.handleHighContrastMediaChange(this, isHighContrast, this._isHighContrastEnabled);
        if (theme === 'autoTheme') {
            MediaQueryListener.subscribe(themeMediaChangeCallback, darkMediaQuery);
            if (this._isHighContrastEnabled) {
                highContrastMediaQueries.forEach(highContrastMediaQuery => MediaQueryListener.subscribe(highContrastChangeCallback, highContrastMediaQuery));
            }
        }
        else {
            MediaQueryListener.unsubscribe(themeMediaChangeCallback, darkMediaQuery);
            if (this._isHighContrastEnabled) {
                highContrastMediaQueries.forEach(highContrastMediaQuery => MediaQueryListener.unsubscribe(highContrastChangeCallback, highContrastMediaQuery));
            }
        }
    }
    get overrideTheme() {
        return this.themeStore.get('overrideTheme');
    }
    set overrideTheme(theme) {
        if (validateThemeCode(theme) && theme !== 'autoTheme') {
            this.themeStore.set('overrideTheme', theme);
            this.selectedTheme = theme;
        }
    }
    async fetchThemeSettings() {
        var _a;
        Logger.debug('theme', 'fetch');
        const accessToken = getAccessToken();
        if (!accessToken) {
            return;
        }
        const cachedTheme = getCachedTheme();
        const validOverrideTheme = validateThemeCode(this.overrideTheme);
        Logger.debug('theme', `cachedTheme: ${JSON.stringify(cachedTheme)}, validOverrideTheme: ${validOverrideTheme}`, { state: this.themeStore.state });
        const organizationStandardUrl = getOrganizationStandardUrl();
        const hasSapEntitlements = (_a = AccountAndTenantsInstanceResolver.Instance) === null || _a === void 0 ? void 0 : _a.hasAccountEntitlement(Entitlement.SAP);
        if (!validOverrideTheme && organizationStandardUrl) {
            const { refetchCondition, setLocalStorageLastRefetched, } = shouldRefetch(Features.ThemeRefetchThrottleMs.name, `${STORAGE_KEYS.PORTAL_SHELL_THEME_LAST_REFETCHED}_${getAccountName()}`);
            let userTheme = null;
            if (!cachedTheme || refetchCondition || hasSapEntitlements) {
                try {
                    const settings = await SettingsInstanceResolver.Instance.getSettings([ApolloSettingKey.UserTheme]);
                    userTheme = settings[ApolloSettingKey.UserTheme];
                    setLocalStorageLastRefetched();
                }
                catch (e) {
                    Logger.error('theme', `unexpected error fetching: ${e}`, { stack: e.stack });
                }
            }
            // We only set the theme in /Settings if the GET call above returns null
            if (!userTheme) {
                Logger.logCustomEvent(ThemeEvent.SetCachedThemeAsSelectedThemeOnInit, {
                    cachedTheme,
                    isSettingUserTheme: !isStandalone(),
                });
                this.themeStore.set('theme', cachedTheme);
                this.selectedTheme = cachedTheme;
            }
            else {
                Logger.logCustomEvent(ThemeEvent.SetUserThemeAsCachedThemeOnInit, { userTheme });
                this.selectedTheme = userTheme;
            }
        }
    }
    async putThemeSettings(theme) {
        const accessToken = getAccessToken();
        const organizationStandardUrl = getOrganizationStandardUrl();
        if (!organizationStandardUrl || !accessToken) {
            return;
        }
        try {
            await setUserTheme(theme, organizationStandardUrl, accessToken, sessionStore.get('organizationId'), sessionStore.get('userId'));
        }
        catch (e) {
            Logger.error('theme', `unexpected error on put: ${e}`, { stack: e.stack });
        }
    }
    static handleThemeMediaChange(delegate, changedToDark, isHighContrastEnabled) {
        const selectedTheme = delegate.selectedTheme || getCachedTheme() || defaultTheme;
        if (selectedTheme !== 'autoTheme') {
            return;
        }
        const isThemeHighContrast = ['light-hc', 'dark-hc'].includes(getAutoThemeValue(isHighContrastEnabled));
        const themeToSet = changedToDark ? 'dark' : 'light';
        delegate.theme = isThemeHighContrast && isHighContrastEnabled ? `${themeToSet}-hc` : themeToSet;
    }
    static handleHighContrastMediaChange(delegate, isHighContrast, isHighContrastEnabled) {
        const selectedTheme = delegate.selectedTheme || getCachedTheme() || defaultTheme;
        if (selectedTheme !== 'autoTheme') {
            return;
        }
        const autoTheme = getAutoThemeValue(isHighContrastEnabled);
        const baseTheme = autoTheme.split('-')[0];
        if (isHighContrast) {
            delegate.theme = `${baseTheme}-hc`;
        }
        else {
            delegate.theme = baseTheme;
        }
    }
}
const themeStore = createStore({
    theme: null,
    overrideTheme: null,
});

var _a;
// Inject the generated global styles
const styleElement = document.createElement('style');
styleElement.appendChild(document.createTextNode(globalStyles));
document.head.appendChild(styleElement);
// Instantiate singleton services
Logger.setAppInsights(AppInsightsInstanceResolver.Instantiate(new AppInsightsClient()));
const pendo = new Pendo();
const featureFlags = new FeatureFlags(getFeatureFlagsStore());
const session = new Session(sessionStore);
const signalRService = getSignalRServiceInstance();
const accountAndTenants = AccountAndTenantsInstanceResolver.Instantiate(new AccountAndTenants(accountAndTenantsStore));
const chat = ChatInstanceResolver.Instantiate(chatStore);
const accessibility = AccessibilityInstanceResolver.Instantiate(new Accessibility(accessibilityStore));
const alert = AlertInstanceResolver.Instantiate(new Alert(alertStore));
const locale = LocaleInstanceResolver.Instantiate(new Locale(localeStore));
const theme = ThemeInstanceResolver.Instantiate(new Theme(themeStore));
const navigation = NavigationInstanceResolver.Instantiate(new Navigation(navigationStore));
const settings = SettingsInstanceResolver.Instantiate(new Settings());
const autopilotChat = AutopilotChatService.Instantiate({ mode: (_a = StorageService.Instance.get(CHAT_MODE_KEY)) !== null && _a !== void 0 ? _a : AutopilotChatMode.Closed });
// Set up initialize function
const initialize = (options = { devProperties: { mode: 'auto' } }) => {
    var _a, _b;
    Logger.debug('globalScript', 'initialize', { object: options });
    // Set up event handlers
    EventHandlersInstanceResolver.Instantiate({
        onTenantsNotAvailable: options.onTenantsNotAvailable,
        onTenantChanged: options.onTenantChanged,
        onTenantLoaded: options.onTenantLoaded,
        onThemeChanged: options.onThemeChanged,
        onThemeSelectChanged: options.onThemeSelectChanged,
        onThemeLoaded: options.onThemeLoaded,
        onLanguageChanged: options.onLanguageChanged,
        onLanguageLoaded: options.onLanguageLoaded,
        onPendoInitialized: options.onPendoInitialized,
        onLogout: options.onLogout,
    });
    // Set up global properties
    GlobalPropsInstanceResolver.Instantiate({
        devProperties: Object.assign(Object.assign({}, options.devProperties), { mode: (_b = (_a = options.devProperties) === null || _a === void 0 ? void 0 : _a.mode) !== null && _b !== void 0 ? _b : 'auto' }),
        featureFlagOverrides: Object.assign({}, options.featureFlagOverrides),
    });
};
window.PortalShell = {
    initialize,
    Pendo: pendo,
    FeatureFlags: featureFlags,
    Session: session,
    SignalRService: signalRService,
    AccountAndTenants: accountAndTenants,
    Chat: chat,
    AutopilotChat: autopilotChat,
    EventHandlers: {},
    GlobalProperties: {
        devProperties: { mode: 'auto' },
        featureFlagOverrides: {},
    },
    ServiceSvgs: ICON_MAP,
    Logger,
    Accessibility: accessibility,
    Alert: alert,
    Locale: locale,
    Theme: theme,
    Navigation: navigation,
    Settings: settings,
};
GlobalEventEmitter.emitPortalShellLoaded();

const globalFn = () => { };

const globalScripts = globalFn;

export { globalScripts as g };

//# sourceMappingURL=app-globals-08f578d3.js.map